import React, { useState, useEffect } from 'react';
import api from './api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const BULAN_NAMA = ['','Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];
const CABANG_NAMA = { PO:'Puri Orchard', PM:'Palm Mansion', MT:'Metro', DM:'Daan Mogot City', TM:'Tomang' };

const formatRp = (val) => {
  if (!val) return 'Rp 0';
  return 'Rp ' + Number(val).toLocaleString('id-ID');
};

const formatTglPendek = (tglStr) => {
  if (!tglStr) return "—";
  const [y, m, d] = tglStr.split("-").map(Number);
  const bln = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agu","Sep","Okt","Nov","Des"];
  return `${d}-${bln[m-1]}-${String(y).slice(2)}`;
};

// =====================
// LOGIN PAGE
// =====================
function LoginPage({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('username', username);
      form.append('password', password);
      const res = await api.post('/auth/login', form);
      if (res.data.is_kasir) {
        setError('Akun ini hanya untuk aplikasi mobile, tidak bisa login ke dashboard');
        return;
      }
      if (res.data.is_gudang) {
        setError('Akun gudang hanya bisa digunakan di aplikasi gudang');
        return;
      }
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('fullname', res.data.full_name);
      onLogin(res.data.full_name);
    } catch {
      setError('Username atau password salah');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"linear-gradient(135deg, #0f2044 0%, #152d5a 60%, #1e40af 100%)"}}>
      <div style={{width:380, padding:"0 16px"}}>
        {/* Brand header */}
        <div style={{textAlign:"center", marginBottom:32}}>
          <div style={{display:"inline-flex", alignItems:"center", gap:12, marginBottom:16}}>
            <div style={{width:48, height:48, borderRadius:12, background:"linear-gradient(135deg, #3b82f6, #1d4ed8)", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 4px 16px rgba(59,130,246,0.4)"}}>
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <ellipse cx="14" cy="20" rx="10" ry="5" fill="rgba(255,255,255,0.15)"/>
                <path d="M4 10 Q14 4 24 10 Q14 16 4 10Z" fill="white" opacity="0.9"/>
                <path d="M4 10 Q14 16 24 10" stroke="white" strokeWidth="1.5" fill="none" opacity="0.5"/>
              </svg>
            </div>
            <div style={{textAlign:"left"}}>
              <div style={{fontSize:22, fontWeight:800, color:"#fff", letterSpacing:-0.5, lineHeight:1.1}}>Mommy Wash</div>
              <div style={{fontSize:14, fontWeight:600, color:"rgba(255,255,255,0.5)", letterSpacing:2, textTransform:"uppercase"}}>LAUNDRY</div>
            </div>
          </div>
          <p style={{color:"rgba(255,255,255,0.5)", fontSize:14, margin:0}}>Sistem Laporan Internal</p>
        </div>

        {/* Login card */}
        <div style={{background:"#fff", borderRadius:20, padding:"36px 32px", boxShadow:"0 24px 64px rgba(0,0,0,0.35)"}}>
          <h2 style={{fontSize:22, fontWeight:700, color:"#1e293b", margin:"0 0 6px"}}>Masuk ke Akun</h2>
          <p style={{fontSize:14, color:"#94a3b8", margin:"0 0 28px"}}>Masukkan username dan password kamu</p>
          <form onSubmit={handleLogin}>
            <div style={{marginBottom:16}}>
              <label style={{fontSize:14, fontWeight:600, color:"#475569", display:"block", marginBottom:6, textTransform:"uppercase", letterSpacing:0.5}}>Username</label>
              <input style={{...styles.input, marginBottom:0}} placeholder="Username" value={username}
                onChange={e => setUsername(e.target.value)} required />
            </div>
            <div style={{marginBottom:24}}>
              <label style={{fontSize:14, fontWeight:600, color:"#475569", display:"block", marginBottom:6, textTransform:"uppercase", letterSpacing:0.5}}>Password</label>
              <input style={{...styles.input, marginBottom:0}} type="password" placeholder="••••••••" value={password}
                onChange={e => setPassword(e.target.value)} required />
            </div>
            {error && <div style={{...styles.error, marginBottom:16}}>{error}</div>}
            <button style={{...styles.btnPrimary, width:"100%", padding:"12px", fontSize:16, borderRadius:10, background:"linear-gradient(135deg, #2563eb, #1d4ed8)", boxShadow:"0 4px 12px rgba(37,99,235,0.3)"}} type="submit" disabled={loading}>
              {loading ? '⏳ Memuat...' : 'Masuk →'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// =====================
// UPLOAD PAGE
// =====================
function UploadPage() {
  const [tahun, setTahun] = useState(new Date().getFullYear());
  const [transaksiFiles, setTransaksiFiles] = useState([]);
  const [smartlinkFiles, setSmartlinkFiles] = useState([]);
  const [qrisFile, setQrisFile] = useState(null);
  const [mutasiFiles, setMutasiFiles] = useState([]);
  const [absensiFiles, setAbsensiFiles] = useState([]);
  const [statusMap, setStatusMap] = useState({});
  const [loadingMap, setLoadingMap] = useState({});
  const [fileKeys, setFileKeys] = useState({t:0, s:0, q:0, m:0, a:0});
  const resetKey = (k) => setFileKeys(prev => ({...prev, [k]: prev[k]+1}));

  const setStatus = (key, msg) => setStatusMap(prev => ({ ...prev, [key]: msg }));
  const setLoading = (key, val) => setLoadingMap(prev => ({ ...prev, [key]: val }));

  const uploadTransaksi = async () => {
    if (transaksiFiles.length === 0) return alert("Pilih file terlebih dahulu!");
    setLoading("transaksi", true);
    setStatus("transaksi", "Memproses...");
    try {
      const form = new FormData();
      transaksiFiles.forEach(f => form.append('files', f));
      form.append('tahun', tahun);
      const res = await api.post('/upload/transaksi-data', form);
      setStatus("transaksi", "✅ " + res.data.message);
      setTransaksiFiles([]);
      resetKey('t');
    } catch (err) {
      setStatus("transaksi", "❌ Error: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading("transaksi", false);
    }
  };

  const uploadSmartlink = async () => {
    if (smartlinkFiles.length === 0) return alert("Pilih file terlebih dahulu!");
    setLoading("smartlink", true);
    setStatus("smartlink", "Memproses...");
    try {
      const form = new FormData();
      smartlinkFiles.forEach(f => form.append('files', f));
      form.append('tahun', tahun);
      const res = await api.post('/upload/smartlink', form);
      setStatus("smartlink", "✅ " + res.data.message);
      setSmartlinkFiles([]);
      resetKey('s');
    } catch (err) {
      setStatus("smartlink", "❌ Error: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading("smartlink", false);
    }
  };

  const uploadQris = async () => {
    if (!qrisFile) return alert("Pilih file terlebih dahulu!");
    setLoading("qris", true);
    setStatus("qris", "Memproses...");
    try {
      const form = new FormData();
      form.append('file', qrisFile);
      form.append('tahun', tahun);
      const res = await api.post('/upload/qris', form);
      setStatus("qris", "✅ " + res.data.message);
      setQrisFile(null);
      resetKey('q');
    } catch (err) {
      setStatus("qris", "❌ Error: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading("qris", false);
    }
  };

  const uploadAbsensi = async () => {
    if (!absensiFiles.length) return alert("Pilih file terlebih dahulu!");
    setLoading("absensi", true);
    setStatus("absensi", "Memproses...");
    try {
      const form = new FormData();
      absensiFiles.forEach(f => form.append('files', f));
      form.append('tahun', tahun);
      const res = await api.post('/upload/absensi', form);
      const results = res.data.results || [];
      const ok = results.filter(r => r.status === 'success');
      const err = results.filter(r => r.status === 'error');
      if (ok.length > 0) {
        setStatus("absensi", "✅ " + ok.map(r => `${r.filename}: Cabang ${r.cabang}, ${r.inserted} data masuk`).join(" | "));
      }
      if (err.length > 0) {
        setStatus("absensi", "❌ " + err.map(r => `${r.filename}: ${r.message}`).join(" | "));
      }
    } catch(err) {
      setStatus("absensi", "❌ Error: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading("absensi", false);
      setAbsensiFiles([]);
      resetKey('a');
    }
  };

  const uploadMutasi = async () => {
    if (mutasiFiles.length === 0) return alert("Pilih file terlebih dahulu!");
    setLoading("mutasi", true);
    setStatus("mutasi", "Memproses...");
    try {
      const form = new FormData();
      mutasiFiles.forEach(f => form.append('files', f));
      form.append('tahun', tahun);
      const res = await api.post('/upload/mutasi-v2', form);
      setStatus("mutasi", "✅ " + res.data.message);
      setMutasiFiles([]);
      resetKey('m');
    } catch (err) {
      setStatus("mutasi", "❌ Error: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading("mutasi", false);
    }
  };

  const UploadCard = ({ color, icon, title, hint, badge, fileKey, multiple, accept, files, onFiles, onUpload, loading, status }) => {
    const isErr = status && status.includes("❌");
    const isOk  = status && status.includes("✅");
    return (
      <div style={{ background: '#fff', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', display: 'flex', flexDirection: 'column' }}>
        {/* Card header strip */}
        <div style={{ background: color.bg, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>{icon}</div>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>{title}</div>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 10, marginTop: 1 }}>{badge}</div>
          </div>
        </div>
        {/* Body */}
        <div style={{ padding: '14px 16px', flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>{hint}</p>
          {/* Drop zone */}
          <label style={{ display: 'block', border: `2px dashed ${color.border}`, borderRadius: 10, padding: '14px 12px', textAlign: 'center', cursor: 'pointer', background: color.light, transition: 'background 0.15s' }}>
            <div style={{ fontSize: 22, marginBottom: 4 }}>📂</div>
            <div style={{ fontSize: 12, color: color.text, fontWeight: 600 }}>Klik untuk pilih file</div>
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{badge}</div>
            <input key={fileKey} type="file" multiple={multiple} accept={accept}
              onChange={e => onFiles(multiple ? Array.from(e.target.files) : e.target.files[0])}
              style={{ display: 'none' }}/>
          </label>
          {/* File list */}
          {files && (Array.isArray(files) ? files.length > 0 : files) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(Array.isArray(files) ? files : [files]).map((f, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, background: color.light, border: `1px solid ${color.border}`, borderRadius: 20, padding: '3px 10px', fontSize: 11, color: color.text, maxWidth: '100%' }}>
                  <span>📄</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>{f.name}</span>
                </div>
              ))}
            </div>
          )}
          {/* Status */}
          {status && (
            <div style={{ fontSize: 12, padding: '8px 12px', borderRadius: 8, background: isErr ? '#fef2f2' : '#f0fdf4', color: isErr ? '#dc2626' : '#16a34a', border: `1px solid ${isErr ? '#fecaca' : '#bbf7d0'}` }}>
              {status}
            </div>
          )}
          {/* Button */}
          <button onClick={onUpload} disabled={loading}
            style={{ marginTop: 'auto', padding: '9px 0', borderRadius: 9, border: 'none', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 13, color: '#fff', background: loading ? '#94a3b8' : color.bg, transition: 'opacity 0.15s' }}>
            {loading ? '⏳ Memproses...' : '⬆️ Upload'}
          </button>
        </div>
      </div>
    );
  };

  const COLORS = {
    blue:   { bg: '#2563eb', border: '#bfdbfe', light: '#eff6ff', text: '#1d4ed8' },
    purple: { bg: '#7c3aed', border: '#ddd6fe', light: '#f5f3ff', text: '#6d28d9' },
    orange: { bg: '#d97706', border: '#fde68a', light: '#fffbeb', text: '#b45309' },
    teal:   { bg: '#0891b2', border: '#a5f3fc', light: '#ecfeff', text: '#0e7490' },
    green:  { bg: '#059669', border: '#a7f3d0', light: '#ecfdf5', text: '#047857' },
  };

  return (
    <div style={styles.page}>
      {/* Header + Tahun 1 baris */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ ...styles.pageTitle, marginBottom: 2 }}>Upload Data</h2>
          <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>Import data dari file Excel / CSV ke sistem</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', borderRadius: 12, padding: '10px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', flexShrink: 0 }}>
          <span style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>📅 Tahun</span>
          <input style={{ padding: '6px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontWeight: 700, color: '#1e3a5f', outline: 'none', width: 90, textAlign: 'center' }}
            type="number" value={tahun} onChange={e => setTahun(Number(e.target.value))} min="2020" max="2099"/>
        </div>
      </div>

      {/* Grid cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
        <UploadCard color={COLORS.blue}   icon="📦" title="Data Transaksi"         badge="XLSX • multi-file"
          hint="Upload file XLSX data transaksi harian per cabang"
          fileKey={fileKeys.t} multiple={true} accept=".xlsx"
          files={transaksiFiles} onFiles={setTransaksiFiles}
          onUpload={uploadTransaksi} loading={loadingMap.transaksi} status={statusMap.transaksi}/>

        <UploadCard color={COLORS.purple} icon="💰" title="Data Pelunasan"         badge="CSV / XLS / XLSX • multi-file"
          hint="Upload file buku besar pelunasan dari Smartlink"
          fileKey={fileKeys.s} multiple={true} accept=".csv,.xls,.xlsx"
          files={smartlinkFiles} onFiles={setSmartlinkFiles}
          onUpload={uploadSmartlink} loading={loadingMap.smartlink} status={statusMap.smartlink}/>

        <UploadCard color={COLORS.orange} icon="💳" title="QRIS BCA"               badge="XLSX • 1 file • password: BCABCA"
          hint="Upload 1 file XLSX laporan QRIS BCA"
          fileKey={fileKeys.q} multiple={false} accept=".xlsx"
          files={qrisFile} onFiles={setQrisFile}
          onUpload={uploadQris} loading={loadingMap.qris} status={statusMap.qris}/>

        <UploadCard color={COLORS.teal}   icon="🏦" title="Mutasi Rekening"        badge="CSV • multi-file"
          hint="Upload file CSV mutasi bank untuk rekonsiliasi"
          fileKey={fileKeys.m} multiple={true} accept=".csv"
          files={mutasiFiles} onFiles={setMutasiFiles}
          onUpload={uploadMutasi} loading={loadingMap.mutasi} status={statusMap.mutasi}/>

        <UploadCard color={COLORS.green}  icon="📋" title="Data Absensi"           badge="XLSX / XLS • multi-file"
          hint="Upload absensi karyawan — cabang dideteksi otomatis dari nama file"
          fileKey={fileKeys.a} multiple={true} accept=".xlsx,.xls"
          files={absensiFiles} onFiles={setAbsensiFiles}
          onUpload={uploadAbsensi} loading={loadingMap.absensi} status={statusMap.absensi}/>
      </div>
    </div>
  );
}


function MetricRow({ label, value, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
      <span style={{ fontSize: 13, color: '#64748b' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: color || '#374151' }}>{value}</span>
    </div>
  );
}

function MiniMetric({ label, value, color }) {
  return (
    <div style={{ flex: 1, textAlign: 'center', padding: '4px 8px' }}>
      <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 12, fontWeight: 700, color: color || '#374151' }}>{value}</div>
    </div>
  );
}

function DashboardPage() {
  const [bulan, setBulan] = useState(new Date().getMonth() + 1);
  const [tahun, setTahun] = useState(new Date().getFullYear());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [kasirSummary, setKasirSummary] = useState(null);
  const [trend, setTrend] = useState(null);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const [res, kasirRes, trendRes] = await Promise.all([
        api.get('/summary', { params: { bulan, tahun } }),
        api.get('/kasir/summary', { params: { bulan, tahun } }),
        api.get('/summary/trend')
      ]);
      setSummary(res.data);
      setKasirSummary(kasirRes.data);
      setTrend(trendRes.data);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSummary(); }, [bulan, tahun]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get('/export/excel', {
        params: { bulan, tahun },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Laporan_${BULAN_NAMA[bulan]}_${tahun}.xlsx`;
      a.click();
    } catch (err) {
      alert('Error export: ' + (err.response?.data?.detail || err.message));
    } finally {
      setExporting(false);
    }
  };

  const chartData = summary ? Object.entries(summary).map(([cabang, d]) => ({
    cabang: cabang,
    "Pelunasan Aplikasi": d.tunai_laporan,
    QRIS: d.qris_laporan,
    'QRIS Masuk': d.qris_masuk_mutasi,
    Transfer: d.transfer_masuk_mutasi,
  })) : [];

  const CABANG_COLORS = {
    PO: { main: '#2563eb', dark: '#1d4ed8', light: '#eff6ff' },
    PM: { main: '#7c3aed', dark: '#6d28d9', light: '#f5f3ff' },
    MT: { main: '#059669', dark: '#047857', light: '#ecfdf5' },
    DM: { main: '#d97706', dark: '#b45309', light: '#fffbeb' },
    TM: { main: '#dc2626', dark: '#b91c1c', light: '#fef2f2' },
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', paddingBottom: 40 }}>

      {/* ── Page Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 26, fontWeight: 800, color: '#1e3a5f', margin: 0, letterSpacing: -0.5 }}>Dashboard</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Ringkasan keuangan Mommy Wash Laundry</p>
        </div>

        {/* Filter Bar */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', background: '#fff', padding: '12px 16px', borderRadius: 14, boxShadow: '0 1px 6px rgba(0,0,0,0.07)', border: '1px solid #f1f5f9' }}>
          <div style={styles.formGroup}>
            <label style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>Bulan</label>
            <select style={styles.select} value={bulan} onChange={e => setBulan(Number(e.target.value))}>
              {BULAN_NAMA.slice(1).map((b, i) => <option key={i+1} value={i+1}>{b}</option>)}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>Tahun</label>
            <select style={styles.select} value={tahun} onChange={e => setTahun(Number(e.target.value))}>
              {Array.from({length:3},(_,i)=>new Date().getFullYear()-1+i).map(y=><option key={y}>{y}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={styles.btnPrimary} onClick={loadSummary} disabled={loading}>
              {loading ? '⏳ Memuat...' : '🔍 Tampilkan'}
            </button>
            {summary && (
              <button style={styles.btnSecondary} onClick={exportExcel} disabled={exporting}>
                {exporting ? '⏳' : '📥'} Export Excel
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Loading State ── */}
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 0', color: '#94a3b8' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>⏳</div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>Memuat data...</div>
          </div>
        </div>
      )}

      {/* ── Summary Cards ── */}
      {summary && !loading && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <div style={{ width: 4, height: 18, background: '#2563eb', borderRadius: 4 }} />
            <span style={{ fontSize: 14, fontWeight: 700, color: '#1e3a5f' }}>
              Periode: {BULAN_NAMA[bulan]} {tahun}
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 18, marginBottom: 32 }}>
            {Object.entries(summary).map(([cabang, d]) => {
              const col = CABANG_COLORS[cabang] || { main: '#2563eb', dark: '#1d4ed8', light: '#eff6ff' };
              const kasir = kasirSummary && kasirSummary[cabang];
              const totalRealisasi = d.qris_masuk_mutasi + d.transfer_masuk_mutasi + (kasir ? kasir.tunai : 0);
              return (
                <div key={cabang} style={{ background: '#fff', borderRadius: 18, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid #f1f5f9' }}>

                  {/* Colored Header */}
                  <div style={{ background: `linear-gradient(135deg, ${col.main} 0%, ${col.dark} 100%)`, padding: '18px 20px', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', right: -20, top: -20, width: 90, height: 90, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
                    <div style={{ position: 'absolute', right: 20, top: -30, width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
                      <span style={{ background: 'rgba(255,255,255,0.22)', color: '#fff', fontSize: 13, fontWeight: 800, padding: '4px 12px', borderRadius: 20 }}>{cabang}</span>
                      <span style={{ color: 'rgba(255,255,255,0.95)', fontWeight: 700, fontSize: 15 }}>{d.nama}</span>
                    </div>
                    <div style={{ marginTop: 6, fontSize: 11, color: 'rgba(255,255,255,0.6)', position: 'relative' }}>Pelunasan & Realisasi</div>
                  </div>

                  {/* Metrics Body */}
                  <div style={{ padding: '16px 20px' }}>
                    <MetricRow label="💵 Pelunasan Aplikasi" value={formatRp(d.tunai_laporan)} color="#2563eb" />
                    <MetricRow label="💳 Laporan QRIS" value={formatRp(d.qris_laporan)} color="#7c3aed" />
                    <MetricRow label="✅ QRIS Masuk Mutasi" value={formatRp(d.qris_masuk_mutasi)} color="#059669" />
                    <MetricRow label="🔄 Transfer Masuk" value={formatRp(d.transfer_masuk_mutasi)} color="#d97706" />

                    {kasir && (
                      <div style={{ marginTop: 14, background: '#f8fafc', borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                        <div style={{ padding: '7px 12px', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>📋 Data Kasir</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>{formatRp(kasir.total)}</span>
                        </div>
                        <div style={{ display: 'flex', padding: '10px 4px' }}>
                          <MiniMetric label="Tunai" value={formatRp(kasir.tunai)} color="#059669" />
                          <div style={{ width: 1, background: '#e2e8f0', margin: '0 2px' }} />
                          <MiniMetric label="QRIS" value={formatRp(kasir.qris)} color="#7c3aed" />
                          <div style={{ width: 1, background: '#e2e8f0', margin: '0 2px' }} />
                          <MiniMetric label="Transfer" value={formatRp(kasir.transfer)} color="#d97706" />
                        </div>
                      </div>
                    )}

                    {/* Total Realisasi - Highlighted */}
                    <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 12, background: col.light, border: `1.5px solid ${col.main}30`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: col.dark, textTransform: 'uppercase', letterSpacing: 0.3 }}>🎯 Total Realisasi</span>
                      <span style={{ fontSize: 17, fontWeight: 800, color: col.main }}>{formatRp(totalRealisasi)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── Trend Section ── */}
      {trend && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <div style={{ width: 4, height: 18, background: '#2563eb', borderRadius: 4 }} />
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e3a5f', margin: 0 }}>Trend 6 Bulan Terakhir</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16 }}>
            {["PO","PM","MT","DM","TM"].map(cabang => {
              const d = trend[cabang];
              if (!d) return null;
              const col = CABANG_COLORS[cabang] || { main: '#2563eb', dark: '#1d4ed8' };
              const chartData = d.labels.map((label, i) => ({
                name: label,
                Pelunasan: d.pelunasan[i],
                Realisasi: d.realisasi[i],
              }));
              return (
                <div key={cabang} style={{ background: '#fff', borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ background: col.main, color: '#fff', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{cabang}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{d.nama}</span>
                  </div>
                  <div style={{ padding: '12px 8px' }}>
                    <ResponsiveContainer width="100%" height={150}>
                      <BarChart data={chartData} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                        <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} tickFormatter={v => v >= 1000000 ? `${(v/1000000).toFixed(1)}jt` : v >= 1000 ? `${(v/1000).toFixed(0)}rb` : v} />
                        <Tooltip formatter={(val) => ['Rp ' + Number(val).toLocaleString('id-ID')]} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="Pelunasan" fill={col.main} radius={[3,3,0,0]} />
                        <Bar dataKey="Realisasi" fill="#10b981" radius={[3,3,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
}


// =====================
// DETAIL PAGE
// =====================
function DetailPage() {
  const CABANG_LIST = [
    { code: "PO", nama: "Puri Orchard" },
    { code: "PM", nama: "Palm Mansion" },
    { code: "MT", nama: "Metro" },
    { code: "DM", nama: "Daan Mogot City" },
    { code: "TM", nama: "Tomang" },
  ];

  const [cabang, setCabang] = useState("PO");
  const [bulan, setBulan] = useState(new Date().getMonth() + 1);
  const [tahun, setTahun] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("transaksi");
  const [deletedTransaksi, setDeletedTransaksi] = useState([]);
  const [penghapusanData, setPenghapusanData] = useState([]);
  const [transaksiData, setTransaksiData] = useState([]);

  // Fetch log state
  const [fetchLog, setFetchLog] = useState([]);
  const [fetchTriggering, setFetchTriggering] = useState(false);
  const [fetchTriggerMsg, setFetchTriggerMsg] = useState("");
  const [fetchResult, setFetchResult] = useState(null); // {fetched, inserted}
  const today = new Date().toISOString().slice(0, 10);
  const [fetchStartDate, setFetchStartDate] = useState(today);
  const [fetchEndDate, setFetchEndDate] = useState(today);

  const loadFetchLog = async () => {
    try {
      const res = await api.get("/smartlink/fetch-log", { params: { cabang, limit: 30 } });
      setFetchLog(res.data);
    } catch {}
  };

  const handleTriggerFetch = async () => {
    setFetchTriggering(true);
    setFetchTriggerMsg("");
    setFetchResult(null);
    const triggerTime = new Date().toISOString();
    try {
      const res = await api.post("/smartlink/trigger-fetch", {
        cabang,
        start_date: fetchStartDate,
        end_date: fetchEndDate,
      });
      if (!res.data.ok) {
        setFetchTriggerMsg(res.data.message);
        setFetchTriggering(false);
        return;
      }
      // Poll fetch-log sampai ada entry baru setelah triggerTime
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const logRes = await api.get("/smartlink/fetch-log", { params: { cabang, limit: 10 } });
          const newEntry = logRes.data.find(r =>
            r.jenis === "buku_besar" && new Date(r.waktu + "Z") > new Date(triggerTime)
          );
          if (newEntry) {
            clearInterval(poll);
            loadFetchLog();
            if (newEntry.status === "success") {
              setFetchResult({ fetched: newEntry.records_fetched, inserted: newEntry.records_inserted });
              setFetchTriggerMsg("");
            } else {
              setFetchTriggerMsg("Fetch gagal. Coba lagi.");
            }
            setFetchTriggering(false);
          } else if (attempts >= 40) { // max ~2 menit
            clearInterval(poll);
            setFetchTriggerMsg("Timeout menunggu hasil fetch.");
            setFetchTriggering(false);
          }
        } catch {}
      }, 3000);
    } catch (err) {
      setFetchTriggerMsg("Gagal: " + (err.response?.data?.detail || err.message));
      setFetchTriggering(false);
    }
  };

  useEffect(() => { loadFetchLog(); }, [cabang]);
  useEffect(() => { if (tab === "fetch") loadFetchLog(); }, [tab]);

  const loadDeleted = async () => {
    try {
      const [resDeleted, resPenghapusan] = await Promise.all([
        api.get("/detail/transaksi-data/deleted", { params: { cabang, tahun } }),
        api.get("/detail/penghapusan-smartlink", { params: { cabang } }),
      ]);
      setDeletedTransaksi(resDeleted.data);
      setPenghapusanData(resPenghapusan.data);
    } catch {}
  };

  useEffect(() => { if (tab === "riwayat") loadDeleted(); }, [tab, cabang, tahun]);

  const [transaksiPage, setTransaksiPage] = useState(1);
  const [transaksiTotal, setTransaksiTotal] = useState(0);
  const [transaksiTotalPages, setTransaksiTotalPages] = useState(1);
  const [transaksiTotalTagihan, setTransaksiTotalTagihan] = useState(0);
  const [transaksiTotalTagihanFiltered, setTransaksiTotalTagihanFiltered] = useState(0);
  const [transaksiTotalBelumLunas, setTransaksiTotalBelumLunas] = useState(0);

  const [filterStatus, setFilterStatus] = useState("semua");
  const [filterNama, setFilterNama] = useState("");
  const [filterNamaInput, setFilterNamaInput] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setFilterNama(filterNamaInput), 400);
    return () => clearTimeout(t);
  }, [filterNamaInput]);
  const [selectedTransaksi, setSelectedTransaksi] = useState([]);
  const [alasanTransaksi, setAlasanTransaksi] = useState("");
  const [deletingTransaksi, setDeletingTransaksi] = useState(false);
  const [hapusError, setHapusError] = useState("");
  const [hapusSuccess, setHapusSuccess] = useState("");

  const toggleSelectTransaksi = (id) => {
    setSelectedTransaksi(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    setHapusSuccess("");
  };

  const toggleAllTransaksi = () => {
    setSelectedTransaksi(selectedTransaksi.length === transaksiData.length ? [] : transaksiData.map(d => d.id));
  };

  const handleHapusTransaksi = async () => {
    if (selectedTransaksi.length === 0) return;
    if (!alasanTransaksi.trim()) { setHapusError("Alasan penghapusan wajib diisi!"); return; }
    setHapusError("");
    setDeletingTransaksi(true);
    const jumlah = selectedTransaksi.length;
    try {
      for (const id of selectedTransaksi) {
        await api.delete(`/detail/transaksi-data/${id}`, { params: { alasan: alasanTransaksi } });
      }
      setSelectedTransaksi([]);
      setAlasanTransaksi("");
      setHapusError("");
      loadTransaksiData(transaksiPage);
      loadDeleted();
      setHapusSuccess(`✓ ${jumlah} transaksi berhasil dihapus`);
    } catch (err) {
      setHapusError("Gagal hapus: " + (err.response?.data?.detail || err.message));
    } finally {
      setDeletingTransaksi(false);
    }
  };

  const loadTransaksiData = async (page = 1) => {
    try {
      const res = await api.get("/detail/transaksi-data", { params: { cabang, bulan, tahun, page, per_page: 100, status: filterStatus, nama: filterNama } });
      setTransaksiData(res.data.data);
      setTransaksiTotal(res.data.total);
      setTransaksiTotalPages(res.data.total_pages);
      setTransaksiTotalTagihan(res.data.total_tagihan);
      setTransaksiTotalTagihanFiltered(res.data.total_tagihan_filtered ?? res.data.total_tagihan);
      setTransaksiTotalBelumLunas(res.data.total_belum_lunas);
      setTransaksiPage(page);
    } catch {}
  };

  useEffect(() => {
    if (tab === "transaksi") {
      setFilterStatus("semua");
      setFilterNama("");
      setFilterNamaInput("");
      setSelectedTransaksi([]);
    }
  }, [tab, cabang, bulan, tahun]);

  useEffect(() => {
    if (tab === "transaksi") {
      setSelectedTransaksi([]);
      loadTransaksiData(1);
    }
  }, [tab, cabang, bulan, tahun, filterStatus, filterNama]);

  const cabangNama = CABANG_LIST.find(c => c.code === cabang)?.nama || "";

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20, flexWrap:"wrap", gap:12}}>
        <div>
          <h2 style={{...styles.pageTitle, marginBottom:2}}>Detail Transaksi</h2>
          <span style={{fontSize:14, color:"#6b7280"}}>{cabang} — {cabangNama} · {BULAN_NAMA[bulan]} {tahun}</span>
        </div>
        {/* Filter bar */}
        <div style={{display:"flex", gap:8, alignItems:"flex-end", flexWrap:"wrap"}}>
          <div>
            <label style={{fontSize:14, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3, textTransform:"uppercase"}}>Cabang</label>
            <select style={{...styles.select, margin:0}} value={cabang} onChange={e => setCabang(e.target.value)}>
              {CABANG_LIST.map(c => <option key={c.code} value={c.code}>{c.code} - {c.nama}</option>)}
            </select>
          </div>
          <div>
            <label style={{fontSize:14, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3, textTransform:"uppercase"}}>Bulan</label>
            <select style={{...styles.select, margin:0}} value={bulan} onChange={e => setBulan(Number(e.target.value))}>
              {BULAN_NAMA.slice(1).map((b, i) => <option key={i+1} value={i+1}>{b}</option>)}
            </select>
          </div>
          <div>
            <label style={{fontSize:14, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3, textTransform:"uppercase"}}>Tahun</label>
            <select style={{...styles.select, margin:0}} value={tahun} onChange={e => setTahun(Number(e.target.value))}>
              {Array.from({length:3},(_,i)=>new Date().getFullYear()-1+i).map(y=><option key={y}>{y}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Summary cards — hanya tampil di tab transaksi */}
      {tab === "transaksi" && transaksiTotal > 0 && (
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(160px, 1fr))", gap:12, marginBottom:20}}>
          {[
            {label:"Total Nota", value:transaksiTotal + " Nota", sub:formatRp(transaksiTotalTagihanFiltered), color:"#2563eb", icon:"📋"},
            {label:"Total Tagihan", value:formatRp(transaksiTotalTagihan), color:"#059669", icon:"💰"},
            {label:"Belum Lunas", value:formatRp(transaksiTotalBelumLunas), color:"#ef4444", icon:"⏳"},
          ].map(({label,value,sub,color,icon}) => (
            <div key={label} style={{background:"#fff", borderRadius:10, padding:"10px 14px", boxShadow:"0 1px 4px rgba(0,0,0,0.07)", borderTop:`3px solid ${color}`}}>
              <div style={{fontSize:14, color:"#6b7280", fontWeight:600, textTransform:"uppercase", marginBottom:4}}>{icon} {label}</div>
              <div style={{fontSize:22, fontWeight:700, color}}>{value}</div>
              {sub && <div style={{fontSize:14, color:"#6b7280", marginTop:2}}>{sub}</div>}
            </div>
          ))}
        </div>
      )}

      <div style={{background:"#fff", borderRadius:12, boxShadow:"0 1px 4px rgba(0,0,0,0.08)", overflow:"hidden"}}>
        {/* Tab navigation */}
        <div style={{display:"flex", borderBottom:"2px solid #f1f5f9", padding:"0 14px"}}>
          {[["transaksi","📦 Data Transaksi"],["riwayat","🗑️ Riwayat Hapus"]].map(([val,label]) => (
            <button key={val} onClick={() => setTab(val)}
              style={{padding:"10px 14px", fontSize:14, fontWeight:tab===val?700:500, background:"none", border:"none", cursor:"pointer",
                color:tab===val?"#2563eb":"#6b7280", borderBottom:tab===val?"2px solid #2563eb":"2px solid transparent",
                marginBottom:"-2px", transition:"all 0.15s"}}>
              {label}
            </button>
          ))}
          <button onClick={() => setTab("fetch")}
            style={{padding:"10px 14px", fontSize:14, fontWeight:tab==="fetch"?700:500, background:"none", border:"none", cursor:"pointer",
              color:tab==="fetch"?"#2563eb":"#6b7280", borderBottom:tab==="fetch"?"2px solid #2563eb":"2px solid transparent",
              marginBottom:"-2px", transition:"all 0.15s", display:"flex", alignItems:"center", gap:5}}>
            🔄 Fetch Status
            {fetchLog.filter(l=>l.status==="error").length > 0 && (
              <span style={{background:"#ef4444", color:"#fff", borderRadius:10, fontSize:11, padding:"1px 6px", fontWeight:700}}>
                {fetchLog.filter(l=>l.status==="error").length}
              </span>
            )}
          </button>
        </div>

        <div style={{padding:"14px"}}>
          {tab === "transaksi" && (
            <>
              {/* Filter & Search bar */}
              <div style={{display:"flex", gap:8, marginBottom:16, flexWrap:"wrap", alignItems:"center"}}>
                <select style={{...styles.select, width:"auto", padding:"7px 12px", fontSize:14, margin:0}}
                  value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                  <option value="semua">Semua Status</option>
                  <option value="belum">Belum Lunas</option>
                  <option value="lunas">Sudah Lunas</option>
                </select>
                <div style={{position:"relative", flex:1, minWidth:200, maxWidth:320}}>
                  <span style={{position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:"#9ca3af", fontSize:14}}>🔍</span>
                  <input style={{...styles.input, paddingLeft:32, margin:0, width:"100%", boxSizing:"border-box", fontSize:14}}
                    placeholder="Cari nama customer..."
                    value={filterNamaInput} onChange={e => setFilterNamaInput(e.target.value)}/>
                </div>
                {(filterStatus !== "semua" || filterNamaInput) && (
                  <button style={{padding:"7px 14px", borderRadius:8, border:"1px solid #e2e8f0", background:"#f9fafb", cursor:"pointer", fontSize:14, color:"#374151", fontWeight:500}}
                    onClick={() => { setFilterStatus("semua"); setFilterNama(""); setFilterNamaInput(""); }}>
                    ✕ Reset
                  </button>
                )}
                {/* Pagination kanan */}
                {transaksiTotalPages > 1 && (
                  <div style={{marginLeft:"auto", display:"flex", gap:4}}>
                    {Array.from({length: transaksiTotalPages}, (_, i) => i + 1).map(p => (
                      <button key={p} onClick={() => loadTransaksiData(p)}
                        style={{width:32, height:32, borderRadius:6, border:"1px solid #e2e8f0", cursor:"pointer", fontSize:14,
                          fontWeight:p===transaksiPage?700:400, background:p===transaksiPage?"#2563eb":"#fff",
                          color:p===transaksiPage?"#fff":"#374151"}}>
                        {p}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Success message */}
              {hapusSuccess && selectedTransaksi.length === 0 && (
                <div style={{background:"#dcfce7", border:"1px solid #86efac", borderRadius:10, padding:"10px 16px", marginBottom:12, fontSize:14, fontWeight:600, color:"#166534", display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                  {hapusSuccess}
                  <button onClick={() => setHapusSuccess("")} style={{background:"none", border:"none", cursor:"pointer", color:"#166534", fontSize:16, lineHeight:1}}>×</button>
                </div>
              )}

              {/* Delete action bar */}
              {selectedTransaksi.length > 0 && (
                <div style={{background:"#fef9c3", border:"1px solid #fde047", borderRadius:10, padding:"12px 16px", marginBottom:16, display:"flex", gap:12, alignItems:"center", flexWrap:"wrap"}}>
                  <span style={{fontSize:14, fontWeight:700, color:"#854d0e"}}>⚠️ {selectedTransaksi.length} transaksi dipilih</span>
                  <div style={{flex:1, minWidth:200, display:"flex", flexDirection:"column", gap:4}}>
                    <input style={{...styles.input, marginBottom:0, fontSize:14, borderColor: hapusError ? "#ef4444" : undefined}}
                      placeholder="Alasan penghapusan (wajib diisi)"
                      value={alasanTransaksi} onChange={e => { setAlasanTransaksi(e.target.value); setHapusError(""); }} />
                    {hapusError && <span style={{fontSize:12, color:"#ef4444", fontWeight:500}}>{hapusError}</span>}
                  </div>
                  <button style={{background:"#ef4444", color:"#fff", border:"none", padding:"8px 18px", borderRadius:8, cursor:"pointer", fontWeight:600, fontSize:14, whiteSpace:"nowrap"}}
                    onClick={handleHapusTransaksi} disabled={deletingTransaksi}>
                    {deletingTransaksi ? "Menghapus..." : "🗑️ Hapus"}
                  </button>
                  <button style={{background:"#f1f5f9", color:"#374151", border:"1px solid #e2e8f0", padding:"8px 14px", borderRadius:8, cursor:"pointer", fontSize:14}}
                    onClick={() => { setSelectedTransaksi([]); setAlasanTransaksi(""); setHapusError(""); }}>
                    Batal
                  </button>
                </div>
              )}

              {transaksiData.length === 0 ? (
                <div style={{textAlign:"center", padding:"48px 0", color:"#9ca3af"}}>
                  <div style={{fontSize:32, marginBottom:8}}>📭</div>
                  <div style={{fontSize:14}}>{filterNamaInput ? `Customer "${filterNamaInput}" tidak ditemukan` : "Tidak ada data transaksi"}</div>
                </div>
              ) : (
                <div style={{overflowX:"auto"}}>
                  <table style={{...styles.table, fontSize:14}}>
                    <thead>
                      <tr style={{background:"#f8fafc"}}>
                        <th style={{...styles.th, width:32}}><input type="checkbox" checked={selectedTransaksi.length === transaksiData.length && transaksiData.length > 0} onChange={toggleAllTransaksi}/></th>
                        <th style={{...styles.th, width:150}}>No Nota</th>
                        <th style={{...styles.th, minWidth:120}}>Customer</th>
                        <th style={{...styles.th, width:100}}>No HP</th>
                        <th style={{...styles.th, width:88}}>Masuk</th>
                        <th style={{...styles.th, width:88}}>Selesai</th>
                        <th style={{...styles.th, width:95, textAlign:"right"}}>Tagihan</th>
                        <th style={{...styles.th, width:95}}>Status</th>
                        <th style={{...styles.th, width:88}}>Pelunasan</th>
                        <th style={{...styles.th, width:58}}>Jam</th>
                        <th style={{...styles.th, width:110, textAlign:"right"}}>Nominal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transaksiData.map((row, i) => (
                        <tr key={i} style={{background: selectedTransaksi.includes(row.id) ? "#eff6ff" : i%2===0 ? "#fff" : "#f8fafc", transition:"background 0.1s"}}>
                          <td style={styles.td}><input type="checkbox" checked={selectedTransaksi.includes(row.id)} onChange={() => toggleSelectTransaksi(row.id)}/></td>
                          <td style={{...styles.td, fontFamily:"monospace", fontSize:14, fontWeight:700, color:"#2563eb"}}>{row.no_nota}</td>
                          <td style={{...styles.td, fontWeight:600}}>{row.customer}</td>
                          <td style={{...styles.td, color:"#6b7280", fontSize:14}}>{row.no_hp || "-"}</td>
                          <td style={{...styles.td, whiteSpace:"nowrap", fontSize:14}}>{formatTglPendek(row.tgl_terima)}</td>
                          <td style={{...styles.td, whiteSpace:"nowrap", fontSize:14}}>{formatTglPendek(row.tgl_selesai)}</td>
                          <td style={{...styles.td, textAlign:"right", fontWeight:600}}>{formatRp(row.total_tagihan)}</td>
                          <td style={{...styles.td}}>
                            {row.tgl_pelunasan
                              ? <span style={{display:"inline-block", background:"#dcfce7", color:"#166534", padding:"2px 10px", borderRadius:20, fontSize:14, fontWeight:700, whiteSpace:"nowrap"}}>✓ Lunas</span>
                              : <span style={{display:"inline-block", background:"#fee2e2", color:"#991b1b", padding:"2px 10px", borderRadius:20, fontSize:14, fontWeight:700, whiteSpace:"nowrap"}}>Belum</span>}
                          </td>
                          <td style={{...styles.td, whiteSpace:"nowrap", fontSize:14, color:"#4a5568"}}>{formatTglPendek(row.tgl_pelunasan)}</td>
                          <td style={{...styles.td, whiteSpace:"nowrap", fontSize:14, color:"#6b7280"}}>{row.jam_pelunasan || "—"}</td>
                          <td style={{...styles.td, textAlign:"right", color:"#059669", fontWeight:600}}>{row.nominal_pelunasan ? formatRp(row.nominal_pelunasan) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {tab === "riwayat" && (
            <>
              {/* --- Penghapusan dari Smartlink --- */}
              {penghapusanData.length > 0 && (
                <div style={{marginBottom:24}}>
                  <div style={{fontSize:14, fontWeight:700, color:"#dc2626", marginBottom:8, display:"flex", alignItems:"center", gap:6}}>
                    <span>🔴</span>
                    <span>Dihapus via Smartlink ({penghapusanData.length} transaksi)</span>
                  </div>
                  <div style={{overflowX:"auto"}}>
                    <table style={{...styles.table, fontSize:14}}>
                      <thead>
                        <tr style={{background:"#fff5f5"}}>
                          <th style={styles.th}>No Nota</th>
                          <th style={styles.th}>Customer</th>
                          <th style={{...styles.th, textAlign:"right"}}>Tagihan</th>
                          <th style={styles.th}>Tgl Masuk</th>
                          <th style={styles.th}>Tgl Permintaan</th>
                          <th style={styles.th}>Keterangan</th>
                          <th style={styles.th}>Tgl Konfirmasi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {penghapusanData.map((row, i) => (
                          <tr key={i} style={{background: i%2===0 ? "#fff" : "#fff5f5"}}>
                            <td style={{...styles.td, fontFamily:"monospace", fontSize:14, fontWeight:700, color:"#dc2626"}}>{row.no_nota}</td>
                            <td style={{...styles.td, fontWeight:600}}>{row.customer}</td>
                            <td style={{...styles.td, textAlign:"right"}}>{formatRp(row.total_tagihan)}</td>
                            <td style={{...styles.td, whiteSpace:"nowrap"}}>{row.tgl_terima ? formatTglPendek(row.tgl_terima) : "—"}</td>
                            <td style={{...styles.td, whiteSpace:"nowrap", color:"#dc2626"}}>{row.tanggal_request ? formatTglPendek(row.tanggal_request.split("T")[0]) : "—"}</td>
                            <td style={{...styles.td, color:"#6b7280"}}>{row.keterangan || "—"}</td>
                            <td style={{...styles.td, whiteSpace:"nowrap", color:"#059669"}}>{row.tanggal_konfirmasi ? formatTglPendek(row.tanggal_konfirmasi.split("T")[0]) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* --- Dihapus manual via sistem --- */}
              {deletedTransaksi.length === 0 && penghapusanData.length === 0 ? (
                <div style={{textAlign:"center", padding:"48px 0", color:"#9ca3af"}}>
                  <div style={{fontSize:32, marginBottom:8}}>🗂️</div>
                  <div style={{fontSize:14}}>Belum ada transaksi yang dihapus</div>
                </div>
              ) : deletedTransaksi.length > 0 && (
                <div>
                  <div style={{fontSize:14, fontWeight:700, color:"#374151", marginBottom:8, display:"flex", alignItems:"center", gap:6}}>
                    <span>🗑️</span>
                    <span>Dihapus manual ({deletedTransaksi.length} transaksi)</span>
                  </div>
                  <div style={{overflowX:"auto"}}>
                    <table style={{...styles.table, fontSize:14}}>
                      <thead>
                        <tr style={{background:"#f8fafc"}}>
                          <th style={styles.th}>No Nota</th>
                          <th style={styles.th}>Customer</th>
                          <th style={{...styles.th, textAlign:"right"}}>Total Tagihan</th>
                          <th style={styles.th}>Alasan</th>
                          <th style={styles.th}>Dihapus oleh</th>
                          <th style={styles.th}>Waktu Hapus</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deletedTransaksi.map((row, i) => (
                          <tr key={i} style={{background: i%2===0 ? "#fff" : "#f8fafc"}}>
                            <td style={{...styles.td, fontFamily:"monospace", fontSize:14, fontWeight:700, color:"#2563eb"}}>{row.no_nota}</td>
                            <td style={{...styles.td, fontWeight:600}}>{row.customer}</td>
                            <td style={{...styles.td, textAlign:"right", fontWeight:600}}>{formatRp(row.total_tagihan)}</td>
                            <td style={{...styles.td, color:"#6b7280"}}>{row.alasan}</td>
                            <td style={styles.td}>{row.deleted_by}</td>
                            <td style={{...styles.td, color:"#6b7280", whiteSpace:"nowrap"}}>{new Date(row.deleted_at).toLocaleString("id-ID")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {tab === "fetch" && (
            <>
              <div style={{marginBottom:16}}>
                <div style={{fontSize:15, fontWeight:700, color:"#1e293b", marginBottom:12}}>
                  Riwayat Fetch Gagal — {cabang}
                </div>
                <div style={{display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:10, padding:"10px 14px"}}>
                  <span style={{fontSize:13, color:"#475569", fontWeight:500}}>Fetch Manual:</span>
                  <input type="date" value={fetchStartDate} onChange={e => setFetchStartDate(e.target.value)}
                    style={{padding:"5px 8px", border:"1px solid #e2e8f0", borderRadius:6, fontSize:13, color:"#1e293b"}} />
                  <span style={{fontSize:13, color:"#94a3b8"}}>s/d</span>
                  <input type="date" value={fetchEndDate} onChange={e => setFetchEndDate(e.target.value)}
                    style={{padding:"5px 8px", border:"1px solid #e2e8f0", borderRadius:6, fontSize:13, color:"#1e293b"}} />
                  <button onClick={handleTriggerFetch} disabled={fetchTriggering}
                    style={{padding:"6px 16px", background:fetchTriggering?"#94a3b8":"#2563eb", color:"#fff",
                      border:"none", borderRadius:6, cursor:fetchTriggering?"not-allowed":"pointer", fontSize:13, fontWeight:600,
                      display:"flex", alignItems:"center", gap:6}}>
                    {fetchTriggering
                      ? <><span style={{display:"inline-block", animation:"spin 1s linear infinite"}}>⟳</span> Fetching...</>
                      : "🔄 Fetch"}
                  </button>
                  {fetchResult && (
                    <span style={{fontSize:13, color:"#059669", fontWeight:600}}>
                      ✓ Fetch berhasil — {fetchResult.fetched} data fetched, {fetchResult.inserted} inserted ke database
                    </span>
                  )}
                  {fetchTriggerMsg && (
                    <span style={{fontSize:13, color:"#dc2626", fontWeight:500}}>{fetchTriggerMsg}</span>
                  )}
                  <button onClick={loadFetchLog}
                    style={{padding:"6px 10px", background:"#fff", color:"#475569",
                      border:"1px solid #e2e8f0", borderRadius:6, cursor:"pointer", fontSize:13, marginLeft:"auto"}}>
                    ↻ Refresh
                  </button>
                </div>
                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
              </div>
              {fetchLog.filter(r => r.status === "error").length === 0 ? (
                <div style={{color:"#94a3b8", fontSize:14, textAlign:"center", padding:"40px 0"}}>
                  Tidak ada fetch yang gagal
                </div>
              ) : (
                <div style={{overflowX:"auto"}}>
                  <table style={{...styles.table, fontSize:13}}>
                    <thead>
                      <tr style={{background:"#f8fafc"}}>
                        <th style={styles.th}>Waktu</th>
                        <th style={styles.th}>Jenis</th>
                        <th style={{...styles.th, textAlign:"right"}}>Fetched</th>
                        <th style={{...styles.th, textAlign:"right"}}>Inserted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fetchLog.filter(r => r.status === "error").map(row => (
                        <tr key={row.id} style={{background:"#fff5f5"}}>
                          <td style={{...styles.td, whiteSpace:"nowrap", fontWeight:500}}>
                            {row.waktu ? new Date(row.waktu.endsWith('Z') || row.waktu.includes('+') ? row.waktu : row.waktu + 'Z').toLocaleString("id-ID",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"}) : "—"}
                          </td>
                          <td style={{...styles.td, color:"#475569"}}>
                            {row.jenis === "buku_besar" ? "📒 Buku Besar" : "📦 Transaksi"}
                          </td>
                          <td style={{...styles.td, textAlign:"right", color:"#475569"}}>{row.records_fetched ?? "—"}</td>
                          <td style={{...styles.td, textAlign:"right", color:"#059669", fontWeight:600}}>{row.records_inserted ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// =====================
// STATUS PAGE (LOG UPLOAD)
// =====================
function StatusPage() {
  const CABANG_LIST = [
    { code: "", nama: "Semua Cabang" },
    { code: "PO", nama: "Puri Orchard" },
    { code: "PM", nama: "Palm Mansion" },
    { code: "MT", nama: "Metro" },
    { code: "DM", nama: "Daan Mogot City" },
    { code: "TM", nama: "Tomang" },
  ];

  const JENIS_LABEL = {
    "transaksi": "📦 Data Transaksi",
    "pelunasan": "💰 Data Pelunasan",
    "qris": "💳 QRIS BCA",
    "mutasi": "🏦 Mutasi",
  };

  const [cabang, setCabang] = useState("");
  const [bulan, setBulan] = useState(new Date().getMonth() + 1);
  const [tahun, setTahun] = useState(new Date().getFullYear());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadLog = async () => {
    setLoading(true);
    try {
      const res = await api.get("/upload/log", { params: { cabang, bulan, tahun } });
      setData(res.data);
    } catch (err) {
      alert("Error: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLog = async (id) => {
    if (!window.confirm("Hapus log ini?")) return;
    try {
      await api.delete(`/upload/log/${id}`);
      loadLog();
    } catch (err) {
      alert("Gagal hapus: " + (err.response?.data?.detail || err.message));
    }
  };

  useEffect(() => { loadLog(); }, [cabang, bulan, tahun]);

  // Group by jenis
  const grouped = {};
  data.forEach(d => {
    if (!grouped[d.jenis]) grouped[d.jenis] = [];
    grouped[d.jenis].push(d);
  });

  const JENIS_CONFIG = {
    transaksi: { label:"Data Transaksi", icon:"📦", color:"#2563eb", bg:"#eff6ff" },
    pelunasan:  { label:"Data Pelunasan", icon:"💰", color:"#059669", bg:"#f0fdf4" },
    qris:       { label:"QRIS BCA",       icon:"💳", color:"#7c3aed", bg:"#f5f3ff" },
    mutasi:     { label:"Mutasi Bank",    icon:"🏦", color:"#d97706", bg:"#fffbeb" },
  };

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20, flexWrap:"wrap", gap:12}}>
        <div>
          <h2 style={{...styles.pageTitle, marginBottom:2}}>Status Upload</h2>
          <span style={{fontSize:14, color:"#6b7280"}}>Riwayat file yang berhasil diupload</span>
        </div>
        <div style={{display:"flex", gap:8, alignItems:"flex-end", flexWrap:"wrap"}}>
          <div>
            <label style={{fontSize:14, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3, textTransform:"uppercase"}}>Cabang</label>
            <select style={{...styles.select, margin:0}} value={cabang} onChange={e => setCabang(e.target.value)}>
              {CABANG_LIST.map(c => <option key={c.code} value={c.code}>{c.code ? `${c.code} - ` : ""}{c.nama}</option>)}
            </select>
          </div>
          <div>
            <label style={{fontSize:14, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3, textTransform:"uppercase"}}>Bulan</label>
            <select style={{...styles.select, margin:0}} value={bulan} onChange={e => setBulan(Number(e.target.value))}>
              {BULAN_NAMA.slice(1).map((b, i) => <option key={i+1} value={i+1}>{b}</option>)}
            </select>
          </div>
          <div>
            <label style={{fontSize:14, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3, textTransform:"uppercase"}}>Tahun</label>
            <select style={{...styles.select, margin:0}} value={tahun} onChange={e => setTahun(Number(e.target.value))}>
              {Array.from({length:3},(_,i)=>new Date().getFullYear()-1+i).map(y=><option key={y}>{y}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Summary badges */}
      {!loading && data.length > 0 && (
        <div style={{display:"flex", gap:10, marginBottom:20, flexWrap:"wrap"}}>
          {["transaksi","pelunasan","qris","mutasi"].map(jenis => {
            const cfg = JENIS_CONFIG[jenis];
            const count = (grouped[jenis] || []).length;
            if (!count) return null;
            return (
              <div key={jenis} style={{display:"flex", alignItems:"center", gap:8, background:cfg.bg, border:`1px solid ${cfg.color}30`, borderRadius:20, padding:"6px 14px"}}>
                <span style={{fontSize:14}}>{cfg.icon}</span>
                <span style={{fontSize:14, fontWeight:600, color:cfg.color}}>{cfg.label}</span>
                <span style={{background:cfg.color, color:"#fff", borderRadius:20, fontSize:14, fontWeight:700, padding:"1px 8px"}}>{count}</span>
              </div>
            );
          })}
        </div>
      )}

      {loading ? (
        <div style={{textAlign:"center", padding:60, color:"#9ca3af"}}>
          <div style={{fontSize:32, marginBottom:8}}>⏳</div>
          <div>Memuat data...</div>
        </div>
      ) : data.length === 0 ? (
        <div style={{background:"#fff", borderRadius:14, boxShadow:"0 1px 4px rgba(0,0,0,0.07)", padding:"60px 0", textAlign:"center", color:"#9ca3af"}}>
          <div style={{fontSize:40, marginBottom:12}}>📭</div>
          <div style={{fontSize:16, fontWeight:600, marginBottom:4}}>Belum ada log upload</div>
          <div style={{fontSize:14}}>Coba ubah filter cabang, bulan, atau tahun</div>
        </div>
      ) : (
        ["transaksi","pelunasan","qris","mutasi"].map(jenis => {
          const rows = grouped[jenis];
          if (!rows || rows.length === 0) return null;
          const cfg = JENIS_CONFIG[jenis];
          return (
            <div key={jenis} style={{background:"#fff", borderRadius:14, boxShadow:"0 1px 4px rgba(0,0,0,0.07)", marginBottom:16, overflow:"hidden"}}>
              {/* Section header */}
              <div style={{display:"flex", alignItems:"center", gap:10, padding:"14px 20px", borderBottom:`2px solid ${cfg.bg}`, background:`linear-gradient(to right, ${cfg.bg}, #fff)`}}>
                <span style={{fontSize:22}}>{cfg.icon}</span>
                <span style={{fontWeight:700, fontSize:14, color:cfg.color}}>{cfg.label}</span>
                <span style={{background:cfg.color, color:"#fff", borderRadius:20, fontSize:14, fontWeight:700, padding:"2px 10px", marginLeft:4}}>{rows.length} file</span>
              </div>
              <div style={{overflowX:"auto"}}>
                <table style={{...styles.table, fontSize:14}}>
                  <thead>
                    <tr style={{background:"#f8fafc"}}>
                      <th style={styles.th}>Nama File</th>
                      <th style={{...styles.th, textAlign:"center"}}>Jumlah Data</th>
                      <th style={styles.th}>Diupload Oleh</th>
                      <th style={styles.th}>Tanggal & Waktu</th>
                      <th style={{...styles.th, width:40}}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr key={i} style={{background: i%2===0 ? "#fff" : "#f8fafc"}}>
                        <td style={{...styles.td, fontWeight:600, color:"#1e293b", maxWidth:300, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{row.filename}</td>
                        <td style={{...styles.td, textAlign:"center"}}>
                          <span style={{background:cfg.bg, color:cfg.color, borderRadius:20, fontSize:14, fontWeight:700, padding:"2px 10px"}}>{row.jumlah_data} data</span>
                        </td>
                        <td style={{...styles.td, color:"#374151"}}>{row.uploaded_by_name}</td>
                        <td style={{...styles.td, color:"#6b7280", whiteSpace:"nowrap"}}>{new Date(row.uploaded_at).toLocaleString("id-ID")}</td>
                        <td style={{...styles.td, textAlign:"center"}}>
                          <button onClick={() => handleDeleteLog(row.id)}
                            style={{background:"none", border:"none", cursor:"pointer", fontSize:16, color:"#ef4444", padding:"2px 6px", borderRadius:6, lineHeight:1}}
                            title="Hapus log">🗑️</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}


// =====================
// QRIS PAGE
// =====================
function getBatch(jam) {
  if (!jam) return '-';
  const [h, m] = jam.split(':').map(Number);
  const t = h * 60 + (m || 0);
  if (t >= 4*60  && t <  8*60)       return 1; // 04:00–07:59
  if (t >= 8*60  && t <= 11*60+40)   return 2; // 08:00–11:40
  if (t >= 11*60+41 && t < 14*60)    return 3; // 11:41–13:59
  if (t >= 14*60 && t < 19*60)       return 4; // 14:00–18:59
  return 5;                                      // 19:00–03:59
}

function QrisPage() {
  const CABANG_LIST = [
    { code: "PO", nama: "Puri Orchard" },
    { code: "PM", nama: "Palm Mansion" },
    { code: "MT", nama: "Metro" },
    { code: "DM", nama: "Daan Mogot City" },
    { code: "TM", nama: "Tomang" },
  ];
  const [cabang, setCabang] = useState("PO");
  const [bulan, setBulan] = useState(new Date().getMonth() + 1);
  const [tahun, setTahun] = useState(new Date().getFullYear());
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalNominal, setTotalNominal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  async function handleExport() {
    setExporting(true);
    const bn = BULAN_NAMA[bulan];
    await downloadExcel('/export/qris', {cabang, bulan, tahun}, `QRIS_${cabang}_${bn}_${tahun}.xlsx`);
    setExporting(false);
  }

  const loadData = async (p = 1) => {
    setLoading(true);
    try {
      const res = await api.get("/detail/qris", { params: { cabang, bulan, tahun, page: p, per_page: 100 } });
      setData(res.data.data);
      setTotal(res.data.total);
      setTotalNominal(res.data.total_nominal);
      setTotalPages(res.data.total_pages);
      setPage(p);
    } catch (err) {
      alert("Error: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(1); }, [cabang, bulan, tahun]);

  const cabangNama = CABANG_LIST.find(c => c.code === cabang)?.nama || "";

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20, flexWrap:"wrap", gap:12}}>
        <div>
          <h2 style={{...styles.pageTitle, marginBottom:2}}>Laporan QRIS</h2>
          <span style={{fontSize:14, color:"#6b7280"}}>{cabang} — {cabangNama} · {BULAN_NAMA[bulan]} {tahun}</span>
        </div>
        <div style={{display:"flex", gap:8, alignItems:"flex-end", flexWrap:"wrap"}}>
          <div>
            <label style={{fontSize:14, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3, textTransform:"uppercase"}}>Cabang</label>
            <select style={{...styles.select, margin:0}} value={cabang} onChange={e => setCabang(e.target.value)}>
              {CABANG_LIST.map(c => <option key={c.code} value={c.code}>{c.code} - {c.nama}</option>)}
            </select>
          </div>
          <div>
            <label style={{fontSize:14, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3, textTransform:"uppercase"}}>Bulan</label>
            <select style={{...styles.select, margin:0}} value={bulan} onChange={e => setBulan(Number(e.target.value))}>
              {BULAN_NAMA.slice(1).map((b, i) => <option key={i+1} value={i+1}>{b}</option>)}
            </select>
          </div>
          <div>
            <label style={{fontSize:14, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3, textTransform:"uppercase"}}>Tahun</label>
            <select style={{...styles.select, margin:0}} value={tahun} onChange={e => setTahun(Number(e.target.value))}>
              {Array.from({length:3},(_,i)=>new Date().getFullYear()-1+i).map(y=><option key={y}>{y}</option>)}
            </select>
          </div>
          <button onClick={handleExport} disabled={exporting}
            style={{background:"#7c3aed", color:"#fff", border:"none", padding:"8px 16px", borderRadius:8, cursor:"pointer", fontWeight:600, fontSize:14, whiteSpace:"nowrap"}}>
            {exporting ? "⏳ Exporting..." : "📥 Export Excel"}
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {!loading && total > 0 && (
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(160px, 1fr))", gap:12, marginBottom:20}}>
          {[
            {label:"Total Transaksi", value:total + " transaksi", color:"#7c3aed", icon:"💳"},
            {label:"Total Nominal", value:formatRp(totalNominal), color:"#059669", icon:"💰"},
          ].map(({label,value,color,icon}) => (
            <div key={label} style={{background:"#fff", borderRadius:12, padding:"14px 18px", boxShadow:"0 1px 4px rgba(0,0,0,0.07)", borderTop:`3px solid ${color}`}}>
              <div style={{fontSize:14, color:"#6b7280", fontWeight:600, textTransform:"uppercase", marginBottom:4}}>{icon} {label}</div>
              <div style={{fontSize:22, fontWeight:700, color}}>{value}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{background:"#fff", borderRadius:14, boxShadow:"0 1px 4px rgba(0,0,0,0.08)", overflow:"hidden"}}>
        {loading ? (
          <div style={{textAlign:"center", padding:60, color:"#9ca3af"}}>
            <div style={{fontSize:32, marginBottom:8}}>⏳</div>
            <div>Memuat data...</div>
          </div>
        ) : data.length === 0 ? (
          <div style={{textAlign:"center", padding:"60px 0", color:"#9ca3af"}}>
            <div style={{fontSize:40, marginBottom:12}}>📭</div>
            <div style={{fontSize:16, fontWeight:600}}>Tidak ada data QRIS</div>
          </div>
        ) : (
          <>
            <div style={{display:"flex", justifyContent:"flex-end", padding:"12px 16px", borderBottom:"1px solid #f1f5f9"}}>
              {totalPages > 1 && (
                <div style={{display:"flex", gap:4}}>
                  {Array.from({length: totalPages}, (_, i) => i + 1).map(p => (
                    <button key={p} onClick={() => loadData(p)}
                      style={{width:32, height:32, borderRadius:6, border:"1px solid #e2e8f0", cursor:"pointer", fontSize:14,
                        fontWeight:p===page?700:400, background:p===page?"#7c3aed":"#fff", color:p===page?"#fff":"#374151"}}>
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div style={{overflowX:"auto"}}>
              <table style={{...styles.table, fontSize:14}}>
                <thead>
                  <tr style={{background:"#f8fafc"}}>
                    <th style={{...styles.th, color:"#6b7280", width:48}}>No</th>
                    <th style={styles.th}>Tanggal</th>
                    <th style={{...styles.th, textAlign:"center"}}>Batch</th>
                    <th style={styles.th}>Jam</th>
                    <th style={{...styles.th, textAlign:"right"}}>Nominal</th>
                    <th style={styles.th}>Nama Pengirim</th>
                    <th style={styles.th}>Tipe</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={i} style={{background: i%2===0 ? "#fff" : "#f8fafc"}}>
                      <td style={{...styles.td, color:"#9ca3af", fontSize:14}}>{(page-1)*100 + i + 1}</td>
                      <td style={{...styles.td, whiteSpace:"nowrap"}}>{new Date(row.tanggal).toLocaleDateString("id-ID")}</td>
                      <td style={{...styles.td, textAlign:"center"}}>
                        {(() => {
                          const b = getBatch(row.jam);
                          const colors = {
                            1: {bg:"#dbeafe", color:"#1d4ed8"},
                            2: {bg:"#d1fae5", color:"#065f46"},
                            3: {bg:"#fef9c3", color:"#854d0e"},
                            4: {bg:"#ffe4e6", color:"#be123c"},
                            5: {bg:"#f3e8ff", color:"#7e22ce"},
                          };
                          const c = colors[b] || {bg:"#f1f5f9", color:"#475569"};
                          return <span style={{background:c.bg, color:c.color, borderRadius:20, fontSize:14, fontWeight:700, padding:"2px 10px"}}>B{b}</span>;
                        })()}
                      </td>
                      <td style={{...styles.td, color:"#6b7280"}}>{row.jam}</td>
                      <td style={{...styles.td, textAlign:"right", fontWeight:700, color:"#7c3aed"}}>{formatRp(row.nominal)}</td>
                      <td style={{...styles.td, fontWeight:600}}>{row.payer_name}</td>
                      <td style={styles.td}>
                        <span style={{background:"#f5f3ff", color:"#7c3aed", borderRadius:20, fontSize:14, fontWeight:600, padding:"2px 10px"}}>{row.payment_type}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// =====================
// SETTLEMENT QRIS PAGE
// =====================
function SettlementQrisPage() {
  const CABANG_LIST = [
    { code: "", nama: "Semua Cabang" },
    { code: "PO", nama: "Puri Orchard" },
    { code: "PM", nama: "Palm Mansion" },
    { code: "MT", nama: "Metro" },
    { code: "DM", nama: "Daan Mogot City" },
    { code: "TM", nama: "Tomang" },
  ];
  const [cabang, setCabang] = useState("PO");
  const [bulan, setBulan] = useState(new Date().getMonth() + 1);
  const [tahun, setTahun] = useState(new Date().getFullYear());
  const [data, setData] = useState([]);
  const [totalNominal, setTotalNominal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [ringkasan, setRingkasan] = useState([]);
  const [batchEdits, setBatchEdits] = useState({});
  const [saving, setSaving] = useState({});

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { bulan, tahun };
      if (cabang) params.cabang = cabang;
      const qrisParams = { bulan, tahun, ...(cabang ? { cabang } : {}) };
      const [res, rng] = await Promise.all([
        api.get("/detail/settlement-qris", { params }),
        api.get("/detail/qris/ringkasan-batch", { params: qrisParams }),
      ]);
      setData(res.data.data);
      setTotalNominal(res.data.total_nominal);
      setRingkasan(rng.data || []);
      setBatchEdits({});
    } catch (err) {
      alert("Error: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  async function saveBatch(id) {
    setSaving(s => ({...s, [id]: true}));
    try {
      const edit = batchEdits[id] || {};
      const row = data.find(r => r.id === id);
      const payload = {
        batch: edit.batch !== undefined ? edit.batch : (row?.batch || ""),
        tgl_qris: edit.tgl_qris !== undefined ? edit.tgl_qris : (row?.tgl_qris || ""),
      };
      await api.put(`/detail/settlement-qris/${id}/batch`, payload);
      setData(prev => prev.map(r => r.id === id ? {...r, ...payload} : r));
      setBatchEdits(e => { const n = {...e}; delete n[id]; return n; });
    } catch(err) {
      alert("Gagal simpan: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(s => ({...s, [id]: false}));
    }
  }

  useEffect(() => { loadData(); }, [cabang, bulan, tahun]);

  const CABANG_COLOR = { PO:"#7c3aed", PM:"#0369a1", MT:"#059669", DM:"#d97706", TM:"#be123c" };

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20, flexWrap:"wrap", gap:12}}>
        <div>
          <h2 style={{...styles.pageTitle, marginBottom:2}}>Settlement QRIS</h2>
          <span style={{fontSize:14, color:"#6b7280"}}>
            {cabang || "Semua Cabang"} · {BULAN_NAMA[bulan]} {tahun}
          </span>
        </div>
        <div style={{display:"flex", gap:8, alignItems:"flex-end", flexWrap:"wrap"}}>
          <div>
            <label style={{fontSize:14, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3, textTransform:"uppercase"}}>Cabang</label>
            <select style={{...styles.select, margin:0}} value={cabang} onChange={e => { setCabang(e.target.value); }}>
              {CABANG_LIST.map(c => <option key={c.code} value={c.code}>{c.code ? `${c.code} - ${c.nama}` : c.nama}</option>)}
            </select>
          </div>
          <div>
            <label style={{fontSize:14, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3, textTransform:"uppercase"}}>Bulan</label>
            <select style={{...styles.select, margin:0}} value={bulan} onChange={e => setBulan(Number(e.target.value))}>
              {BULAN_NAMA.slice(1).map((b, i) => <option key={i+1} value={i+1}>{b}</option>)}
            </select>
          </div>
          <div>
            <label style={{fontSize:14, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3, textTransform:"uppercase"}}>Tahun</label>
            <select style={{...styles.select, margin:0}} value={tahun} onChange={e => setTahun(Number(e.target.value))}>
              {Array.from({length:3},(_,i)=>new Date().getFullYear()-1+i).map(y=><option key={y}>{y}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      {!loading && data.length > 0 && (
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(160px, 1fr))", gap:12, marginBottom:20}}>
          {[
            {label:"Total QRIS", value:formatRp(ringkasan.reduce((s,r)=>s+r.total,0)), color:"#7c3aed", icon:"💳"},
            {label:"Total Settlement", value:formatRp(totalNominal), color:"#059669", icon:"✅"},
          ].map(({label,value,color,icon}) => (
            <div key={label} style={{background:"#fff", borderRadius:12, padding:"14px 18px", boxShadow:"0 1px 4px rgba(0,0,0,0.07)", borderTop:`3px solid ${color}`}}>
              <div style={{fontSize:14, color:"#6b7280", fontWeight:600, textTransform:"uppercase", marginBottom:4}}>{icon} {label}</div>
              <div style={{fontSize:22, fontWeight:700, color}}>{value}</div>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{background:"#fff", borderRadius:14, boxShadow:"0 1px 4px rgba(0,0,0,0.08)", textAlign:"center", padding:60, color:"#9ca3af"}}>
          <div style={{fontSize:32, marginBottom:8}}>⏳</div>
          <div>Memuat data...</div>
        </div>
      ) : data.length === 0 ? (
        <div style={{background:"#fff", borderRadius:14, boxShadow:"0 1px 4px rgba(0,0,0,0.08)", textAlign:"center", padding:"60px 0", color:"#9ca3af"}}>
          <div style={{fontSize:40, marginBottom:12}}>📭</div>
          <div style={{fontSize:16, fontWeight:600}}>Tidak ada data settlement</div>
          <div style={{fontSize:14, marginTop:6}}>Upload mutasi rekening terlebih dahulu</div>
        </div>
      ) : (
        <>{(() => {
          const MO = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agu","Sep","Okt","Nov","Des"];
          const fmtTgl = s => { if (!s) return null; const d = new Date(s+"T00:00:00"); return `${d.getDate()} ${MO[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`; };
          const BATCH_COLOR = {
            1:{bg:"#dbeafe",color:"#1d4ed8"}, 2:{bg:"#d1fae5",color:"#065f46"},
            3:{bg:"#fef9c3",color:"#854d0e"}, 4:{bg:"#ffe4e6",color:"#be123c"},
            5:{bg:"#f3e8ff",color:"#7e22ce"},
          };
          const cardStyle = {background:"#fff", borderRadius:16, boxShadow:"0 2px 8px rgba(0,0,0,0.07)", overflow:"hidden", border:"1px solid #f1f5f9"};
          const cardHeader = (icon, title, accent, right) => (
            <div style={{padding:"0 18px", borderBottom:"1px solid #f1f5f9", borderTop:`3px solid ${accent}`, display:"flex", alignItems:"center", gap:8, height:52, flexShrink:0}}>
              <span style={{fontSize:16}}>{icon}</span>
              <span style={{fontSize:14, fontWeight:700, color:"#374151", letterSpacing:0.3, flex:1}}>{title}</span>
              {right}
            </div>
          );
          const TH = ({children, right, center}) => (
            <th style={{padding:"10px 12px", fontSize:14, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:0.5,
              textAlign: right?"right": center?"center":"left", background:"#f8fafc", borderBottom:"2px solid #e5e7eb", whiteSpace:"nowrap"}}>
              {children}
            </th>
          );
          const TD = ({children, right, center, style:s}) => (
            <td style={{padding:"10px 12px", fontSize:14, color:"#374151", textAlign: right?"right":center?"center":"left", borderBottom:"1px solid #f1f5f9", ...s}}>
              {children}
            </td>
          );

          const ROW_H = 44;

          return (
            <div style={{display:"flex", gap:16, alignItems:"flex-start"}}>

              {/* ── CARD KIRI: Ringkasan Batch ── */}
              <div style={{...cardStyle, flexShrink:0, width:300}}>
                {cardHeader("💳", "Ringkasan Batch · Laporan QRIS", "#7c3aed")}
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%", borderCollapse:"collapse"}}>
                    <thead>
                      <tr><TH>Tanggal</TH><TH center>Batch</TH><TH right>Nominal</TH></tr>
                    </thead>
                    <tbody>
                      {ringkasan.length === 0 ? (
                        <tr><td colSpan={3} style={{padding:"28px 12px", color:"#9ca3af", textAlign:"center", fontSize:14}}>Tidak ada data</td></tr>
                      ) : ringkasan.map((r, i) => {
                        const bc = BATCH_COLOR[r.batch] || {bg:"#f1f5f9", color:"#6b7280"};
                        return (
                          <tr key={i} style={{height:ROW_H, background:i%2===0?"#fff":"#fafafa"}}>
                            <td style={{padding:"0 12px", fontSize:14, color:"#374151", borderBottom:"1px solid #f1f5f9", whiteSpace:"nowrap"}}>
                              {fmtTgl(r.tanggal)}
                            </td>
                            <td style={{padding:"0 12px", textAlign:"center", borderBottom:"1px solid #f1f5f9"}}>
                              <span style={{...bc, borderRadius:20, fontSize:14, fontWeight:700, padding:"3px 11px", display:"inline-block"}}>B{r.batch}</span>
                            </td>
                            <td style={{padding:"0 12px", textAlign:"right", borderBottom:"1px solid #f1f5f9"}}>
                              <span style={{fontWeight:700, fontSize:14, color:"#059669", whiteSpace:"nowrap"}}>{formatRp(r.total)}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {ringkasan.length > 0 && (
                      <tfoot>
                        <tr style={{background:"#f8fafc", borderTop:"2px solid #e5e7eb"}}>
                          <td colSpan={2} style={{padding:"10px 12px", fontSize:14, fontWeight:700, color:"#6b7280"}}>Total</td>
                          <td style={{padding:"10px 12px", textAlign:"right", fontSize:14, fontWeight:700, color:"#059669"}}>
                            {formatRp(ringkasan.reduce((s,r)=>s+r.total,0))}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>

              {/* ── CARD KANAN: Detail Settlement ── */}
              <div style={{...cardStyle, flex:1, minWidth:0}}>
                {cardHeader("✅", "Detail Settlement · Mutasi Rekening", "#059669")}
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%", borderCollapse:"collapse"}}>
                    <thead>
                      <tr>
                        <TH>#</TH><TH>Tanggal</TH><TH>Cabang</TH><TH>MID</TH><TH right>Nominal</TH>
                        <th colSpan={2} style={{padding:"10px 12px", fontSize:14, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:0.5, textAlign:"center", background:"#f8fafc", borderBottom:"2px solid #e5e7eb", whiteSpace:"nowrap"}}>Tanggal Settlement</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((row, i) => {
                        const edit = batchEdits[row.id] || {};
                        const curBatch = edit.batch !== undefined ? edit.batch : (row.batch||"");
                        const curTgl = edit.tgl_qris !== undefined ? edit.tgl_qris : (row.tgl_qris||"");
                        const dirty = batchEdits[row.id] !== undefined;
                        const bc = BATCH_COLOR[parseInt(curBatch?.replace("B",""))] || null;
                        const setEdit = (field, val) => setBatchEdits(p => ({
                          ...p,
                          [row.id]: { batch: p[row.id]?.batch !== undefined ? p[row.id].batch : (row.batch||""),
                                      tgl_qris: p[row.id]?.tgl_qris !== undefined ? p[row.id].tgl_qris : (row.tgl_qris||""),
                                      [field]: val }
                        }));
                        return (
                          <tr key={row.id} style={{height:ROW_H, background:i%2===0?"#fff":"#fafafa"}}>
                            <td style={{padding:"0 12px", fontSize:14, color:"#9ca3af", borderBottom:"1px solid #f1f5f9"}}>{i+1}</td>
                            <td style={{padding:"0 12px", fontSize:14, color:"#374151", borderBottom:"1px solid #f1f5f9", whiteSpace:"nowrap"}}>
                              {new Date(row.tanggal).toLocaleDateString("id-ID",{day:"2-digit",month:"short",year:"numeric"})}
                            </td>
                            <td style={{padding:"0 12px", borderBottom:"1px solid #f1f5f9"}}>
                              <span style={{background:(CABANG_COLOR[row.cabang]||"#6b7280")+"18", color:CABANG_COLOR[row.cabang]||"#6b7280",
                                borderRadius:20, fontSize:14, fontWeight:700, padding:"3px 10px"}}>{row.cabang}</span>
                            </td>
                            <td style={{padding:"0 12px", borderBottom:"1px solid #f1f5f9"}}>
                              <span style={{fontFamily:"monospace", fontSize:14, color:"#6b7280"}}>{row.mid}</span>
                            </td>
                            <td style={{padding:"0 12px", textAlign:"right", borderBottom:"1px solid #f1f5f9"}}>
                              <span style={{fontWeight:700, fontSize:14, color:"#059669", whiteSpace:"nowrap"}}>{formatRp(row.jumlah)}</span>
                            </td>
                            <td style={{padding:"0 12px", textAlign:"center", borderBottom:"1px solid #f1f5f9"}}>
                              {dirty && edit.tgl_qris !== undefined ? (
                                <input type="date" value={curTgl}
                                  onChange={e => setEdit("tgl_qris", e.target.value)}
                                  autoFocus
                                  style={{padding:"3px 5px", borderRadius:7, border:"1.5px solid #f59e0b",
                                    fontSize:14, color:"#374151", background:"#fffbeb", outline:"none", cursor:"pointer"}}
                                />
                              ) : (
                                <span onClick={() => setEdit("tgl_qris", curTgl)}
                                  style={{display:"inline-block", padding:"3px 7px", borderRadius:7, border:"1px dashed #d1d5db",
                                    fontSize:14, cursor:"pointer", whiteSpace:"nowrap",
                                    color: curTgl ? "#374151" : "#9ca3af",
                                    background: curTgl ? "#f0fdf4" : "#fafafa"}}>
                                  {fmtTgl(curTgl) || "+ tanggal"}
                                </span>
                              )}
                            </td>
                            <td style={{padding:"0 12px", textAlign:"center", borderBottom:"1px solid #f1f5f9"}}>
                              <div style={{display:"flex", alignItems:"center", gap:5, justifyContent:"center"}}>
                                {bc && !dirty && <span style={{...bc, borderRadius:20, fontSize:14, fontWeight:700, padding:"3px 10px"}}>{curBatch}</span>}
                                <select value={curBatch} onChange={e => setEdit("batch", e.target.value)}
                                  style={{padding:"3px 5px", borderRadius:7,
                                    border:`1.5px solid ${dirty&&edit.batch!==undefined?"#f59e0b":bc?"#e5e7eb":"#cbd5e0"}`,
                                    fontSize:14, color:"#374151",
                                    background:dirty&&edit.batch!==undefined?"#fffbeb":"#fff",
                                    cursor:"pointer", outline:"none"}}>
                                  <option value="">—</option>
                                  {["B1","B2","B3","B4","B5"].map(b=><option key={b} value={b}>{b}</option>)}
                                </select>
                                {dirty && (
                                  <button onClick={()=>saveBatch(row.id)} disabled={saving[row.id]}
                                    style={{padding:"3px 8px", borderRadius:7, border:"none", background:"#059669", color:"#fff",
                                      fontSize:14, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap"}}>
                                    {saving[row.id]?"...":"Simpan"}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{background:"#f8fafc", borderTop:"2px solid #e5e7eb"}}>
                        <td colSpan={4} style={{padding:"10px 12px", fontSize:14, fontWeight:700, color:"#6b7280"}}>Total</td>
                        <td style={{padding:"10px 12px", textAlign:"right", fontSize:14, fontWeight:700, color:"#059669"}}>
                          {formatRp(data.reduce((s,r)=>s+r.jumlah,0))}
                        </td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

            </div>
          );
        })()}</>
      )}
    </div>
  );
}

// =====================
// TRANSFER PAGE
// =====================
function TransferPage() {
  const CABANG_LIST = [
    { code: "PO", nama: "Puri Orchard" },
    { code: "PM", nama: "Palm Mansion" },
    { code: "MT", nama: "Metro" },
    { code: "DM", nama: "Daan Mogot City" },
    { code: "TM", nama: "Tomang" },
  ];
  const [cabang, setCabang] = useState("PO");
  const [bulan, setBulan] = useState(new Date().getMonth() + 1);
  const [tahun, setTahun] = useState(new Date().getFullYear());
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalNominal, setTotalNominal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exportingTransfer, setExportingTransfer] = useState(false);

  async function handleExportTransfer() {
    setExportingTransfer(true);
    const bn = BULAN_NAMA[bulan];
    await downloadExcel('/export/transfer', {cabang, bulan, tahun}, `Transfer_${cabang}_${bn}_${tahun}.xlsx`);
    setExportingTransfer(false);
  }

  const loadData = async (p = 1) => {
    setLoading(true);
    try {
      const res = await api.get("/detail/transfer", { params: { cabang, bulan, tahun, page: p, per_page: 100 } });
      setData(res.data.data);
      setTotal(res.data.total);
      setTotalNominal(res.data.total_nominal);
      setTotalPages(res.data.total_pages);
      setPage(p);
    } catch (err) {
      alert("Error: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(1); }, [cabang, bulan, tahun]);

  const cabangNamaTransfer = CABANG_LIST.find(c => c.code === cabang)?.nama || "";

  return (
    <div style={styles.page}>
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20, flexWrap:"wrap", gap:12}}>
        <div>
          <h2 style={{...styles.pageTitle, marginBottom:2}}>Transfer Masuk</h2>
          <span style={{fontSize:14, color:"#6b7280"}}>{cabang} — {cabangNamaTransfer} · {BULAN_NAMA[bulan]} {tahun}</span>
        </div>
        <div style={{display:"flex", gap:8, alignItems:"flex-end", flexWrap:"wrap"}}>
          <div>
            <label style={{fontSize:14, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3, textTransform:"uppercase"}}>Cabang</label>
            <select style={{...styles.select, margin:0}} value={cabang} onChange={e => setCabang(e.target.value)}>
              {CABANG_LIST.map(c => <option key={c.code} value={c.code}>{c.code} - {c.nama}</option>)}
            </select>
          </div>
          <div>
            <label style={{fontSize:14, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3, textTransform:"uppercase"}}>Bulan</label>
            <select style={{...styles.select, margin:0}} value={bulan} onChange={e => setBulan(Number(e.target.value))}>
              {BULAN_NAMA.slice(1).map((b, i) => <option key={i+1} value={i+1}>{b}</option>)}
            </select>
          </div>
          <div>
            <label style={{fontSize:14, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3, textTransform:"uppercase"}}>Tahun</label>
            <select style={{...styles.select, margin:0}} value={tahun} onChange={e => setTahun(Number(e.target.value))}>
              {Array.from({length:3},(_,i)=>new Date().getFullYear()-1+i).map(y=><option key={y}>{y}</option>)}
            </select>
          </div>
          <button onClick={handleExportTransfer} disabled={exportingTransfer}
            style={{background:"#d97706", color:"#fff", border:"none", padding:"8px 16px", borderRadius:8, cursor:"pointer", fontWeight:600, fontSize:14, whiteSpace:"nowrap"}}>
            {exportingTransfer ? "⏳ Exporting..." : "📥 Export Excel"}
          </button>
        </div>
      </div>

      {!loading && total > 0 && (
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(160px, 1fr))", gap:12, marginBottom:20}}>
          {[
            {label:"Total Transaksi", value:total + " transaksi", color:"#d97706", icon:"🏦"},
            {label:"Total Nominal", value:formatRp(totalNominal), color:"#059669", icon:"💰"},
          ].map(({label,value,color,icon}) => (
            <div key={label} style={{background:"#fff", borderRadius:12, padding:"14px 18px", boxShadow:"0 1px 4px rgba(0,0,0,0.07)", borderTop:`3px solid ${color}`}}>
              <div style={{fontSize:14, color:"#6b7280", fontWeight:600, textTransform:"uppercase", marginBottom:4}}>{icon} {label}</div>
              <div style={{fontSize:22, fontWeight:700, color}}>{value}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{background:"#fff", borderRadius:14, boxShadow:"0 1px 4px rgba(0,0,0,0.08)", overflow:"hidden"}}>
        {loading ? (
          <div style={{textAlign:"center", padding:60, color:"#9ca3af"}}>
            <div style={{fontSize:32, marginBottom:8}}>⏳</div><div>Memuat data...</div>
          </div>
        ) : data.length === 0 ? (
          <div style={{textAlign:"center", padding:"60px 0", color:"#9ca3af"}}>
            <div style={{fontSize:40, marginBottom:12}}>📭</div>
            <div style={{fontSize:16, fontWeight:600}}>Tidak ada data transfer</div>
          </div>
        ) : (
          <>
            {totalPages > 1 && (
              <div style={{display:"flex", justifyContent:"flex-end", padding:"12px 16px", borderBottom:"1px solid #f1f5f9"}}>
                <div style={{display:"flex", gap:4}}>
                  {Array.from({length: totalPages}, (_, i) => i + 1).map(p => (
                    <button key={p} onClick={() => loadData(p)}
                      style={{width:32, height:32, borderRadius:6, border:"1px solid #e2e8f0", cursor:"pointer", fontSize:14,
                        fontWeight:p===page?700:400, background:p===page?"#d97706":"#fff", color:p===page?"#fff":"#374151"}}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div style={{overflowX:"auto"}}>
              <table style={{...styles.table, fontSize:14}}>
                <thead>
                  <tr style={{background:"#f8fafc"}}>
                    <th style={{...styles.th, width:48, color:"#6b7280"}}>No</th>
                    <th style={styles.th}>Tanggal</th>
                    <th style={styles.th}>Keterangan</th>
                    <th style={{...styles.th, textAlign:"right"}}>Nominal</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={i} style={{background: i%2===0 ? "#fff" : "#f8fafc"}}>
                      <td style={{...styles.td, color:"#9ca3af", fontSize:14}}>{(page-1)*100 + i + 1}</td>
                      <td style={{...styles.td, whiteSpace:"nowrap"}}>{new Date(row.tanggal).toLocaleDateString("id-ID")}</td>
                      <td style={{...styles.td, fontWeight:600}}>{row.keterangan}</td>
                      <td style={{...styles.td, textAlign:"right", fontWeight:700, color:"#d97706"}}>{formatRp(row.jumlah)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// =====================
// MAIN APP
// =====================
// =====================
// HELPER EXPORT EXCEL
// =====================
async function downloadExcel(url, params, filename) {
  const token = localStorage.getItem('token');
  const qs = new URLSearchParams(params).toString();
  const res = await fetch(`/api${url}?${qs}`, {
    headers: { 'Authorization': 'Bearer ' + token }
  });
  if (!res.ok) { alert('Gagal export: ' + res.status); return; }
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
}

// =====================
// PELUNASAN PAGE
// =====================
function PelunasanPage() {
  const CABANG_LIST = ["PO", "PM", "MT", "DM", "TM"];
  const CABANG_NAMA = {PO:"PO", PM:"Palm", MT:"Metro", DM:"Damocit", TM:"Tomang"};
  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];
  const [cabang, setCabang] = useState("PO");
  const [tanggal, setTanggal] = useState(todayStr);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalNominal, setTotalNominal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [qrisData, setQrisData] = useState([]);
  const [qrisTotalNominal, setQrisTotalNominal] = useState(0);
  const [transferData, setTransferData] = useState([]);
  const [transferTotalNominal, setTransferTotalNominal] = useState(0);
  const firstOfMonth = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-01`;
  const today = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
  const [expCabang, setExpCabang] = useState("PO");
  const [expDari, setExpDari] = useState(firstOfMonth);
  const [expSampai, setExpSampai] = useState(today);
  const [expLoading, setExpLoading] = useState(false);

  const loadData = (p=1) => {
    setLoading(true);
    const params = { cabang, tgl_dari: tanggal, tgl_sampai: tanggal };
    Promise.all([
      api.get("/pelunasan/list", { params: { ...params, page: p, per_page: 100 } }),
      api.get("/qris/list-range", { params }),
      api.get("/transfer/list-range", { params }),
    ]).then(([pel, qris, trx]) => {
      setData(pel.data.data || []);
      setTotal(pel.data.total || 0);
      setTotalNominal(pel.data.total_nominal || 0);
      setTotalPages(pel.data.total_pages || 1);
      setPage(p);
      setQrisData(qris.data.data || []);
      setQrisTotalNominal(qris.data.total_nominal || 0);
      setTransferData(trx.data.data || []);
      setTransferTotalNominal(trx.data.total_nominal || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { loadData(1); }, [cabang, tanggal]);

  function formatTgl(d) {
    if (!d) return "—";
    const dt = new Date(d+"T00:00:00");
    return dt.getDate() + " " + BULAN_NAMA[dt.getMonth()+1].slice(0,3);
  }

  const th = {padding:"10px 12px", textAlign:"left", fontWeight:600, fontSize:14, whiteSpace:"nowrap"};
  const td = {padding:"8px 12px", borderBottom:"1px solid #e2e8f0", fontSize:14};

  const PaginationBar = () => {
    const start = Math.max(1,page-2), end = Math.min(totalPages,page+2);
    const pages = []; for(let i=start;i<=end;i++) pages.push(i);
    const btn = (active) => ({padding:"5px 10px",border:"1px solid #e2e8f0",borderRadius:5,background:active?"#2b6cb0":"#fff",color:active?"#fff":"#4a5568",cursor:"pointer",fontSize:14});
    return (
      <div style={{display:"flex",gap:3}}>
        <button disabled={page===1} onClick={()=>loadData(1)} style={btn(false)}>«</button>
        <button disabled={page===1} onClick={()=>loadData(page-1)} style={btn(false)}>‹</button>
        {pages.map(p=><button key={p} onClick={()=>loadData(p)} style={btn(p===page)}>{p}</button>)}
        <button disabled={page===totalPages} onClick={()=>loadData(page+1)} style={btn(false)}>›</button>
        <button disabled={page===totalPages} onClick={()=>loadData(totalPages)} style={btn(false)}>»</button>
      </div>
    );
  };

  return (
    <div style={styles.page}>
      {/* Header: judul kiri, export filter kanan */}
      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16, flexWrap:"wrap", gap:12}}>
        <h2 style={{...styles.pageTitle, margin:0}}>💵 Data Pelunasan</h2>
        <div style={{display:"flex", gap:10, alignItems:"flex-end", flexWrap:"wrap"}}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Cabang</label>
            <select style={styles.select} value={expCabang} onChange={e=>setExpCabang(e.target.value)}>
              {CABANG_LIST.map(c=><option key={c} value={c}>{CABANG_NAMA[c]}</option>)}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Dari</label>
            <input type="date" value={expDari} onChange={e=>{
              setExpDari(e.target.value);
              if(e.target.value > expSampai) setExpSampai(e.target.value);
            }} style={{...styles.select, width:"auto"}}/>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Sampai</label>
            <input type="date" value={expSampai} min={expDari} onChange={e=>setExpSampai(e.target.value)}
              style={{...styles.select, width:"auto"}}/>
          </div>
          <button onClick={async()=>{
              setExpLoading(true);
              await downloadExcel('/export/rekap-cabang', {cabang: expCabang, tgl_dari: expDari, tgl_sampai: expSampai},
                `Rekap_${expCabang}_${expDari}_${expSampai}.xlsx`);
              setExpLoading(false);
            }} disabled={expLoading} style={{...styles.btnPrimary}}>
            {expLoading ? "Exporting..." : "📊 Export Rekap"}
          </button>
        </div>
      </div>

      {/* Filter tampilan */}
      <div style={{...styles.card, display:"flex", gap:16, alignItems:"center", flexWrap:"wrap"}}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Cabang</label>
          <select style={styles.select} value={cabang} onChange={e=>setCabang(e.target.value)}>
            {CABANG_LIST.map(c=><option key={c} value={c}>{CABANG_NAMA[c]}</option>)}
          </select>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Tanggal</label>
          <input type="date" value={tanggal} onChange={e=>setTanggal(e.target.value)}
            style={{...styles.select, width:"auto"}}/>
        </div>
        <div style={{display:"flex", gap:0, borderLeft:"1px solid #e2e8f0", paddingLeft:16}}>
          <div style={{paddingRight:16, borderRight:"1px solid #e2e8f0"}}>
            <div style={{fontSize:14, color:"#718096", fontWeight:600, textTransform:"uppercase"}}>Pelunasan</div>
            <div style={{fontSize:16, fontWeight:700, color:"#2563eb"}}>{formatRp(totalNominal)}</div>
            <div style={{fontSize:14, color:"#a0aec0"}}>{total} transaksi</div>
          </div>
          <div style={{paddingLeft:16}}>
            <div style={{fontSize:14, color:"#718096", fontWeight:600, textTransform:"uppercase"}}>QRIS + Transfer</div>
            <div style={{fontSize:16, fontWeight:700, color:"#276749"}}>{formatRp(qrisTotalNominal + transferTotalNominal)}</div>
            <div style={{fontSize:14, color:"#a0aec0"}}>{qrisData.length + transferData.length} transaksi</div>
          </div>
        </div>
      </div>

      <div style={{display:"flex", gap:16, alignItems:"flex-start"}}>

        {/* Pelunasan */}
        <div style={{...styles.card, flex:2, minWidth:0}}>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12, flexWrap:"wrap", gap:8}}>
            <div>
              <div style={{fontWeight:700, fontSize:14, color:"#2b6cb0"}}>💵 Data Pelunasan</div>
              <div style={{fontSize:14, color:"#2563eb", fontWeight:600, marginTop:2}}>{formatRp(totalNominal)}</div>
              <div style={{fontSize:14, color:"#718096"}}>{total} transaksi</div>
            </div>
            {totalPages > 1 && <PaginationBar />}
          </div>
          {loading ? <div style={{textAlign:"center",padding:40,color:"#a0aec0"}}>Memuat data...</div> : (
            <>
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%", borderCollapse:"collapse", fontSize:14}}>
                  <thead>
                    <tr style={{background:"#f8fafc"}}>
                      {[{l:"No",w:28},{l:"Tanggal",w:60},{l:"No Nota",w:90},{l:"Nama Customer",w:"auto"},{l:"Nominal",w:130}].map(h=><th key={h.l} style={{...th,width:h.w}}>{h.l}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {data.length===0 ? (
                      <tr><td colSpan={5} style={{textAlign:"center",padding:40,color:"#a0aec0"}}>Tidak ada data</td></tr>
                    ) : data.map((r,i)=>(
                      <tr key={r.id} style={{background:i%2===0?"#fff":"#f7fafc", borderBottom:"1px solid #e2e8f0"}}>
                        <td style={{...td,color:"#a0aec0"}}>{(page-1)*100+i+1}</td>
                        <td style={{...td,whiteSpace:"nowrap"}}>{formatTgl(r.tanggal)}</td>
                        <td style={{...td,fontSize:14,color:"#4a5568"}}>{r.ref_id ? r.ref_id.slice(-8) : "—"}</td>
                        <td style={{...td,fontWeight:600}}>{r.customer || "—"}</td>
                        <td style={{...td,fontWeight:600,color:"#2563eb",whiteSpace:"nowrap"}}>{formatRp(r.tunai)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && <div style={{marginTop:10,display:"flex",justifyContent:"flex-end"}}><PaginationBar /></div>}
            </>
          )}
        </div>

        {/* QRIS */}
        <div style={{...styles.card, flex:1, minWidth:0}}>
          <div style={{marginBottom:12}}>
            <div style={{fontWeight:700, fontSize:14, color:"#276749"}}>💳 QRIS</div>
            <div style={{fontSize:14, color:"#276749", fontWeight:600, marginTop:2}}>{formatRp(qrisTotalNominal)}</div>
            <div style={{fontSize:14, color:"#718096"}}>{qrisData.length} transaksi</div>
          </div>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%", borderCollapse:"collapse", fontSize:14}}>
              <thead>
                <tr style={{background:"#f8fafc"}}>
                  <th style={{...th,width:32}}>No</th>
                  <th style={{...th,width:70}}>Tanggal</th>
                  <th style={{...th}}>Nominal</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={3} style={{textAlign:"center",padding:24,color:"#a0aec0"}}>Memuat...</td></tr>
                ) : qrisData.length === 0 ? (
                  <tr><td colSpan={3} style={{textAlign:"center",padding:24,color:"#a0aec0"}}>Tidak ada data</td></tr>
                ) : qrisData.map((r,i)=>(
                  <tr key={i} style={{background:i%2===0?"#fff":"#f7fafc"}}>
                    <td style={{...td,color:"#a0aec0"}}>{i+1}</td>
                    <td style={{...td,whiteSpace:"nowrap"}}>{formatTgl(r.tanggal)}</td>
                    <td style={{...td,color:"#276749",fontWeight:600}}>{formatRp(r.nominal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Transfer */}
        <div style={{...styles.card, flex:1, minWidth:0}}>
          <div style={{marginBottom:12}}>
            <div style={{fontWeight:700, fontSize:14, color:"#744210"}}>🔄 Transfer</div>
            <div style={{fontSize:14, color:"#744210", fontWeight:600, marginTop:2}}>{formatRp(transferTotalNominal)}</div>
            <div style={{fontSize:14, color:"#718096"}}>{transferData.length} transaksi</div>
          </div>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%", borderCollapse:"collapse", fontSize:14}}>
              <thead>
                <tr style={{background:"#f8fafc"}}>
                  <th style={{...th,width:32}}>No</th>
                  <th style={{...th,width:70}}>Tanggal</th>
                  <th style={{...th}}>Nominal</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={3} style={{textAlign:"center",padding:24,color:"#a0aec0"}}>Memuat...</td></tr>
                ) : transferData.length === 0 ? (
                  <tr><td colSpan={3} style={{textAlign:"center",padding:24,color:"#a0aec0"}}>Tidak ada data</td></tr>
                ) : transferData.map((r,i)=>(
                  <tr key={i} style={{background:i%2===0?"#fff":"#f7fafc"}}>
                    <td style={{...td,color:"#a0aec0"}}>{i+1}</td>
                    <td style={{...td,whiteSpace:"nowrap"}}>{formatTgl(r.tanggal)}</td>
                    <td style={{...td,color:"#744210",fontWeight:600}}>{formatRp(r.nominal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}

// =====================
// ABSENSI PAGE
// =====================
const JAM_KERJA_STANDAR = { PO: 540, PM: 600, MT: 540, DM: 540, TM: 540 }; // menit

function AbsensiPage() {
  const CABANG_LIST = ["PO", "PM", "MT", "DM", "TM"];
  const now = new Date();
  const [cabang, setCabang] = useState("DM");
  const [bulan, setBulan] = useState(now.getMonth() + 1);
  const [tahun, setTahun] = useState(now.getFullYear());
  const [selectedNama, setSelectedNama] = useState("semua");
  const [namaList, setNamaList] = useState([]);
  const [data, setData] = useState({});        // { "nama": [ {tgl, ...}, ... ] }
  const [rekap, setRekap] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("detail");
  const [editCell, setEditCell] = useState(null); // {id, tgl, field}
  const [editVal, setEditVal] = useState("");
  const [savingId, setSavingId] = useState(null);

  const jamStandar = JAM_KERJA_STANDAR[cabang] || 540;

  // Fetch daftar nama
  useEffect(() => {
    api.get("/absensi/rekap", { params: { cabang, bulan, tahun } })
      .then(res => {
        const names = (res.data.rekap || []).map(r => r.nama_karyawan);
        setNamaList(names);
        setSelectedNama(names.length > 0 ? names[0] : "semua");
      })
      .catch(() => setNamaList([]));
  }, [cabang, bulan, tahun]);

  // Fetch data detail
  useEffect(() => {
    if (activeTab !== "detail") return;
    setLoading(true);
    const nama = selectedNama === "semua" ? undefined : selectedNama;
    api.get("/absensi/laporan", { params: { cabang, bulan, tahun, per_page: 1000, nama } })
      .then(res => {
        // Group by nama_karyawan
        const grouped = {};
        (res.data.data || []).forEach(r => {
          if (!grouped[r.nama_karyawan]) grouped[r.nama_karyawan] = {};
          grouped[r.nama_karyawan][r.tanggal] = r;
        });
        setData(grouped);
      })
      .catch(() => setData({}))
      .finally(() => setLoading(false));
  }, [cabang, bulan, tahun, selectedNama, activeTab]);

  // Fetch rekap
  useEffect(() => {
    if (activeTab !== "rekap") return;
    setLoading(true);
    api.get("/absensi/rekap", { params: { cabang, bulan, tahun } })
      .then(res => setRekap(res.data.rekap || []))
      .catch(() => setRekap([]))
      .finally(() => setLoading(false));
  }, [cabang, bulan, tahun, activeTab]);

  // Generate semua tanggal dalam bulan
  function getTanggalBulan() {
    const days = new Date(tahun, bulan, 0).getDate();
    const result = [];
    for (let d = 1; d <= days; d++) {
      const tgl = `${tahun}-${String(bulan).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
      result.push(tgl);
    }
    return result;
  }

  function formatTglDisplay(dateStr) {
    const d = new Date(dateStr + "T00:00:00");
    const days = ["Minggu","Senin","Selasa","Rabu","Kamis","Jumat","Sabtu"];
    const bln = BULAN_NAMA[d.getMonth()+1];
    return { hari: days[d.getDay()], tanggal: `${d.getDate()} ${bln} ${d.getFullYear()}` };
  }

  function formatJam(t) { return t ? t.slice(0,5) : ""; }

  function hitungOvertime(durasi_menit) {
    if (durasi_menit == null) return null;
    return durasi_menit - jamStandar;
  }

  function formatOvertime(ot) {
    if (ot == null) return "—";
    const abs = Math.abs(ot);
    const j = Math.floor(abs / 60), m = abs % 60;
    const str = (ot < 0 ? "-" : "+") + j + "j " + m + "m";
    const color = ot < 0 ? "#e53e3e" : ot > 0 ? "#276749" : "#718096";
    return <span style={{color, fontWeight:600}}>{str}</span>;
  }

  function getKeterangan(ot) {
    if (ot == null || ot <= 0) return "";
    if (ot >= 230) return <span style={{background:"#fed7d7",color:"#9b2c2c",padding:"2px 8px",borderRadius:20,fontSize:14,fontWeight:700}}>Longshift</span>;
    if (ot >= 110) return <span style={{background:"#fefcbf",color:"#744210",padding:"2px 8px",borderRadius:20,fontSize:14,fontWeight:700}}>Lembur</span>;
    return "";
  }

  // Inline edit handlers
  function startEdit(id, tgl, field, currentVal) {
    setEditCell({id, tgl, field});
    setEditVal(currentVal || "");
  }

  async function saveEdit() {
    if (!editCell) return;
    setSavingId(editCell.id + editCell.field);
    try {
      const payload = { [editCell.field]: editVal || null };
      // Jika id ada, update existing
      if (editCell.id) {
        const res = await api.put("/absensi/" + editCell.id, payload);
        // Update local state
        setData(prev => {
          const next = {...prev};
          Object.keys(next).forEach(nama => {
            if (next[nama][editCell.tgl] && next[nama][editCell.tgl].id === editCell.id) {
              next[nama][editCell.tgl] = {
                ...next[nama][editCell.tgl],
                absen_masuk: res.data.absen_masuk,
                absen_pulang: res.data.absen_pulang,
                durasi_menit: res.data.durasi_menit,
              };
            }
          });
          return next;
        });
      } else {
        // Tambah baris baru (tanggal kosong)
        const res = await api.post("/absensi/tambah", {
          cabang, nama_karyawan: editCell.nama,
          tanggal: editCell.tgl,
          [editCell.field]: editVal || null,
        });
        setData(prev => {
          const next = {...prev};
          if (!next[editCell.nama]) next[editCell.nama] = {};
          next[editCell.nama][editCell.tgl] = {
            id: res.data.id, cabang,
            nama_karyawan: editCell.nama,
            tanggal: editCell.tgl,
            absen_masuk: res.data.absen_masuk,
            absen_pulang: res.data.absen_pulang,
            durasi_menit: res.data.durasi_menit,
          };
          return next;
        });
      }
    } catch(e) {
      alert("Gagal simpan: " + (e.response?.data?.detail || e.message));
    } finally {
      setSavingId(null);
      setEditCell(null);
      setEditVal("");
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter") saveEdit();
    if (e.key === "Escape") { setEditCell(null); setEditVal(""); }
  }

  const tanggalList = getTanggalBulan();
  const namaFilter = selectedNama && selectedNama !== "semua" ? [selectedNama] : Object.keys(data);

  const th = {padding:"9px 10px", textAlign:"left", fontWeight:600, fontSize:14, whiteSpace:"nowrap"};
  const td = {padding:"7px 10px", borderBottom:"1px solid #e2e8f0", fontSize:14};

  function EditableCell({id, tgl, field, value, nama}) {
    const isEditing = editCell && editCell.id === id && editCell.tgl === tgl && editCell.field === field && (id || editCell.nama === nama);
    if (isEditing) {
      return (
        <input
          autoFocus
          value={editVal}
          onChange={e => setEditVal(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={saveEdit}
          placeholder="HH:MM"
          style={{width:60, padding:"2px 4px", border:"1px solid #3182ce", borderRadius:4, fontSize:14, fontFamily:"monospace"}}
        />
      );
    }
    return (
      <span
        onClick={() => startEdit(id, tgl, field, value)}
        style={{
          cursor:"pointer", fontFamily:"monospace", fontSize:14,
          color: value ? "#2d3748" : "#cbd5e0",
          borderBottom: "1px dashed #a0aec0", paddingBottom:1,
          minWidth:40, display:"inline-block"
        }}
        title="Klik untuk edit"
      >
        {value ? value.slice(0,5) : "—"}
      </span>
    );
  }

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20, flexWrap:"wrap", gap:12}}>
        <div>
          <h2 style={{...styles.pageTitle, marginBottom:2}}>Laporan Absensi</h2>
          <span style={{fontSize:14, color:"#6b7280"}}>{cabang} · {BULAN_NAMA[bulan]} {tahun}</span>
        </div>
        <div style={{display:"flex", gap:8, alignItems:"flex-end", flexWrap:"wrap"}}>
          <div>
            <label style={{fontSize:14, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3, textTransform:"uppercase"}}>Cabang</label>
            <select style={{...styles.select, margin:0}} value={cabang} onChange={e=>setCabang(e.target.value)}>
              {CABANG_LIST.map(c=><option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={{fontSize:14, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3, textTransform:"uppercase"}}>Bulan</label>
            <select style={{...styles.select, margin:0}} value={bulan} onChange={e=>setBulan(Number(e.target.value))}>
              {BULAN_NAMA.slice(1).map((b,i)=><option key={i+1} value={i+1}>{b}</option>)}
            </select>
          </div>
          <div>
            <label style={{fontSize:14, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3, textTransform:"uppercase"}}>Tahun</label>
            <select style={{...styles.select, margin:0}} value={tahun} onChange={e=>setTahun(Number(e.target.value))}>
              {Array.from({length:3},(_,i)=>new Date().getFullYear()-1+i).map(y=><option key={y}>{y}</option>)}
            </select>
          </div>
          {namaList.length > 0 && (
            <div>
              <label style={{fontSize:14, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3, textTransform:"uppercase"}}>Karyawan</label>
              <select style={{...styles.select, margin:0, minWidth:160}} value={selectedNama} onChange={e=>setSelectedNama(e.target.value)}>
                {namaList.map(n=><option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Tab nav */}
      <div style={{background:"#fff", borderRadius:14, boxShadow:"0 1px 4px rgba(0,0,0,0.08)", overflow:"hidden", marginBottom:16}}>
        <div style={{display:"flex", borderBottom:"2px solid #f1f5f9", padding:"0 20px"}}>
          {[["detail","📋 Detail Harian"],["rekap","📊 Rekap Per Karyawan"]].map(([id,lbl])=>(
            <button key={id} onClick={()=>setActiveTab(id)}
              style={{padding:"14px 18px", fontSize:14, fontWeight:activeTab===id?700:500, background:"none", border:"none", cursor:"pointer",
                color:activeTab===id?"#2563eb":"#6b7280", borderBottom:activeTab===id?"2px solid #2563eb":"2px solid transparent",
                marginBottom:"-2px", transition:"all 0.15s"}}>
              {lbl}
            </button>
          ))}
        </div>
      </div>

      {/* TAB DETAIL */}
      {activeTab==="detail" && (
        loading ? <div style={{background:"#fff",borderRadius:14,padding:60,textAlign:"center",color:"#9ca3af"}}><div style={{fontSize:32,marginBottom:8}}>⏳</div>Memuat data...</div> :
        namaFilter.length === 0 ? <div style={{background:"#fff",borderRadius:14,padding:"60px 0",textAlign:"center",color:"#9ca3af"}}><div style={{fontSize:40,marginBottom:12}}>📭</div><div style={{fontSize:14}}>Tidak ada data — upload file absensi terlebih dahulu.</div></div> :
        namaFilter.map(nama => (
          <div key={nama} style={{background:"#fff", borderRadius:14, boxShadow:"0 1px 4px rgba(0,0,0,0.08)", marginBottom:16, overflow:"hidden"}}>
            {/* Header nama */}
            <div style={{background:"linear-gradient(135deg, #2563eb, #1d4ed8)", color:"#fff", padding:"12px 20px", fontWeight:700, fontSize:14, display:"flex", alignItems:"center", gap:8}}>
              <span style={{background:"rgba(255,255,255,0.2)", borderRadius:"50%", width:28, height:28, display:"inline-flex", alignItems:"center", justifyContent:"center", fontSize:14}}>👤</span>
              {nama}
            </div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%", borderCollapse:"collapse", fontSize:14}}>
                <thead>
                  <tr style={{background:"#ebf8ff"}}>
                    <th style={{...th, width:80}}>Hari</th>
                    <th style={{...th, width:130}}>Tanggal</th>
                    <th style={{...th, width:90}}>Jam Masuk</th>
                    <th style={{...th, width:90}}>Jam Pulang</th>
                    <th style={{...th, width:80}}>Durasi</th>
                    <th style={{...th, width:90}}>Overtime</th>
                    <th style={{...th, width:90}}>Keterangan</th>
                  </tr>
                </thead>
                <tbody>
                  {tanggalList.map(tgl => {
                    const row = data[nama] && data[nama][tgl];
                    const {hari, tanggal} = formatTglDisplay(tgl);
                    const durasi = row ? row.durasi_menit : null;
                    const ot = hitungOvertime(durasi);
                    const isSunday = new Date(tgl+"T00:00:00").getDay() === 0;
                    const bgColor = isSunday ? "#fff5f5" : row ? "#fff" : "#f7fafc";

                    const today = new Date(); today.setHours(0,0,0,0);
                    const tglDate = new Date(tgl+"T00:00:00");
                    const isPast = tglDate <= today;
                    const isOff = isPast && !row;

                    return (
                      <tr key={tgl} style={{background: isOff ? "#f0f0f0" : bgColor, borderBottom:"1px solid #e2e8f0"}}>
                        <td style={{...td, color: isSunday?"#e53e3e":"#4a5568", fontWeight: isSunday?600:400}}>
                          {hari}
                        </td>
                        <td style={{...td, fontWeight:500, color:"#2d3748"}}>
                          {tanggal}
                        </td>
                        <td style={td}>
                          {isOff || !row ? <span style={{color:"#a0aec0"}}>—</span> :
                          <EditableCell
                            id={row.id}
                            tgl={tgl} field="absen_masuk"
                            value={row.absen_masuk}
                            nama={nama}
                          />}
                        </td>
                        <td style={td}>
                          {isOff || !row ? <span style={{color:"#a0aec0"}}>—</span> :
                          <EditableCell
                            id={row.id}
                            tgl={tgl} field="absen_pulang"
                            value={row.absen_pulang}
                            nama={nama}
                          />}
                        </td>
                        <td style={{...td, fontFamily:"monospace"}}>
                          {isOff ? <span style={{color:"#a0aec0"}}>—</span> :
                           durasi != null ? Math.floor(durasi/60)+"j "+durasi%60+"m" : "—"}
                        </td>
                        <td style={td}>
                          {isOff ? <span style={{color:"#a0aec0"}}>—</span> : formatOvertime(ot)}
                        </td>
                        <td style={td}>
                          {isOff
                            ? <span style={{background:"#e2e8f0",color:"#718096",padding:"2px 8px",borderRadius:20,fontSize:14,fontWeight:700}}>Off</span>
                            : getKeterangan(ot)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      {/* TAB REKAP */}
      {activeTab==="rekap" && (
        <>
          <div style={{display:"flex",gap:12,marginBottom:16,flexWrap:"wrap"}}>
            {[
              ["Total Karyawan", rekap.length, "#3182ce"],
              ["Total Hari Hadir", rekap.reduce((s,r)=>s+r.total_hadir,0), "#38a169"],
              ["Jam Standar/Hari", Math.floor(jamStandar/60)+"j", "#805ad5"],
            ].map(([lbl,val,clr])=>(
              <div key={lbl} style={{background:"#fff",borderRadius:10,padding:"14px 20px",boxShadow:"0 1px 4px rgba(0,0,0,0.08)",minWidth:140,borderTop:"3px solid "+clr}}>
                <div style={{fontSize:14,color:"#718096",marginBottom:4,textTransform:"uppercase",fontWeight:600}}>{lbl}</div>
                <div style={{fontSize:24,fontWeight:700,color:clr}}>{val}</div>
              </div>
            ))}
          </div>
          <div style={{overflowX:"auto",borderRadius:10,boxShadow:"0 1px 4px rgba(0,0,0,0.08)"}}>
            <table style={{width:"100%",borderCollapse:"collapse",background:"#fff",fontSize:14}}>
              <thead>
                <tr style={{background:"#f8fafc"}}>
                  {["No","Nama Karyawan","Total Hadir","Tanpa Pulang","Total Durasi","Rata-rata/Hari","Total Overtime"].map(h=>(
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} style={{textAlign:"center",padding:40,color:"#a0aec0"}}>Memuat...</td></tr>
                ) : rekap.length===0 ? (
                  <tr><td colSpan={7} style={{textAlign:"center",padding:40,color:"#a0aec0"}}>Tidak ada data.</td></tr>
                ) : rekap.map((r,i)=>{
                  const rj=Math.floor(r.rata_durasi_menit/60), rm=r.rata_durasi_menit%60;
                  const totalOt = r.total_menit - (jamStandar * (r.total_hadir - r.tanpa_pulang));
                  const otJ=Math.floor(Math.abs(totalOt)/60), otM=Math.abs(totalOt)%60;
                  return (
                    <tr key={r.nama_karyawan} style={{background:i%2===0?"#fff":"#f7fafc",borderBottom:"1px solid #e2e8f0"}}>
                      <td style={{...td,color:"#a0aec0"}}>{i+1}</td>
                      <td style={{...td,fontWeight:700,color:"#2d3748"}}>{r.nama_karyawan}</td>
                      <td style={{...td,textAlign:"center"}}>
                        <span style={{background:"#bee3f8",color:"#2c5282",padding:"3px 10px",borderRadius:20,fontWeight:700}}>{r.total_hadir}</span>
                      </td>
                      <td style={{...td,textAlign:"center"}}>
                        {r.tanpa_pulang>0
                          ? <span style={{background:"#fed7d7",color:"#9b2c2c",padding:"3px 10px",borderRadius:20,fontWeight:600}}>{r.tanpa_pulang}</span>
                          : <span style={{color:"#a0aec0"}}>—</span>}
                      </td>
                      <td style={{...td,fontWeight:500}}>{r.total_durasi}</td>
                      <td style={td}>
                        {r.rata_durasi_menit>0
                          ? <span style={{color:r.rata_durasi_menit<jamStandar?"#c05621":"#276749",fontWeight:500}}>{rj}j {rm}m</span>
                          : "—"}
                      </td>
                      <td style={td}>
                        <span style={{color:totalOt<0?"#e53e3e":"#276749",fontWeight:600}}>
                          {totalOt>=0?"+":"-"}{otJ}j {otM}m
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// =====================
// PENGELUARAN PAGE
// =====================

function buildNextcloudUrl(url) {
  if (!url || url === '-' || url === '') return null;
  return '/api/proxy/bukti?url=' + encodeURIComponent(url);
}

async function openBukti(url) {
  if (!url || url === '-' || url === '') return;
  try {
    const token = localStorage.getItem('token');
    const res = await fetch('/api/proxy/bukti?url=' + encodeURIComponent(url), {
      headers: { 'Authorization': 'Bearer ' + token }
    });
    if (!res.ok) { alert('Gagal buka bukti: ' + res.status); return; }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
  } catch(e) {
    alert('Error: ' + e.message);
  }
}

// ── Delete Modal Component ──
function DeleteModal({ onConfirm, onCancel, label }) {
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  async function handleConfirm() {
    if (!password) { setError("Password wajib diisi"); return; }
    setLoading(true);
    setError("");
    try {
      // Verifikasi password dengan login
      const form = new FormData();
      form.append("username", "ronald");
      form.append("password", password);
      const res = await fetch("/api/auth/login", { method: "POST", body: form });
      if (!res.ok) { setError("Password salah"); setLoading(false); return; }
      onConfirm();
    } catch(e) {
      setError("Error: " + e.message);
      setLoading(false);
    }
  }

  return (
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{background:"#fff",borderRadius:12,padding:28,width:340,boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
        <h3 style={{margin:"0 0 8px",color:"#2d3748",fontSize:16}}>🗑️ Konfirmasi Hapus</h3>
        <p style={{margin:"0 0 16px",fontSize:14,color:"#718096"}}>{label}</p>
        <label style={{fontSize:14,fontWeight:600,color:"#4a5568",display:"block",marginBottom:6}}>Password Ronald</label>
        <input
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleConfirm()}
          autoFocus
          placeholder="Masukkan password..."
          autoComplete="new-password"
          style={{width:"100%",padding:"8px 12px",border:"1px solid #e2e8f0",borderRadius:6,fontSize:14,boxSizing:"border-box",marginBottom:8}}
        />
        {error && <div style={{color:"#e53e3e",fontSize:14,marginBottom:8}}>{error}</div>}
        <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:8}}>
          <button onClick={onCancel}
            style={{padding:"8px 16px",background:"#e2e8f0",border:"none",borderRadius:6,cursor:"pointer",fontWeight:600,fontSize:14}}>
            Batal
          </button>
          <button onClick={handleConfirm} disabled={loading}
            style={{padding:"8px 16px",background:"#e53e3e",color:"#fff",border:"none",borderRadius:6,cursor:"pointer",fontWeight:600,fontSize:14}}>
            {loading ? "Memverifikasi..." : "Hapus"}
          </button>
        </div>
      </div>
    </div>
  );
}

const KATEGORI_MAP = [
  { key: "Gaji",    keywords: ["gaji"],                       label: "Gaji Karyawan",  icon: "👥" },
  { key: "Kasbon",  keywords: ["kasbon"],                     label: "Kasbon",         icon: "💵" },
  { key: "Bonus",   keywords: ["bonus","lembur","longshift"],  label: "Bonus & Lembur", icon: "🎁" },
  { key: "Gas",     keywords: ["gas"],                        label: "Gas",            icon: "🔥" },
  { key: "Listrik", keywords: ["listrik","utility","pembayaran utility"], label: "Listrik / Utility", icon: "⚡" },
  { key: "IPL",     keywords: ["ipl","keamanan","kebersihan"],label: "IPL / Keamanan", icon: "🏢" },
  { key: "Air",     keywords: ["air minum","aqua"],           label: "Air Minum",      icon: "💧" },
  { key: "Kuota",   keywords: ["kuota"],                      label: "Kuota",          icon: "📶" },
  { key: "Plastik", keywords: ["plastik"],                    label: "Plastik",        icon: "🛍️", noCabang: true },
  { key: "Sabun",   keywords: ["sabun"],                      label: "Bahan Sabun",    icon: "🧴",  noCabang: true },
  { key: "Hanger",  keywords: ["hanger"],                     label: "Hanger",         icon: "👔",  noCabang: true },
  { key: "Selotip", keywords: ["selotip"],                    label: "Selotip",        icon: "📦",  noCabang: true },
  { key: "Koin",     keywords: ["koin smartlink","koin"],      label: "Koin Smartlink", icon: "🪙" },
  { key: "Peralatan",keywords: ["peralatan","printer","dryer","kipas","baterai","regulator","seal"], label: "Peralatan", icon: "🔧", noCabang: true },
  { key: "Kertas",   keywords: ["kertas printer","otani"],     label: "Kertas Printer", icon: "🖨️",  noCabang: true },
  { key: "Others",  keywords: ["others","lainnya"],           label: "Others",         icon: "📋" },
];

function matchKategori(kategori_utama) {
  if (!kategori_utama) return "Others";
  const k = kategori_utama.toLowerCase();
  for (const cat of KATEGORI_MAP) {
    if (cat.keywords.some(kw => k.includes(kw))) return cat.key;
  }
  return "Others";
}

function PengeluaranPage() {
  const CABANG_LIST = ["PO", "PM", "MT", "DM", "TM"];
  const CABANG_NAMA = {PO:"Puri Orchard", PM:"Palm Mansion", MT:"Metro", DM:"Daan Mogot City", TM:"Tomang"};
  const now = new Date();
  const [bulan, setBulan] = useState(now.getMonth() + 1);
  const [tahun, setTahun] = useState(now.getFullYear());
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailView, setDetailView] = useState(null); // {cabang, kategoriKey, label}
  const [detailData, setDetailData] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null); // {id, label}
  const [showTambah, setShowTambah] = useState(false);
  const [formTambah, setFormTambah] = useState({tanggal:'', keterangan:'', nominal:'', bukti_url:''});
  const [savingTambah, setSavingTambah] = useState(false);
  const [quickFilter, setQuickFilter] = useState(null); // null | 'today' | 'last3' | 'last7'
  const currentUser = localStorage.getItem('fullname') || '';
  const canDelete = ['Ronald', 'ronald'].some(u => currentUser.toLowerCase().includes(u.toLowerCase()));

  // Reset detail saat page aktif
  useEffect(() => { setDetailView(null); setShowTambah(false); }, []);

  // Fetch semua data sekaligus
  useEffect(() => {
    setLoading(true);
    setDetailView(null);
    api.get("/pengeluaran/list", { params: { bulan, tahun, per_page: 2000 } })
      .then(res => setAllData(res.data.data || []))
      .catch(() => setAllData([]))
      .finally(() => setLoading(false));
  }, [bulan, tahun]);

  function handleQuickFilter(f) {
    if (quickFilter === f) { setQuickFilter(null); return; }
    // Pastikan bulan/tahun = sekarang saat klik quick filter
    const n = new Date();
    setBulan(n.getMonth() + 1);
    setTahun(n.getFullYear());
    setQuickFilter(f);
  }

  // Filter data berdasarkan quickFilter
  const displayData = (() => {
    if (!quickFilter) return allData;
    const pad = n => String(n).padStart(2, '0');
    const todayD = new Date();
    const todayStr = `${todayD.getFullYear()}-${pad(todayD.getMonth()+1)}-${pad(todayD.getDate())}`;
    const days = quickFilter === 'today' ? 0 : quickFilter === 'last3' ? 2 : 6;
    const fromD = new Date(todayD); fromD.setDate(fromD.getDate() - days);
    const fromStr = `${fromD.getFullYear()}-${pad(fromD.getMonth()+1)}-${pad(fromD.getDate())}`;
    return allData.filter(r => r.tanggal >= fromStr && r.tanggal <= todayStr);
  })();

  // Group data: { cabang: { kategoriKey: [rows] } }
  const grouped = {};
  for (const cabang of CABANG_LIST) grouped[cabang] = {};
  displayData.forEach(r => {
    const cab = r.cabang || "UNKNOWN";
    if (!grouped[cab]) grouped[cab] = {};
    const key = matchKategori(r.kategori_utama);
    if (!grouped[cab][key]) grouped[cab][key] = [];
    grouped[cab][key].push(r);
  });

  const totalPerCabang = {};
  for (const cab of CABANG_LIST) {
    totalPerCabang[cab] = Object.values(grouped[cab] || {}).flat().reduce((s,r) => s+r.nominal, 0);
  }
  const grandTotal = displayData.reduce((s,r) => s+r.nominal, 0);

  function openDetail(cabang, kategoriKey, label) {
    setDetailView({cabang, kategoriKey, label});
    let rows;
    if (!cabang || cabang === '') {
      // Non Cabang
      const nonCabang = displayData.filter(r => !r.cabang || r.cabang === '-' || r.cabang === '');
      const grouped_nc = {};
      nonCabang.forEach(r => {
        const key = matchKategori(r.kategori_utama);
        if (!grouped_nc[key]) grouped_nc[key] = [];
        grouped_nc[key].push(r);
      });
      rows = [...(grouped_nc[kategoriKey] || [])];
    } else {
      rows = [...((grouped[cabang] || {})[kategoriKey] || [])];
    }
    rows.sort((a,b) => new Date(a.tanggal) - new Date(b.tanggal));
    setDetailData(rows);
  }

  async function handleTambahTransaksi() {
    if (!formTambah.tanggal || !formTambah.nominal || !formTambah.keterangan) {
      alert("Tanggal, keterangan, dan nominal wajib diisi"); return;
    }
    setSavingTambah(true);
    try {
      const payload = {
        tanggal: formTambah.tanggal,
        nominal: parseFloat(formTambah.nominal.replace(/\./g, '').replace(',', '.')) || 0,
        kategori_utama: detailView.kategoriKey === 'Others' ? 'Pengeluaran Others' :
          KATEGORI_MAP.find(k=>k.key===detailView.kategoriKey)?.keywords?.[0] ?
          `Pengeluaran ${KATEGORI_MAP.find(k=>k.key===detailView.kategoriKey)?.label}` : null,
        sub_kategori: null,
        cabang: detailView.cabang,
        keterangan: formTambah.keterangan,
        sumber: 'Manual',
        nextcloudLink: formTambah.bukti_url || null,
        status: 'OK',
        dicatat: new Date().toLocaleString('id-ID'),
      };
      const res = await api.post('/bot/pengeluaran', payload);
      if (res.data.success) {
        // Reload detail data dan urutkan by tanggal
        const newRow = {
          id: res.data.id,
          tanggal: formTambah.tanggal,
          nominal: payload.nominal,
          keterangan: formTambah.keterangan,
          bukti_url: formTambah.bukti_url || null,
          kategori_utama: payload.kategori_utama,
          cabang: detailView.cabang,
        };
        setDetailData(prev => [...prev, newRow].sort((a,b) => new Date(a.tanggal) - new Date(b.tanggal)));
        setAllData(prev => [...prev, newRow]);
        setFormTambah({tanggal:'', keterangan:'', nominal:'', bukti_url:''});
        setShowTambah(false);
      } else {
        alert("Gagal simpan: " + res.data.error);
      }
    } catch(e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setSavingTambah(false);
    }
  }

  async function handleDeletePengeluaran(id) {
    try {
      await api.delete("/pengeluaran/" + id);
      setDetailData(prev => prev.filter(r => r.id !== id));
      // Update allData juga
      setAllData(prev => prev.filter(r => r.id !== id));
      setDeleteModal(null);
    } catch(e) {
      alert("Gagal hapus: " + (e.response?.data?.detail || e.message));
      setDeleteModal(null);
    }
  }

  function formatTgl(dateStr) {
    if (!dateStr) return "—";
    const d = new Date(dateStr + "T00:00:00");
    return d.getDate() + "/" + String(d.getMonth()+1).padStart(2,"0");
  }

  const th = {padding:"8px 12px", textAlign:"left", fontWeight:600, fontSize:14, whiteSpace:"nowrap"};
  const td = {padding:"7px 12px", borderBottom:"1px solid #e2e8f0", fontSize:14};

  // ── DETAIL VIEW ──
  if (detailView) {
    const totalDetail = detailData.reduce((s,r) => s+r.nominal, 0);
    return (
      <div style={styles.page}>
        <div style={{display:"flex", alignItems:"center", gap:12, marginBottom:20}}>
          <button onClick={() => setDetailView(null)}
            style={{background:"#e2e8f0", border:"none", borderRadius:8, padding:"8px 16px", cursor:"pointer", fontWeight:600, fontSize:14}}>
            ← Kembali
          </button>
          <div>
            <h2 style={{...styles.pageTitle, margin:0}}>
              {KATEGORI_MAP.find(k=>k.key===detailView.kategoriKey)?.icon} {detailView.label}
            </h2>
            <span style={{fontSize:14, color:"#718096"}}>
              {detailView.cabang} — {CABANG_NAMA[detailView.cabang]} · {BULAN_NAMA[bulan]} {tahun}
            </span>
          </div>
        </div>

        <div style={{display:"flex", gap:12, marginBottom:16}}>
          <div style={{background:"#fff", borderRadius:10, padding:"14px 20px", boxShadow:"0 1px 4px rgba(0,0,0,0.08)", borderTop:"3px solid #e53e3e"}}>
            <div style={{fontSize:14, color:"#718096", marginBottom:4, textTransform:"uppercase", fontWeight:600}}>Total</div>
            <div style={{fontSize:22, fontWeight:700, color:"#e53e3e"}}>{formatRp(totalDetail)}</div>
          </div>
          <div style={{background:"#fff", borderRadius:10, padding:"14px 20px", boxShadow:"0 1px 4px rgba(0,0,0,0.08)", borderTop:"3px solid #3182ce"}}>
            <div style={{fontSize:14, color:"#718096", marginBottom:4, textTransform:"uppercase", fontWeight:600}}>Transaksi</div>
            <div style={{fontSize:22, fontWeight:700, color:"#3182ce"}}>{detailData.length}</div>
          </div>
        </div>

        {/* Tombol Tambah */}
        <div style={{marginBottom:12, display:"flex", justifyContent:"flex-end"}}>
          <button onClick={()=>setShowTambah(!showTambah)}
            style={{background:"#38a169",color:"#fff",border:"none",padding:"8px 18px",borderRadius:8,cursor:"pointer",fontWeight:600,fontSize:14}}>
            {showTambah ? "✕ Batal" : "+ Tambah Transaksi"}
          </button>
        </div>

        {showTambah && (
          <div style={{background:"#f0fff4",border:"1px solid #9ae6b4",borderRadius:10,padding:16,marginBottom:16}}>
            <h4 style={{margin:"0 0 12px",color:"#276749",fontSize:14}}>+ Tambah Transaksi Manual</h4>
            <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"flex-end"}}>
              <div>
                <label style={{fontSize:14,fontWeight:600,color:"#4a5568",display:"block",marginBottom:4}}>TANGGAL</label>
                <input type="date" value={formTambah.tanggal}
                  onChange={e=>setFormTambah(p=>({...p,tanggal:e.target.value}))}
                  style={{padding:"7px 10px",border:"1px solid #e2e8f0",borderRadius:6,fontSize:14}}/>
              </div>
              <div style={{flex:2,minWidth:200}}>
                <label style={{fontSize:14,fontWeight:600,color:"#4a5568",display:"block",marginBottom:4}}>KETERANGAN</label>
                <input type="text" value={formTambah.keterangan} placeholder="Contoh: Gas PO 1085000"
                  onChange={e=>setFormTambah(p=>({...p,keterangan:e.target.value}))}
                  style={{width:"100%",padding:"7px 10px",border:"1px solid #e2e8f0",borderRadius:6,fontSize:14,boxSizing:"border-box"}}/>
              </div>
              <div>
                <label style={{fontSize:14,fontWeight:600,color:"#4a5568",display:"block",marginBottom:4}}>NOMINAL</label>
                <input type="number" value={formTambah.nominal} placeholder="1085000"
                  onChange={e=>setFormTambah(p=>({...p,nominal:e.target.value}))}
                  style={{padding:"7px 10px",border:"1px solid #e2e8f0",borderRadius:6,fontSize:14,width:130}}/>
              </div>
              <div style={{flex:2,minWidth:200}}>
                <label style={{fontSize:14,fontWeight:600,color:"#4a5568",display:"block",marginBottom:4}}>URL BUKTI (opsional)</label>
                <input type="text" value={formTambah.bukti_url} placeholder="https://nextcloud..."
                  onChange={e=>setFormTambah(p=>({...p,bukti_url:e.target.value}))}
                  style={{width:"100%",padding:"7px 10px",border:"1px solid #e2e8f0",borderRadius:6,fontSize:14,boxSizing:"border-box"}}/>
              </div>
              <button onClick={handleTambahTransaksi} disabled={savingTambah}
                style={{background:"#2b6cb0",color:"#fff",border:"none",padding:"8px 18px",borderRadius:6,cursor:"pointer",fontWeight:600,fontSize:14,whiteSpace:"nowrap"}}>
                {savingTambah ? "Menyimpan..." : "Simpan"}
              </button>
            </div>
          </div>
        )}

        <div style={{overflowX:"auto", borderRadius:10, boxShadow:"0 1px 4px rgba(0,0,0,0.08)"}}>
          <table style={{width:"100%", borderCollapse:"collapse", background:"#fff", fontSize:14}}>
            <thead>
              <tr style={{background:"#f8fafc"}}>
                {["No","Tanggal","Keterangan","Nominal","Bukti",...(canDelete?["Hapus"]:[])] .map(h=><th key={h} style={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {detailData.length === 0
                ? <tr><td colSpan={5} style={{textAlign:"center", padding:40, color:"#a0aec0"}}>Tidak ada data</td></tr>
                : detailData.map((r,i) => (
                  <tr key={r.id} style={{background:i%2===0?"#fff":"#f7fafc", borderBottom:"1px solid #e2e8f0"}}>
                    <td style={{...td, color:"#a0aec0"}}>{i+1}</td>
                    <td style={{...td, whiteSpace:"nowrap"}}>{formatTgl(r.tanggal)}</td>
                    <td style={td}>{r.keterangan || "—"}</td>
                    <td style={{...td, fontWeight:700, color:"#e53e3e", whiteSpace:"nowrap", textAlign:"right"}}>{formatRp(r.nominal)}</td>
                    <td style={{...td, textAlign:"center"}}>
                      {r.bukti_url && r.bukti_url !== "-" && r.bukti_url !== ""
                        ? <button onClick={()=>openBukti(r.bukti_url)}
                            style={{background:"#3182ce",color:"#fff",padding:"3px 10px",borderRadius:6,fontSize:14,fontWeight:600,border:"none",cursor:"pointer"}}>
                            📎 Lihat
                          </button>
                        : <span style={{color:"#a0aec0", fontSize:14}}>—</span>}
                    </td>
                    {canDelete && (
                      <td style={{...td, textAlign:"center"}}>
                        <button onClick={()=>setDeleteModal({id:r.id, label:`Hapus "${r.keterangan}" - ${formatRp(r.nominal)}?`})}
                          style={{background:"#fff5f5",color:"#e53e3e",border:"1px solid #fed7d7",padding:"3px 10px",borderRadius:6,fontSize:14,fontWeight:600,cursor:"pointer"}}>
                          🗑️
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
            </tbody>
            {detailData.length > 0 && (
              <tfoot>
                <tr style={{background:"#ebf8ff"}}>
                  <td colSpan={3} style={{...td, fontWeight:700}}>Total</td>
                  <td style={{...td, fontWeight:700, color:"#e53e3e", textAlign:"right"}}>{formatRp(totalDetail)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      {deleteModal && (
        <DeleteModal
          label={deleteModal.label}
          onConfirm={() => handleDeletePengeluaran(deleteModal.id)}
          onCancel={() => setDeleteModal(null)}
        />
      )}
      </div>
    );
  }

  // ── DASHBOARD VIEW ──
  return (
    <div style={styles.page}>
      <h2 style={styles.pageTitle}>💰 Laporan Pengeluaran</h2>

      {/* Filter */}
      <div style={{...styles.card, display:"flex", gap:12, alignItems:"flex-end", flexWrap:"wrap", marginBottom:16}}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Bulan</label>
          <select style={styles.select} value={bulan} onChange={e=>{setBulan(Number(e.target.value));setQuickFilter(null);}}>
            {BULAN_NAMA.slice(1).map((b,i)=><option key={i+1} value={i+1}>{b}</option>)}
          </select>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Tahun</label>
          <select style={styles.select} value={tahun} onChange={e=>{setTahun(Number(e.target.value));setQuickFilter(null);}}>
            {Array.from({length:3},(_,i)=>new Date().getFullYear()-1+i).map(y=><option key={y}>{y}</option>)}
          </select>
        </div>
        <div style={{display:"flex", gap:6, alignItems:"flex-end", paddingBottom:2}}>
          {[['today','Hari ini'],['last3','Last 3'],['last7','Last 7']].map(([f,label])=>(
            <button key={f} onClick={()=>handleQuickFilter(f)}
              style={{padding:"6px 14px", borderRadius:8, border:"1px solid #e2e8f0", cursor:"pointer",
                fontSize:14, fontWeight:quickFilter===f?700:500,
                background:quickFilter===f?"#2563eb":"#f8fafc",
                color:quickFilter===f?"#fff":"#374151"}}>
              {label}
            </button>
          ))}
        </div>
        <div style={{marginLeft:"auto", display:"flex", alignItems:"flex-end", gap:16}}>
          <button onClick={async()=>{
              await downloadExcel('/export/pengeluaran', {bulan, tahun}, `Pengeluaran_${BULAN_NAMA[bulan]}_${tahun}.xlsx`);
            }} style={{padding:"6px 14px", borderRadius:8, border:"1px solid #e2e8f0", cursor:"pointer",
              fontSize:14, fontWeight:500, background:"#f8fafc", color:"#374151", whiteSpace:"nowrap"}}>
            📥 Export Excel
          </button>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:14, color:"#718096", marginBottom:2, textTransform:"uppercase", fontWeight:600}}>
              Grand Total{quickFilter ? " (filtered)" : ""}
            </div>
            <div style={{fontSize:22, fontWeight:700, color:"#e53e3e"}}>{formatRp(grandTotal)}</div>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{textAlign:"center", padding:60, color:"#a0aec0"}}>Memuat data...</div>
      ) : (
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(320px, 1fr))", gap:16}}>
          {CABANG_LIST.map(cab => {
            const cabData = grouped[cab] || {};
            const total = totalPerCabang[cab];
            return (
              <div key={cab} style={{background:"#fff", borderRadius:12, boxShadow:"0 1px 4px rgba(0,0,0,0.08)", overflow:"hidden"}}>
                {/* Header cabang */}
                <div style={{background:"#2b6cb0", padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                  <div>
                    <span style={{background:"rgba(255,255,255,0.2)", color:"#fff", padding:"2px 8px", borderRadius:20, fontSize:14, fontWeight:700, marginRight:8}}>{cab}</span>
                    <span style={{color:"#fff", fontWeight:700, fontSize:14}}>{CABANG_NAMA[cab]}</span>
                  </div>
                  <span style={{color:"#fff", fontWeight:700, fontSize:16}}>{formatRp(total)}</span>
                </div>

                {/* Rows per kategori */}
                <div style={{padding:"8px 0"}}>
                  {KATEGORI_MAP.filter(cat => !cat.noCabang).map(cat => {
                    const rows = cabData[cat.key] || [];
                    const subtotal = rows.reduce((s,r) => s+r.nominal, 0);
                    return (
                      <div key={cat.key}
                        onClick={() => rows.length > 0 && openDetail(cab, cat.key, cat.label)}
                        style={{
                          display:"flex", justifyContent:"space-between", alignItems:"center",
                          padding:"9px 16px", cursor: rows.length > 0 ? "pointer" : "default",
                          borderBottom:"1px solid #f0f4f8",
                          transition:"background 0.1s",
                        }}
                        onMouseEnter={e => { if(rows.length>0) e.currentTarget.style.background="#ebf8ff"; }}
                        onMouseLeave={e => { e.currentTarget.style.background="transparent"; }}
                      >
                        <span style={{fontSize:14, color: rows.length>0?"#2d3748":"#a0aec0"}}>
                          {cat.icon} {cat.label}
                          {rows.length > 0 && <span style={{fontSize:14, color:"#718096", marginLeft:6}}>({rows.length}x)</span>}
                        </span>
                        <span style={{fontSize:14, fontWeight: subtotal>0?700:400, color: subtotal>0?"#e53e3e":"#a0aec0"}}>
                          {subtotal > 0 ? formatRp(subtotal) : "Rp 0"}
                          {rows.length > 0 && <span style={{fontSize:10, color:"#3182ce", marginLeft:4}}>›</span>}
                        </span>
                      </div>
                    );
                  })}
                  {/* Total row */}
                  <div style={{display:"flex", justifyContent:"space-between", padding:"10px 16px", background:"#f7fafc", borderTop:"2px solid #e2e8f0", marginTop:4}}>
                    <span style={{fontWeight:700, fontSize:14, color:"#2d3748"}}>Total</span>
                    <span style={{fontWeight:700, fontSize:14, color:"#e53e3e"}}>{formatRp(total)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        {/* Card Non Cabang */}
        {(() => {
          const nonCabangData = displayData.filter(r => !r.cabang || r.cabang === '-' || r.cabang === '');
          const grouped_nc = {};
          nonCabangData.forEach(r => {
            const key = matchKategori(r.kategori_utama);
            if (!grouped_nc[key]) grouped_nc[key] = [];
            grouped_nc[key].push(r);
          });
          const total_nc = nonCabangData.reduce((s,r) => s+r.nominal, 0);
          if (total_nc === 0) return null;
          return (
            <div style={{background:"#fff", borderRadius:12, boxShadow:"0 1px 4px rgba(0,0,0,0.08)", overflow:"hidden"}}>
              <div style={{background:"#718096", padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                <div>
                  <span style={{background:"rgba(255,255,255,0.2)", color:"#fff", padding:"2px 8px", borderRadius:20, fontSize:14, fontWeight:700, marginRight:8}}>—</span>
                  <span style={{color:"#fff", fontWeight:700, fontSize:14}}>Non Cabang</span>
                </div>
                <span style={{color:"#fff", fontWeight:700, fontSize:16}}>{formatRp(total_nc)}</span>
              </div>
              <div style={{padding:"8px 0"}}>
                {KATEGORI_MAP.filter(cat => cat.noCabang || cat.key === 'Others').map(cat => {
                  const rows = grouped_nc[cat.key] || [];
                  const subtotal = rows.reduce((s,r) => s+r.nominal, 0);
                  return (
                    <div key={cat.key}
                      onClick={() => rows.length > 0 && openDetail('', cat.key, cat.label + ' (Non Cabang)')}
                      style={{display:"flex", justifyContent:"space-between", alignItems:"center",
                        padding:"9px 16px", cursor: rows.length > 0 ? "pointer" : "default",
                        borderBottom:"1px solid #f0f4f8", transition:"background 0.1s"}}
                      onMouseEnter={e => { if(rows.length>0) e.currentTarget.style.background="#f7fafc"; }}
                      onMouseLeave={e => { e.currentTarget.style.background="transparent"; }}>
                      <span style={{fontSize:14, color: rows.length>0?"#2d3748":"#a0aec0"}}>
                        {cat.icon} {cat.label}
                        {rows.length > 0 && <span style={{fontSize:14, color:"#718096", marginLeft:6}}>({rows.length}x)</span>}
                      </span>
                      <span style={{fontSize:14, fontWeight: subtotal>0?700:400, color: subtotal>0?"#e53e3e":"#a0aec0"}}>
                        {subtotal > 0 ? formatRp(subtotal) : "Rp 0"}
                        {rows.length > 0 && <span style={{fontSize:10, color:"#3182ce", marginLeft:4}}>›</span>}
                      </span>
                    </div>
                  );
                })}
                <div style={{display:"flex", justifyContent:"space-between", padding:"10px 16px", background:"#f7fafc", borderTop:"2px solid #e2e8f0", marginTop:4}}>
                  <span style={{fontWeight:700, fontSize:14, color:"#2d3748"}}>Total</span>
                  <span style={{fontWeight:700, fontSize:14, color:"#e53e3e"}}>{formatRp(total_nc)}</span>
                </div>
              </div>
            </div>
          );
        })()}
        </div>
      )}
    </div>
  );
}

// =====================
// KASBON PAGE
// =====================
function KasbonPage() {
  const CABANG_LIST = ["PO", "PM", "MT", "DM", "TM"];
  const CABANG_NAMA = {PO:"Puri Orchard", PM:"Palm Mansion", MT:"Metro", DM:"Daan Mogot City", TM:"Tomang"};
  const now = new Date();
  const [bulan, setBulan] = useState(now.getMonth() + 1);
  const [tahun, setTahun] = useState(now.getFullYear());
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailView, setDetailView] = useState(null); // {cabang, nama}
  const [detailData, setDetailData] = useState([]);
  const [showLunas, setShowLunas] = useState(null); // kasbon_id
  const [formLunas, setFormLunas] = useState({tanggal:'', nominal:''});
  const [savingLunas, setSavingLunas] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);
  const [editModal, setEditModal] = useState(null); // {id, nama_karyawan, tanggal, nominal, bukti_url}
  const [savingEdit, setSavingEdit] = useState(false);
  const [showTambahKasbon, setShowTambahKasbon] = useState(null); // cabang
  const [formKasbon, setFormKasbon] = useState({nama:'', tanggal:'', nominal:'', bukti_url:''});
  const [savingKasbon, setSavingKasbon] = useState(false);
  const [kasbonNamaOpts, setKasbonNamaOpts] = useState([]);
  const [kasbonTab, setKasbonTab] = useState("aktif");
  const currentUser = localStorage.getItem('fullname') || '';
  const canDelete = ['Ronald','ronald'].some(u => currentUser.toLowerCase().includes(u.toLowerCase()));

  async function reloadAllData() {
    const params = kasbonTab === "aktif"
      ? { mode: "aktif", per_page: 1000 }
      : { mode: "arsip", per_page: 1000, ...(tahun && tahun > 0 ? { tahun } : {}) };
    const res = await api.get("/kasbon/list", { params });
    return res.data.data || [];
  }

  useEffect(() => {
    setLoading(true);
    setDetailView(null);
    reloadAllData()
      .then(data => setAllData(data))
      .catch(() => setAllData([]))
      .finally(() => setLoading(false));
  }, [tahun, kasbonTab]);

  useEffect(() => {
    if (!showTambahKasbon) { setKasbonNamaOpts([]); return; }
    api.get("/karyawan/list", { params: { cabang: showTambahKasbon, status: "aktif" } })
      .then(r => setKasbonNamaOpts((r.data.data||[]).map(k => k.nama)))
      .catch(() => setKasbonNamaOpts([]));
  }, [showTambahKasbon]);

  // Group by cabang -> nama
  const grouped = {};
  for (const cab of CABANG_LIST) grouped[cab] = {};
  allData.forEach(r => {
    if (!grouped[r.cabang]) grouped[r.cabang] = {};
    if (!grouped[r.cabang][r.nama_karyawan]) grouped[r.cabang][r.nama_karyawan] = [];
    grouped[r.cabang][r.nama_karyawan].push(r);
  });

  async function handleTambahKasbon(cabang) {
    if (!formKasbon.nama || !formKasbon.tanggal || !formKasbon.nominal) {
      alert("Nama, tanggal, dan nominal wajib diisi"); return;
    }
    setSavingKasbon(true);
    try {
      const res = await api.post("/bot/kasbon", {
        nama: formKasbon.nama.trim(),
        cabang: cabang,
        nominal: parseFloat(formKasbon.nominal),
        tanggal: new Date(formKasbon.tanggal).toLocaleDateString('id-ID'),
        nextcloudLink: formKasbon.bukti_url || '',
      });
      if (res.data.success) {
        setAllData(await reloadAllData());
        setFormKasbon({nama:'', tanggal:'', nominal:'', bukti_url:''});
        setShowTambahKasbon(null);
      } else {
        alert("Gagal: " + res.data.error);
      }
    } catch(e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setSavingKasbon(false);
    }
  }

  function totalSisaKaryawan(rows) {
    return rows.reduce((s,r) => s + (r.sisa || 0), 0);
  }

  function openDetail(cabang, nama) {
    setDetailView({cabang, nama});
    setDetailData(allData.filter(r => r.cabang === cabang && r.nama_karyawan === nama)
      .sort((a,b) => new Date(a.tanggal) - new Date(b.tanggal)));
    setShowLunas(null);
  }

  async function handleLunas(kasbonId) {
    if (!formLunas.tanggal || !formLunas.nominal) { alert("Tanggal dan nominal wajib diisi"); return; }
    setSavingLunas(true);
    try {
      const res = await api.post("/bot/kasbon/cicilan", {
        kasbon_id: kasbonId,
        nama: detailView.nama,
        cabang: detailView.cabang,
        nominal: parseFloat(formLunas.nominal),
        tanggal: formLunas.tanggal.split('-').reverse().join('/'),
      });
      if (res.data.success) {
        const newAll = await reloadAllData();
        setAllData(newAll);
        setDetailData(newAll.filter(r => r.cabang === detailView.cabang && r.nama_karyawan === detailView.nama)
          .sort((a,b) => new Date(a.tanggal) - new Date(b.tanggal)));
        setFormLunas({tanggal:'', nominal:''});
        setShowLunas(null);
      } else {
        alert("Gagal: " + res.data.error);
      }
    } catch(e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setSavingLunas(false);
    }
  }

  async function handleDeleteCicilan(cicilanId, kasbonId) {
    if (!window.confirm("Hapus cicilan pelunasan ini?")) return;
    try {
      await api.delete("/kasbon/cicilan/" + cicilanId);
      const newAll = await reloadAllData();
      setAllData(newAll);
      setDetailData(newAll.filter(r => r.cabang === detailView.cabang && r.nama_karyawan === detailView.nama)
        .sort((a,b) => new Date(a.tanggal) - new Date(b.tanggal)));
    } catch(e) {
      alert("Gagal hapus cicilan: " + (e.response?.data?.detail || e.message));
    }
  }

  async function handleEditKasbon() {
    if (!editModal.nama_karyawan || !editModal.tanggal || !editModal.nominal) {
      alert("Nama, tanggal, dan nominal wajib diisi"); return;
    }
    setSavingEdit(true);
    try {
      await api.put("/kasbon/" + editModal.id, {
        nama_karyawan: editModal.nama_karyawan,
        tanggal: editModal.tanggal,
        nominal: parseFloat(editModal.nominal),
        bukti_url: editModal.bukti_url || "",
      });
      const newAll = await reloadAllData();
      setAllData(newAll);
      if (detailView) {
        setDetailData(newAll.filter(r => r.cabang === detailView.cabang && r.nama_karyawan === detailView.nama)
          .sort((a,b) => new Date(a.tanggal) - new Date(b.tanggal)));
      }
      setEditModal(null);
    } catch(e) {
      alert("Gagal edit: " + (e.response?.data?.detail || e.message));
    } finally {
      setSavingEdit(false);
    }
  }

  async function handleDeleteKasbon(id) {
    try {
      await api.delete("/kasbon/" + id);
      const newAll = allData.filter(r => r.id !== id);
      setAllData(newAll);
      setDetailData(prev => prev.filter(r => r.id !== id));
      setDeleteModal(null);
    } catch(e) {
      alert("Gagal hapus: " + (e.response?.data?.detail || e.message));
      setDeleteModal(null);
    }
  }

  function formatTgl(dateStr) {
    if (!dateStr) return "—";
    const d = new Date(dateStr + "T00:00:00");
    return d.getDate() + " " + BULAN_NAMA[d.getMonth()+1].slice(0,3) + " " + d.getFullYear();
  }

  function statusBadge(status) {
    const s = {"Belum Lunas":{bg:"#fed7d7",color:"#9b2c2c"},"Sebagian":{bg:"#fefcbf",color:"#744210"},"Lunas":{bg:"#c6f6d5",color:"#276749"}}[status]||{bg:"#e2e8f0",color:"#4a5568"};
    return <span style={{background:s.bg,color:s.color,padding:"2px 8px",borderRadius:20,fontSize:14,fontWeight:700}}>{status}</span>;
  }

  const grandTotal = allData.reduce((s,r) => s+(r.sisa||0), 0);
  const th = {padding:"9px 12px",textAlign:"left",fontWeight:600,fontSize:14,whiteSpace:"nowrap"};
  const td = {padding:"8px 12px",borderBottom:"1px solid #e2e8f0",fontSize:14};

  // ── DETAIL VIEW ──
  if (detailView) {
    const totalNominal = detailData.reduce((s,r) => s+r.nominal, 0);
    const totalSisa = detailData.reduce((s,r) => s+(r.sisa||0), 0);
    return (
      <div style={styles.page}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
          <button onClick={()=>setDetailView(null)}
            style={{background:"#e2e8f0",border:"none",borderRadius:8,padding:"8px 16px",cursor:"pointer",fontWeight:600,fontSize:14}}>
            ← Kembali
          </button>
          <div>
            <h2 style={{...styles.pageTitle,margin:0}}>👤 {detailView.nama}</h2>
            <span style={{fontSize:14,color:"#718096"}}>{detailView.cabang} — {CABANG_NAMA[detailView.cabang]}</span>
          </div>
        </div>

        {/* Summary */}
        <div style={{display:"flex",gap:12,marginBottom:16,flexWrap:"wrap"}}>
          {[["Total Kasbon",formatRp(totalNominal),"#e53e3e"],["Total Sisa",formatRp(totalSisa),"#c05621"],["Jumlah Transaksi",detailData.length,"#3182ce"]].map(([l,v,c])=>(
            <div key={l} style={{background:"#fff",borderRadius:10,padding:"14px 20px",boxShadow:"0 1px 4px rgba(0,0,0,0.08)",minWidth:140,borderTop:"3px solid "+c}}>
              <div style={{fontSize:14,color:"#718096",marginBottom:4,textTransform:"uppercase",fontWeight:600}}>{l}</div>
              <div style={{fontSize:22,fontWeight:700,color:c}}>{v}</div>
            </div>
          ))}
        </div>

        {deleteModal && <DeleteModal label={deleteModal.label} onConfirm={()=>handleDeleteKasbon(deleteModal.id)} onCancel={()=>setDeleteModal(null)}/>}

        {editModal && (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <div style={{background:"#fff",borderRadius:12,padding:28,width:380,boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}}>
              <h3 style={{margin:"0 0 18px",fontSize:16,fontWeight:700}}>✏️ Edit Kasbon</h3>
              {[
                ["Nama Karyawan","text","nama_karyawan"],
                ["Tanggal","date","tanggal"],
                ["Nominal","number","nominal"],
                ["Link Bukti","text","bukti_url"],
              ].map(([label,type,key])=>(
                <div key={key} style={{marginBottom:12}}>
                  <label style={{fontSize:14,fontWeight:600,color:"#4a5568",display:"block",marginBottom:4}}>{label}</label>
                  <input type={type} value={editModal[key]||""} onChange={e=>setEditModal(p=>({...p,[key]:e.target.value}))}
                    style={{width:"100%",border:"1px solid #d1d5db",borderRadius:6,padding:"7px 10px",fontSize:14,boxSizing:"border-box"}}/>
                </div>
              ))}
              <div style={{display:"flex",gap:10,marginTop:20,justifyContent:"flex-end"}}>
                <button onClick={()=>setEditModal(null)} style={{padding:"8px 18px",borderRadius:7,border:"1px solid #d1d5db",background:"#f8fafc",fontSize:14,cursor:"pointer"}}>Batal</button>
                <button onClick={handleEditKasbon} disabled={savingEdit} style={{padding:"8px 18px",borderRadius:7,border:"none",background:"#3182ce",color:"#fff",fontSize:14,fontWeight:600,cursor:"pointer"}}>
                  {savingEdit?"Menyimpan...":"💾 Simpan"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div style={{overflowX:"auto",borderRadius:10,boxShadow:"0 1px 4px rgba(0,0,0,0.08)"}}>
          <table style={{width:"100%",borderCollapse:"collapse",background:"#fff",fontSize:14}}>
            <thead>
              <tr style={{background:"#f8fafc"}}>
                {["No","Tgl Kasbon","Nominal","Sisa","Status","Bukti","Cicilan","Pelunasan","Edit",...(canDelete?["Hapus"]:[])].map(h=><th key={h} style={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {detailData.map((r,i)=>(
                <>
                  <tr key={r.id} style={{background:r.status==="Lunas"?"#f0fff4":i%2===0?"#fff":"#f7fafc",borderBottom:"1px solid #e2e8f0"}}>
                    <td style={{...td,color:"#a0aec0"}}>{i+1}</td>
                    <td style={{...td,whiteSpace:"nowrap"}}>{formatTgl(r.tanggal)}</td>
                    <td style={{...td,fontWeight:600,color:"#e53e3e",whiteSpace:"nowrap"}}>{formatRp(r.nominal)}</td>
                    <td style={{...td,fontWeight:600,color:r.sisa>0?"#c05621":"#276749",whiteSpace:"nowrap"}}>{r.sisa>0?formatRp(r.sisa):"—"}</td>
                    <td style={td}>{statusBadge(r.status)}</td>
                    <td style={{...td,textAlign:"center"}}>
                      {r.bukti_url && r.bukti_url!=="-"
                        ? <button onClick={()=>openBukti(r.bukti_url)} style={{background:"#3182ce",color:"#fff",padding:"3px 10px",borderRadius:6,fontSize:14,fontWeight:600,border:"none",cursor:"pointer"}}>📎 Lihat</button>
                        : <span style={{color:"#a0aec0",fontSize:14}}>—</span>}
                    </td>
                    <td style={{...td,textAlign:"center"}}>
                      {r.cicilan&&r.cicilan.length>0
                        ? <button onClick={()=>setShowLunas(showLunas===r.id?null:r.id)}
                            style={{background:showLunas===r.id?"#e2e8f0":"#ebf8ff",color:showLunas===r.id?"#4a5568":"#2b6cb0",border:"none",padding:"3px 10px",borderRadius:6,fontSize:14,fontWeight:600,cursor:"pointer"}}>
                            {showLunas===r.id?"▲ Tutup":`▼ ${r.cicilan.length}x`}
                          </button>
                        : <span style={{color:"#a0aec0",fontSize:14}}>—</span>}
                    </td>
                    <td style={{...td,textAlign:"center"}}>
                      {r.status!=="Lunas" && (
                        <button onClick={()=>{setShowLunas('form_'+r.id); setFormLunas({tanggal:'',nominal:''}); }}
                          style={{background:"#c6f6d5",color:"#276749",border:"none",padding:"3px 10px",borderRadius:6,fontSize:14,fontWeight:600,cursor:"pointer"}}>
                          💵 Lunas
                        </button>
                      )}
                    </td>
                    <td style={{...td,textAlign:"center"}}>
                      <button onClick={()=>setEditModal({id:r.id, nama_karyawan:r.nama_karyawan, tanggal:r.tanggal, nominal:r.nominal, bukti_url:r.bukti_url||""})}
                        style={{background:"#ebf8ff",color:"#2b6cb0",border:"1px solid #bee3f8",padding:"3px 10px",borderRadius:6,fontSize:14,fontWeight:600,cursor:"pointer"}}>✏️</button>
                    </td>
                    {canDelete && (
                      <td style={{...td,textAlign:"center"}}>
                        <button onClick={()=>setDeleteModal({id:r.id,label:`Hapus kasbon ${r.nama_karyawan} - ${formatRp(r.nominal)}?`})}
                          style={{background:"#fff5f5",color:"#e53e3e",border:"1px solid #fed7d7",padding:"3px 10px",borderRadius:6,fontSize:14,fontWeight:600,cursor:"pointer"}}>🗑️</button>
                      </td>
                    )}
                  </tr>
                  {showLunas===r.id && r.cicilan&&r.cicilan.length>0 && (
                    <tr key={r.id+"_c"}>
                      <td colSpan={9} style={{padding:"0 0 0 48px",background:"#f7fafc",borderBottom:"1px solid #e2e8f0"}}>
                        <table style={{width:"60%",borderCollapse:"collapse",margin:"8px 0",fontSize:14}}>
                          <thead><tr style={{background:"#e2e8f0"}}>
                            <th style={{...th,fontSize:14}}>Tgl Pelunasan</th>
                            <th style={{...th,fontSize:14}}>Nominal</th>
                            {canDelete && <th style={{...th,fontSize:14}}></th>}
                          </tr></thead>
                          <tbody>{r.cicilan.map((c,ci)=>(
                            <tr key={ci} style={{borderBottom:"1px solid #e2e8f0"}}>
                              <td style={{...td,fontSize:14}}>{formatTgl(c.tanggal)}</td>
                              <td style={{...td,fontSize:14,fontWeight:600,color:"#276749"}}>{formatRp(c.nominal)}</td>
                              {canDelete && (
                                <td style={{...td,fontSize:14}}>
                                  <button onClick={()=>handleDeleteCicilan(c.id, r.id)}
                                    style={{background:"#fff5f5",color:"#e53e3e",border:"1px solid #fed7d7",padding:"2px 8px",borderRadius:5,fontSize:14,cursor:"pointer"}}>
                                    🗑️
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}</tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                  {showLunas===('form_'+r.id) && (
                    <tr key={r.id+"_form"}>
                      <td colSpan={9} style={{padding:"12px 16px",background:"#f0fff4",borderBottom:"1px solid #9ae6b4"}}>
                        <div style={{display:"flex",gap:10,alignItems:"flex-end",flexWrap:"wrap"}}>
                          <div>
                            <label style={{fontSize:14,fontWeight:600,color:"#276749",display:"block",marginBottom:4}}>TANGGAL PELUNASAN</label>
                            <input type="date" value={formLunas.tanggal}
                              onChange={e=>setFormLunas(p=>({...p,tanggal:e.target.value}))}
                              style={{padding:"7px 10px",border:"1px solid #9ae6b4",borderRadius:6,fontSize:14}}/>
                          </div>
                          <div>
                            <label style={{fontSize:14,fontWeight:600,color:"#276749",display:"block",marginBottom:4}}>NOMINAL</label>
                            <input type="number" value={formLunas.nominal} placeholder="500000"
                              onChange={e=>setFormLunas(p=>({...p,nominal:e.target.value}))}
                              style={{padding:"7px 10px",border:"1px solid #9ae6b4",borderRadius:6,fontSize:14,width:130}}/>
                          </div>
                          <button onClick={()=>handleLunas(r.id)} disabled={savingLunas}
                            style={{background:"#38a169",color:"#fff",border:"none",padding:"8px 18px",borderRadius:6,cursor:"pointer",fontWeight:600,fontSize:14}}>
                            {savingLunas?"Menyimpan...":"💾 Simpan"}
                          </button>
                          <button onClick={()=>setShowLunas(null)}
                            style={{background:"#e2e8f0",color:"#4a5568",border:"none",padding:"8px 14px",borderRadius:6,cursor:"pointer",fontSize:14}}>
                            Batal
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ── DASHBOARD VIEW ──
  return (
    <div style={styles.page}>
      <h2 style={styles.pageTitle}>🤝 Kasbon Karyawan</h2>

      <div style={{...styles.card,display:"flex",gap:12,alignItems:"center",flexWrap:"wrap",marginBottom:16}}>
        <div style={{display:"flex",gap:6}}>
          {[["aktif","🔴 Aktif"],["arsip","📦 Arsip"]].map(([val,label])=>(
            <button key={val} onClick={()=>setKasbonTab(val)}
              style={{padding:"6px 16px",borderRadius:8,border:"1px solid #e2e8f0",cursor:"pointer",fontSize:14,fontWeight:kasbonTab===val?700:400,background:kasbonTab===val?"#2563eb":"#fff",color:kasbonTab===val?"#fff":"#374151"}}>
              {label}
            </button>
          ))}
        </div>
        {kasbonTab === "arsip" && (
          <div style={styles.formGroup}>
            <label style={styles.label}>Tahun</label>
            <select style={styles.select} value={tahun || 0} onChange={e=>setTahun(Number(e.target.value)||null)}>
              <option value={0}>Semua Tahun Lalu</option>
              {Array.from({length:5},(_,i)=>new Date().getFullYear()-1-i).map(y=><option key={y}>{y}</option>)}
            </select>
          </div>
        )}
        <div style={{marginLeft:"auto",textAlign:"right"}}>
          <div style={{fontSize:14,color:"#718096",marginBottom:2,textTransform:"uppercase",fontWeight:600}}>Total Sisa Kasbon</div>
          <div style={{fontSize:22,fontWeight:700,color:"#e53e3e"}}>{formatRp(grandTotal)}</div>
        </div>
      </div>

      {loading ? <div style={{textAlign:"center",padding:60,color:"#a0aec0"}}>Memuat data...</div> : (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(300px, 1fr))",gap:16}}>
          {CABANG_LIST.map(cab => {
            const cabData = grouped[cab] || {};
            const namaList = Object.keys(cabData);
            const totalSisa = Object.values(cabData).flat().reduce((s,r) => s+(r.sisa||0), 0);
            return (
              <div key={cab} style={{background:"#fff",borderRadius:12,boxShadow:"0 1px 4px rgba(0,0,0,0.08)",overflow:"hidden"}}>
                <div style={{background:"#2b6cb0",padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <span style={{background:"rgba(255,255,255,0.2)",color:"#fff",padding:"2px 8px",borderRadius:20,fontSize:14,fontWeight:700,marginRight:8}}>{cab}</span>
                    <span style={{color:"#fff",fontWeight:700,fontSize:14}}>{CABANG_NAMA[cab]}</span>
                  </div>
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <span style={{color:"#fff",fontWeight:700,fontSize:16}}>{formatRp(totalSisa)}</span>
                    <button onClick={e=>{e.stopPropagation(); setShowTambahKasbon(showTambahKasbon===cab?null:cab); setFormKasbon({nama:'',tanggal:'',nominal:'',bukti_url:''}); }}
                      style={{background:"rgba(255,255,255,0.2)",color:"#fff",border:"1px solid rgba(255,255,255,0.4)",padding:"3px 10px",borderRadius:6,fontSize:14,fontWeight:600,cursor:"pointer"}}>
                      {showTambahKasbon===cab ? "✕" : "+ Tambah"}
                    </button>
                  </div>
                </div>
                {showTambahKasbon===cab && (
                  <div style={{padding:"12px 16px",background:"#f0fff4",borderBottom:"1px solid #9ae6b4"}}>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"flex-end"}}>
                      <div>
                        <label style={{fontSize:10,fontWeight:600,color:"#276749",display:"block",marginBottom:3}}>NAMA</label>
                        <select value={formKasbon.nama} onChange={e=>setFormKasbon(p=>({...p,nama:e.target.value}))}
                          style={{padding:"6px 10px",border:"1px solid #9ae6b4",borderRadius:6,fontSize:14,width:140}}>
                          <option value="">-- Pilih --</option>
                          {kasbonNamaOpts.map(n=><option key={n} value={n}>{n}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{fontSize:10,fontWeight:600,color:"#276749",display:"block",marginBottom:3}}>TANGGAL</label>
                        <input type="date" value={formKasbon.tanggal} onChange={e=>setFormKasbon(p=>({...p,tanggal:e.target.value}))}
                          style={{padding:"6px 10px",border:"1px solid #9ae6b4",borderRadius:6,fontSize:14}}/>
                      </div>
                      <div>
                        <label style={{fontSize:10,fontWeight:600,color:"#276749",display:"block",marginBottom:3}}>NOMINAL</label>
                        <input type="number" value={formKasbon.nominal} onChange={e=>setFormKasbon(p=>({...p,nominal:e.target.value}))}
                          placeholder="500000"
                          style={{padding:"6px 10px",border:"1px solid #9ae6b4",borderRadius:6,fontSize:14,width:120}}/>
                      </div>
                      <div style={{flex:1,minWidth:150}}>
                        <label style={{fontSize:10,fontWeight:600,color:"#276749",display:"block",marginBottom:3}}>URL BUKTI (opsional)</label>
                        <input value={formKasbon.bukti_url} onChange={e=>setFormKasbon(p=>({...p,bukti_url:e.target.value}))}
                          placeholder="https://nextcloud..."
                          style={{width:"100%",padding:"6px 10px",border:"1px solid #9ae6b4",borderRadius:6,fontSize:14,boxSizing:"border-box"}}/>
                      </div>
                      <button onClick={()=>handleTambahKasbon(cab)} disabled={savingKasbon}
                        style={{background:"#38a169",color:"#fff",border:"none",padding:"7px 16px",borderRadius:6,cursor:"pointer",fontWeight:600,fontSize:14,whiteSpace:"nowrap"}}>
                        {savingKasbon?"Menyimpan...":"💾 Simpan"}
                      </button>
                    </div>
                  </div>
                )}
                <div style={{padding:"8px 0"}}>
                  {namaList.length === 0 ? (
                    <div style={{padding:"16px",color:"#a0aec0",fontSize:14,textAlign:"center"}}>Tidak ada kasbon</div>
                  ) : namaList.map(nama => {
                    const rows = cabData[nama];
                    const sisa = totalSisaKaryawan(rows);
                    const belumLunas = rows.filter(r=>r.status!=="Lunas").length;
                    const semuaLunas = rows.every(r=>r.status==="Lunas");
                    return (
                      <div key={nama} onClick={()=>openDetail(cab,nama)}
                        style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                          padding:"10px 16px",cursor:"pointer",borderBottom:"1px solid #f0f4f8",transition:"background 0.1s"}}
                        onMouseEnter={e=>e.currentTarget.style.background="#ebf8ff"}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <div>
                          <span style={{fontWeight:600,fontSize:14,color:"#2d3748"}}>{nama}</span>
                          <span style={{fontSize:14,color:"#718096",marginLeft:8}}>{rows.length}x kasbon</span>
                          {belumLunas>0 && <span style={{background:"#fed7d7",color:"#9b2c2c",padding:"1px 6px",borderRadius:20,fontSize:10,fontWeight:600,marginLeft:6}}>{belumLunas} aktif</span>}
                        </div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontWeight:700,fontSize:14,color:semuaLunas?"#276749":"#e53e3e"}}>{sisa>0?formatRp(sisa):"Lunas"}</div>
                          <div style={{fontSize:10,color:"#3182ce"}}>›</div>
                        </div>
                      </div>
                    );
                  })}
                  <div style={{display:"flex",justifyContent:"space-between",padding:"10px 16px",background:"#f7fafc",borderTop:"2px solid #e2e8f0",marginTop:4}}>
                    <span style={{fontWeight:700,fontSize:14,color:"#2d3748"}}>Total Sisa</span>
                    <span style={{fontWeight:700,fontSize:14,color:"#e53e3e"}}>{formatRp(totalSisa)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// =====================
// =====================
// GAJI KARYAWAN PAGE
// =====================
// =====================
// DATA KARYAWAN PAGE
// =====================
function KaryawanPage() {
  const CABANG_LIST = ["PO","PM","MT","DM","TM"];
  const [tab, setTab] = useState("aktif");
  const [cabang, setCabang] = useState("semua");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tambahModal, setTambahModal] = useState(false);
  const [resignModal, setResignModal] = useState(null);
  const [pinModal, setPinModal] = useState(null);
  const [newPinVal, setNewPinVal] = useState('');
  const [pinMsg, setPinMsg] = useState(null);
  const [formTambah, setFormTambah] = useState({nama:"", cabang:"PO", jabatan:"", tgl_masuk:""});
  const [tglResign, setTglResign] = useState("");
  const [saving, setSaving] = useState(false);

  async function loadData() {
    setLoading(true);
    try {
      const params = { status: tab };
      if (cabang !== "semua") params.cabang = cabang;
      const res = await api.get("/karyawan/list", { params });
      setData(res.data.data || []);
    } catch { setData([]); } finally { setLoading(false); }
  }

  useEffect(() => { loadData(); }, [tab, cabang]);

  async function handleTambah() {
    if (!formTambah.nama || !formTambah.cabang) { alert("Nama dan cabang wajib diisi"); return; }
    setSaving(true);
    try {
      await api.post("/karyawan/tambah", formTambah);
      setTambahModal(false);
      setFormTambah({nama:"", cabang:"PO", jabatan:"", tgl_masuk:""});
      loadData();
    } catch(e) { alert(e.response?.data?.detail || "Gagal menambah"); }
    finally { setSaving(false); }
  }

  async function handleResign(id) {
    if (!tglResign) { alert("Tanggal resign wajib diisi"); return; }
    setSaving(true);
    try {
      await api.put(`/karyawan/${id}/resign`, { tgl_resign: tglResign });
      setResignModal(null); setTglResign("");
      loadData();
    } catch(e) { alert(e.response?.data?.detail || "Gagal"); }
    finally { setSaving(false); }
  }

  async function handleAktifkan(id) {
    if (!window.confirm("Aktifkan kembali karyawan ini?")) return;
    try { await api.put(`/karyawan/${id}/aktifkan`, {}); loadData(); }
    catch(e) { alert(e.response?.data?.detail || "Gagal"); }
  }

  async function handleResetPin(id) {
    const pin = newPinVal.trim();
    if (pin.length !== 6 || !/^\d{6}$/.test(pin)) { setPinMsg({type:'error', text:'PIN harus 6 angka'}); return; }
    setSaving(true); setPinMsg(null);
    try {
      const fd = new FormData();
      fd.append('pin', pin);
      await api.put(`/karyawan/${id}/pin`, fd);
      setPinMsg({type:'success', text:'PIN berhasil direset'});
      setNewPinVal('');
      setTimeout(() => { setPinModal(null); setPinMsg(null); }, 1500);
    } catch(e) { setPinMsg({type:'error', text: e.response?.data?.detail || 'Gagal reset PIN'}); }
    finally { setSaving(false); }
  }

  const th = {padding:"9px 12px", fontWeight:600, fontSize:14, textAlign:"left", whiteSpace:"nowrap"};
  const td = {padding:"8px 12px", borderBottom:"1px solid #e2e8f0", fontSize:14};

  const grouped = {};
  CABANG_LIST.forEach(c => { grouped[c] = data.filter(r => r.cabang === c); });

  return (
    <div style={styles.page}>
      <h2 style={{...styles.pageTitle, marginBottom:16}}>👥 Data Karyawan</h2>

      {/* Filter */}
      <div style={{...styles.card, display:"flex", gap:12, alignItems:"center", flexWrap:"wrap", marginBottom:16}}>
        <div style={{display:"flex", gap:0, borderRadius:8, overflow:"hidden", border:"1px solid #e2e8f0"}}>
          {["aktif","resign"].map(t => (
            <button key={t} onClick={()=>setTab(t)}
              style={{padding:"7px 20px", fontSize:14, fontWeight:600, border:"none", cursor:"pointer",
                background: tab===t ? "#3182ce" : "#f8fafc",
                color: tab===t ? "#fff" : "#4a5568"}}>
              {t === "aktif" ? "✅ Aktif" : "🚪 Resign"}
            </button>
          ))}
        </div>
        <select value={cabang} onChange={e=>setCabang(e.target.value)} style={styles.select}>
          <option value="semua">Semua Cabang</option>
          {CABANG_LIST.map(c=><option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={()=>setTambahModal(true)}
          style={{...styles.btnPrimary, marginLeft:"auto"}}>+ Tambah Karyawan</button>
      </div>

      {/* Tabel */}
      {loading ? <div style={{textAlign:"center",padding:40,color:"#a0aec0"}}>Memuat...</div> : (
        <div style={{...styles.card, padding:0, overflow:"hidden"}}>
          <table style={{width:"100%", borderCollapse:"collapse", fontSize:14}}>
            <thead>
              <tr style={{background:"#f8fafc"}}>
                {["No","Nama","Cabang","Jabatan","Tgl Masuk", tab==="resign"?"Tgl Resign":"", "Aksi"].filter(Boolean).map(h=>(
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.length === 0 && (
                <tr><td colSpan={7} style={{...td, textAlign:"center", color:"#a0aec0", padding:32}}>Tidak ada data</td></tr>
              )}
              {data.map((r,i) => (
                <tr key={r.id} style={{background: i%2===0?"#fff":"#f7fafc"}}>
                  <td style={{...td, color:"#a0aec0"}}>{i+1}</td>
                  <td style={{...td, fontWeight:600}}>{r.nama}</td>
                  <td style={td}><span style={{background:"#ebf8ff",color:"#2b6cb0",padding:"2px 8px",borderRadius:20,fontSize:14,fontWeight:700}}>{r.cabang}</span></td>
                  <td style={{...td, color:"#718096"}}>{r.jabatan || "—"}</td>
                  <td style={td}>{r.tgl_masuk ? new Date(r.tgl_masuk+"T00:00:00").toLocaleDateString("id-ID",{day:"numeric",month:"short",year:"numeric"}) : "—"}</td>
                  {tab==="resign" && <td style={td}>{r.tgl_resign ? new Date(r.tgl_resign+"T00:00:00").toLocaleDateString("id-ID",{day:"numeric",month:"short",year:"numeric"}) : "—"}</td>}
                  <td style={td}>
                    <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
                      {tab==="aktif"
                        ? <button onClick={()=>{setResignModal(r); setTglResign("");}}
                            style={{background:"#fff5f5",color:"#e53e3e",border:"1px solid #fed7d7",padding:"3px 12px",borderRadius:6,fontSize:13,fontWeight:600,cursor:"pointer"}}>
                            Resign
                          </button>
                        : <button onClick={()=>handleAktifkan(r.id)}
                            style={{background:"#f0fff4",color:"#276749",border:"1px solid #9ae6b4",padding:"3px 12px",borderRadius:6,fontSize:13,fontWeight:600,cursor:"pointer"}}>
                            Aktifkan
                          </button>
                      }
                      <button onClick={()=>{ setPinModal(r); setNewPinVal(''); setPinMsg(null); }}
                        style={{background:"#faf5ff",color:"#6b46c1",border:"1px solid #d6bcfa",padding:"3px 12px",borderRadius:6,fontSize:13,fontWeight:600,cursor:"pointer"}}>
                        🔑 PIN
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Tambah */}
      {tambahModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#fff",borderRadius:12,padding:28,width:360,boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}}>
            <h3 style={{margin:"0 0 18px",fontSize:16,fontWeight:700}}>+ Tambah Karyawan</h3>
            {[["Nama","text","nama"],["Jabatan","text","jabatan"],["Tgl Masuk","date","tgl_masuk"]].map(([label,type,key])=>(
              <div key={key} style={{marginBottom:12}}>
                <label style={{fontSize:14,fontWeight:600,color:"#4a5568",display:"block",marginBottom:4}}>{label}</label>
                <input type={type} value={formTambah[key]||""} onChange={e=>setFormTambah(p=>({...p,[key]:e.target.value}))}
                  style={{width:"100%",border:"1px solid #d1d5db",borderRadius:6,padding:"7px 10px",fontSize:14,boxSizing:"border-box"}}/>
              </div>
            ))}
            <div style={{marginBottom:12}}>
              <label style={{fontSize:14,fontWeight:600,color:"#4a5568",display:"block",marginBottom:4}}>Cabang</label>
              <select value={formTambah.cabang} onChange={e=>setFormTambah(p=>({...p,cabang:e.target.value}))}
                style={{width:"100%",border:"1px solid #d1d5db",borderRadius:6,padding:"7px 10px",fontSize:14}}>
                {CABANG_LIST.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{display:"flex",gap:10,marginTop:20,justifyContent:"flex-end"}}>
              <button onClick={()=>setTambahModal(false)} style={{padding:"8px 18px",borderRadius:7,border:"1px solid #d1d5db",background:"#f8fafc",fontSize:14,cursor:"pointer"}}>Batal</button>
              <button onClick={handleTambah} disabled={saving} style={{...styles.btnPrimary, padding:"8px 18px"}}>
                {saving?"Menyimpan...":"Simpan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Reset PIN */}
      {pinModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#fff",borderRadius:12,padding:28,width:340,boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}}>
            <h3 style={{margin:"0 0 6px",fontSize:16,fontWeight:700}}>🔑 Reset PIN Karyawan</h3>
            <p style={{fontSize:14,color:"#4a5568",margin:"0 0 18px"}}><b>{pinModal.nama}</b> — {pinModal.cabang}</p>
            <div style={{marginBottom:16}}>
              <label style={{fontSize:12,fontWeight:600,color:"#718096",display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.4}}>PIN Baru (6 angka)</label>
              <input
                type="text" inputMode="numeric" maxLength={6}
                value={newPinVal}
                onChange={e => setNewPinVal(e.target.value.replace(/\D/g,'').slice(0,6))}
                placeholder="______"
                style={{width:"100%",border:"1.5px solid #d6bcfa",borderRadius:8,padding:"10px 14px",fontSize:22,fontWeight:700,textAlign:"center",letterSpacing:12,boxSizing:"border-box",color:"#6b46c1"}}
              />
              <div style={{fontSize:11,color:"#a0aec0",marginTop:4}}>Kosongkan untuk reset ke default: 123456</div>
            </div>
            {pinMsg && (
              <div style={{marginBottom:12,padding:"8px 12px",borderRadius:7,fontSize:12,fontWeight:500,
                background: pinMsg.type==='success'?'#f0fdf4':'#fef2f2',
                color: pinMsg.type==='success'?'#16a34a':'#dc2626',
                border: `1px solid ${pinMsg.type==='success'?'#bbf7d0':'#fecaca'}`}}>
                {pinMsg.text}
              </div>
            )}
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button onClick={()=>{setPinModal(null);setPinMsg(null);setNewPinVal('');}} style={{padding:"8px 18px",borderRadius:7,border:"1px solid #d1d5db",background:"#f8fafc",fontSize:14,cursor:"pointer"}}>Batal</button>
              <button onClick={()=>handleResetPin(pinModal.id)} disabled={saving}
                style={{padding:"8px 18px",borderRadius:7,border:"none",background:"#6b46c1",color:"#fff",fontSize:14,fontWeight:600,cursor:"pointer"}}>
                {saving?"Menyimpan...":"Reset PIN"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Resign */}
      {resignModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#fff",borderRadius:12,padding:28,width:340,boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}}>
            <h3 style={{margin:"0 0 8px",fontSize:16,fontWeight:700}}>🚪 Tandai Resign</h3>
            <p style={{fontSize:14,color:"#4a5568",margin:"0 0 16px"}}><b>{resignModal.nama}</b> — {resignModal.cabang}</p>
            <div style={{marginBottom:16}}>
              <label style={{fontSize:14,fontWeight:600,color:"#4a5568",display:"block",marginBottom:4}}>Tanggal Resign</label>
              <input type="date" value={tglResign} onChange={e=>setTglResign(e.target.value)}
                style={{width:"100%",border:"1px solid #d1d5db",borderRadius:6,padding:"7px 10px",fontSize:14,boxSizing:"border-box"}}/>
            </div>
            <p style={{fontSize:14,color:"#718096",margin:"0 0 16px"}}>Data absensi, kasbon, dan gaji karyawan ini tetap tersimpan.</p>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button onClick={()=>setResignModal(null)} style={{padding:"8px 18px",borderRadius:7,border:"1px solid #d1d5db",background:"#f8fafc",fontSize:14,cursor:"pointer"}}>Batal</button>
              <button onClick={()=>handleResign(resignModal.id)} disabled={saving}
                style={{padding:"8px 18px",borderRadius:7,border:"none",background:"#e53e3e",color:"#fff",fontSize:14,fontWeight:600,cursor:"pointer"}}>
                {saving?"...":"Tandai Resign"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const gajiInp = (wide) => ({
  border:"1px solid #d1d5db", borderRadius:5, padding:"3px 7px",
  fontSize:14, width: wide||70, textAlign:"right",
  MozAppearance:"textfield", appearance:"textfield",
});
const gajiInpText = { border:"1px solid #d1d5db", borderRadius:5, padding:"3px 7px", fontSize:14, width:160 };
const gajiTd = { padding:"5px 10px", borderBottom:"1px solid #f0f0f0", fontSize:12 };

const BULAN_SINGKAT = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agu","Sep","Okt","Nov","Des"];
function fmtTgl(val) {
  if (!val) return "";
  const [y, m, d] = val.split("-");
  if (!y || !m || !d) return val;
  return `${parseInt(d)} ${BULAN_SINGKAT[parseInt(m)-1]} ${y}`;
}
function GajiPotonganRow({ label, nominalKey, qtyKey, ketKey, form, onNum, onSet, editMode }) {
  const nom = parseFloat(form[nominalKey]) || 0;
  const qty = parseFloat(form[qtyKey]) || 0;
  const total = nom * qty;
  if (!editMode) {
    return (
      <tr>
        <td style={{...gajiTd, paddingLeft:28, color:"#374151"}}>{label}</td>
        <td style={{...gajiTd, textAlign:"right", color:"#374151", width:106}}>{nom ? formatRp(nom) : ""}</td>
        <td style={{...gajiTd, textAlign:"center", color:"#9ca3af", width:30}}>×</td>
        <td style={{...gajiTd, textAlign:"center", color:"#374151", width:61}}>{qty || ""}</td>
        <td style={{...gajiTd, textAlign:"right", color:"#374151", fontWeight:600}}>{total ? formatRp(total) : ""}</td>
        <td style={{...gajiTd, color:"#6b7280", fontSize:14}}>{form[ketKey] || ""}</td>
      </tr>
    );
  }
  return (
    <tr>
      <td style={{...gajiTd, paddingLeft:28, color:"#374151"}}>{label}</td>
      <td style={gajiTd}>
        <input type="text" inputMode="numeric" value={form[nominalKey]||""} onChange={e=>onNum(nominalKey,e.target.value)} style={gajiInp(90)} placeholder="0"/>
      </td>
      <td style={{...gajiTd, textAlign:"center", color:"#6b7280"}}>×</td>
      <td style={gajiTd}>
        <input type="text" inputMode="numeric" value={form[qtyKey]||""} onChange={e=>onNum(qtyKey,e.target.value)} style={gajiInp(45)} placeholder="0"/>
      </td>
      <td style={{...gajiTd, textAlign:"right", color:"#374151", fontWeight:600}}>
        {formatRp((parseFloat(form[nominalKey])||0)*(parseFloat(form[qtyKey])||0))}
      </td>
      <td style={gajiTd}>
        <input type="text" value={form[ketKey]||""} onChange={e=>onSet(ketKey,e.target.value)} style={gajiInpText} placeholder="Keterangan"/>
      </td>
    </tr>
  );
}

function GajiSummaryTab() {
  const CABANG_LIST = ["PO","PM","MT","DM","TM"];
  const CABANG_NAMA = {PO:"Puri Orchard",PM:"Palm Mansion",MT:"Metro",DM:"Daan Mogot City",TM:"Tomang"};
  const now = new Date();
  const [bulan, setBulan] = useState(now.getMonth() + 1);
  const [tahun, setTahun] = useState(now.getFullYear());
  const [jenis, setJenis] = useState("tengah");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ket, setKet] = useState({});
  const [editingKet, setEditingKet] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [savingKet, setSavingKet] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/gaji/summary", { params: { bulan, tahun } }),
      api.get("/gaji/keterangan-transfer", { params: { bulan, tahun } }),
    ])
      .then(([gajiRes, ketRes]) => {
        setData(gajiRes.data.data || []);
        setKet(ketRes.data.data || {});
      })
      .catch(() => { setData([]); setKet({}); })
      .finally(() => setLoading(false));
  }, [bulan, tahun]);

  const updateKet = async (kk, val) => {
    const [cabang, nama_karyawan] = kk.split('|');
    setSavingKet(true);
    try {
      await api.put("/gaji/keterangan-transfer", { cabang, nama_karyawan, jenis, bulan, tahun, keterangan: val });
      setKet(p => ({ ...p, [kk]: val }));
    } finally {
      setSavingKet(false);
    }
  };

  const grouped = {};
  CABANG_LIST.forEach(c => { grouped[c] = []; });
  data.forEach(row => {
    const val = jenis === "tengah" ? row.tengah_terima : jenis === "akhir" ? row.akhir_terima : row.bonus_terima;
    if (val > 0 && grouped[row.cabang] !== undefined) {
      grouped[row.cabang].push(row);
    }
  });

  const thS = {padding:"8px 12px", fontWeight:600, fontSize:13, background:"#f1f5f9", textAlign:"left", whiteSpace:"nowrap"};
  const tdS = {padding:"8px 12px", fontSize:13, borderBottom:"1px solid #f1f5f9"};

  const grandTotal = CABANG_LIST.reduce((s, c) =>
    s + (grouped[c] || []).reduce((ss, r) => ss + (jenis==="tengah" ? r.tengah_terima : jenis==="akhir" ? r.akhir_terima : r.bonus_terima), 0), 0);

  return (
    <div>
      <div style={{...styles.card, display:"flex", gap:16, alignItems:"flex-end", flexWrap:"wrap", marginBottom:16}}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Tahun</label>
          <select style={styles.select} value={tahun} onChange={e=>setTahun(+e.target.value)}>
            {[2024,2025,2026,2027].map(y=><option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Bulan</label>
          <select style={styles.select} value={bulan} onChange={e=>setBulan(+e.target.value)}>
            {Object.entries(BULAN_NAMA).map(([k,v])=><option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Jenis Gaji</label>
          <select style={styles.select} value={jenis} onChange={e=>setJenis(e.target.value)}>
            <option value="tengah">Gaji Tengah Bulan</option>
            <option value="akhir">Gaji Akhir Bulan</option>
            <option value="bonus">Bonus & Lembur</option>
          </select>
        </div>
      </div>

      {loading && <div style={{textAlign:"center", padding:40, color:"#a0aec0"}}>Memuat...</div>}

      {!loading && CABANG_LIST.map(cab => {
        const rows = grouped[cab] || [];
        if (!rows.length) return null;
        const total = rows.reduce((s, r) => s + (jenis==="tengah" ? r.tengah_terima : jenis==="akhir" ? r.akhir_terima : r.bonus_terima), 0);
        return (
          <div key={cab} style={{...styles.card, marginBottom:14}}>
            <div style={{fontWeight:700, fontSize:15, color:"#1e293b", marginBottom:10, paddingBottom:8, borderBottom:"2px solid #e2e8f0"}}>
              {cab} — {CABANG_NAMA[cab]}
            </div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%", borderCollapse:"collapse"}}>
                <thead>
                  <tr>
                    <th style={{...thS, width:36, textAlign:"center"}}>No</th>
                    <th style={thS}>Nama Karyawan</th>
                    <th style={{...thS, textAlign:"right"}}>{jenis==="tengah"?"Terima Gaji Tengah":jenis==="akhir"?"Terima Gaji Akhir":"Bonus & Lembur"}</th>
                    <th style={{...thS, minWidth:200}}>Keterangan Transfer</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const val = jenis==="tengah" ? r.tengah_terima : jenis==="akhir" ? r.akhir_terima : r.bonus_terima;
                    const kk = `${r.cabang}|${r.nama_karyawan}`;
                    return (
                      <tr key={r.nama_karyawan} style={{background:i%2===0?"#fff":"#f8fafc"}}>
                        <td style={{...tdS, textAlign:"center", color:"#94a3b8"}}>{i+1}</td>
                        <td style={{...tdS, fontWeight:500}}>{r.nama_karyawan}</td>
                        <td style={{...tdS, textAlign:"right", color:"#16a34a", fontWeight:700}}>{formatRp(val)}</td>
                        <td style={tdS}>
                          {editingKet === kk ? (
                            <div style={{display:"flex", gap:6, alignItems:"center"}}>
                              <input type="text" value={editVal} onChange={e=>setEditVal(e.target.value)}
                                autoFocus
                                placeholder="Contoh: BCA 1234567890"
                                style={{flex:1, border:"1px solid #93c5fd", borderRadius:6, padding:"5px 8px", fontSize:13, color:"#374151", outline:"none"}}/>
                              <button onClick={async ()=>{ await updateKet(kk, editVal); setEditingKet(null); }}
                                disabled={savingKet}
                                style={{padding:"4px 10px", background:"#16a34a", color:"#fff", border:"none", borderRadius:6, fontSize:12, fontWeight:600, cursor:"pointer", whiteSpace:"nowrap", opacity:savingKet?0.6:1}}>
                                {savingKet ? "..." : "💾 Simpan"}
                              </button>
                              <button onClick={()=>setEditingKet(null)}
                                style={{padding:"4px 8px", background:"#fff", color:"#64748b", border:"1px solid #d1d5db", borderRadius:6, fontSize:12, cursor:"pointer"}}>Batal</button>
                            </div>
                          ) : (
                            <div style={{display:"flex", gap:8, alignItems:"center"}}>
                              <span style={{flex:1, fontSize:13, color: ket[kk] ? "#374151" : "#cbd5e0"}}>
                                {ket[kk] || "—"}
                              </span>
                              <button onClick={()=>{ setEditingKet(kk); setEditVal(ket[kk]||""); }}
                                style={{padding:"3px 10px", background:"#eff6ff", color:"#2563eb", border:"1px solid #bfdbfe", borderRadius:6, fontSize:12, fontWeight:600, cursor:"pointer", whiteSpace:"nowrap"}}>✏️ Edit</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{background:"#eff6ff", fontWeight:700}}>
                    <td colSpan={2} style={{...tdS, textAlign:"right", color:"#1e293b", borderBottom:"none"}}>Total {cab}</td>
                    <td style={{...tdS, textAlign:"right", color:"#2563eb", borderBottom:"none"}}>{formatRp(total)}</td>
                    <td style={{...tdS, borderBottom:"none"}}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        );
      })}

      {!loading && CABANG_LIST.every(c => !(grouped[c]||[]).length) && (
        <div style={{...styles.card, textAlign:"center", padding:48, color:"#a0aec0"}}>
          Tidak ada data {jenis==="tengah"?"gaji tengah":jenis==="akhir"?"gaji akhir":"bonus & lembur"} untuk {BULAN_NAMA[bulan]} {tahun}
        </div>
      )}

      {!loading && grandTotal > 0 && (
        <div style={{...styles.card, display:"flex", justifyContent:"space-between", alignItems:"center", background:"#1e293b", color:"#fff"}}>
          <span style={{fontWeight:700, fontSize:15}}>Grand Total — Semua Cabang</span>
          <span style={{fontWeight:800, fontSize:18, color:"#86efac"}}>{formatRp(grandTotal)}</span>
        </div>
      )}
    </div>
  );
}

function GajiPage() {
  const CABANG_LIST = ["PO","PM","MT","DM","TM"];
  const now = new Date();
  const [cabang, setCabang] = useState("PO");
  const [bulan,  setBulan]  = useState(now.getMonth() + 1);
  const [tahun,  setTahun]  = useState(now.getFullYear());
  const [nama,   setNama]   = useState("");
  const [namaList, setNamaList] = useState([]);
  const [saving,   setSaving]   = useState(false);
  const [msg,      setMsg]      = useState(null);
  const [editSec,  setEditSec]  = useState({ tengah:false, akhir:false, bonus:false });
  const [savedForm, setSavedForm] = useState(null);
  const [activeTab, setActiveTab] = useState("input");

  const emptyForm = {
    tengah_gaji:0, tengah_gaji_qty:1, tengah_gaji_ket:"",
    tengah_pot1_nominal:0, tengah_pot1_qty:0, tengah_pot1_ket:"",
    tengah_pot2_nominal:0, tengah_pot2_qty:0, tengah_pot2_ket:"",
    tengah_pot3_nominal:0, tengah_pot3_qty:0, tengah_pot3_ket:"",
    tengah_tgl:"",
    akhir_gaji:0, akhir_gaji_qty:1, akhir_gaji_ket:"",
    akhir_pot1_nominal:0, akhir_pot1_qty:0, akhir_pot1_ket:"",
    akhir_pot2_nominal:0, akhir_pot2_qty:0, akhir_pot2_ket:"",
    akhir_pot3_nominal:0, akhir_pot3_qty:0, akhir_pot3_ket:"",
    akhir_tgl:"",
    bonus_nominal:0, bonus_nominal_qty:1, bonus_nominal_ket:"",
    lembur_rate:0, lembur_qty:0, lembur_ket:"",
    longshift_rate:0, longshift_qty:0, longshift_ket:"",
    bonus_sepatu:0, bonus_sepatu_qty:1, bonus_sepatu_ket:"",
    bonus_pot1_nominal:0, bonus_pot1_qty:0, bonus_pot1_ket:"",
    bonus_pot2_nominal:0, bonus_pot2_qty:0, bonus_pot2_ket:"",
    bonus_tgl:"",
  };
  const [form, setForm] = useState(emptyForm);
  const onSet = (f, v) => setForm(p => ({...p, [f]: v}));
  const onNum = (f, v) => onSet(f, v);

  // Computed totals
  const n = (k) => parseFloat(form[k])||0;
  const tengah_terima = n("tengah_gaji") * n("tengah_gaji_qty")
    - n("tengah_pot1_nominal") * n("tengah_pot1_qty")
    - n("tengah_pot2_nominal") * n("tengah_pot2_qty")
    - n("tengah_pot3_nominal") * n("tengah_pot3_qty");
  const akhir_terima = n("akhir_gaji") * n("akhir_gaji_qty")
    - n("akhir_pot1_nominal") * n("akhir_pot1_qty")
    - n("akhir_pot2_nominal") * n("akhir_pot2_qty")
    - n("akhir_pot3_nominal") * n("akhir_pot3_qty");
  const bonus_terima = n("bonus_nominal") * n("bonus_nominal_qty")
    + n("lembur_rate") * n("lembur_qty")
    + n("longshift_rate") * n("longshift_qty")
    + n("bonus_sepatu") * n("bonus_sepatu_qty")
    - n("bonus_pot1_nominal") * n("bonus_pot1_qty")
    - n("bonus_pot2_nominal") * n("bonus_pot2_qty");

  const resetEdit = () => setEditSec({ tengah:false, akhir:false, bonus:false });

  // Load nama list saat cabang berubah
  useEffect(() => {
    api.get("/gaji/nama-list", { params: { cabang } })
      .then(r => { setNamaList(r.data.data || []); setNama(""); setForm(emptyForm); setSavedForm(null); resetEdit(); })
      .catch(() => {});
  }, [cabang]);

  // Load data saat nama/bulan/tahun lengkap
  useEffect(() => {
    if (!nama) { setForm(emptyForm); setSavedForm(null); resetEdit(); return; }
    api.get("/gaji/data", { params: { cabang, nama_karyawan: nama, bulan, tahun } })
      .then(r => { const d = r.data.data ? r.data.data : emptyForm; setForm(d); setSavedForm(d); resetEdit(); })
      .catch(() => {});
  }, [cabang, nama, bulan, tahun]);

  const numFields = [
    "tengah_gaji","tengah_gaji_qty","tengah_pot1_nominal","tengah_pot1_qty",
    "tengah_pot2_nominal","tengah_pot2_qty","tengah_pot3_nominal","tengah_pot3_qty",
    "akhir_gaji","akhir_gaji_qty","akhir_pot1_nominal","akhir_pot1_qty",
    "akhir_pot2_nominal","akhir_pot2_qty","akhir_pot3_nominal","akhir_pot3_qty",
    "bonus_nominal","bonus_nominal_qty","lembur_rate","lembur_qty",
    "longshift_rate","longshift_qty","bonus_pot1_nominal","bonus_pot1_qty",
    "bonus_pot2_nominal","bonus_pot2_qty",
  ];
  const simpanSec = (sec) => {
    setSaving(true); setMsg(null);
    const payload = {...form};
    numFields.forEach(f => { payload[f] = parseFloat(payload[f]) || 0; });
    api.post("/gaji/simpan", { cabang, nama_karyawan: nama, bulan, tahun, ...payload })
      .then(() => {
        setMsg({ok:true, text:"Tersimpan"});
        if (!namaList.includes(nama)) setNamaList(p => [...p, nama].sort());
        setSavedForm({...payload});
        setEditSec(p => ({...p, [sec]: false}));
      })
      .catch(e => setMsg({ok:false, text: e.response?.data?.detail || "Gagal menyimpan"}))
      .finally(() => setSaving(false));
  };
  const batalSec = (sec) => {
    if (savedForm) setForm({...savedForm});
    setEditSec(p => ({...p, [sec]: false}));
    setMsg(null);
  };

  const thS = { padding:"6px 10px", fontWeight:600, fontSize:14, background:"#f1f5f9", textAlign:"left" };
  const secHdr = (bg) => ({ background:bg, fontWeight:700, fontSize:14, padding:"8px 10px", color:"#1e293b" });
  const totalRow = { background:"#f0fdf4", fontWeight:700, fontSize:13 };
  const btnEd = { fontSize:14, padding:"2px 10px", borderRadius:4, border:"1px solid #93c5fd", background:"#eff6ff", color:"#1d4ed8", cursor:"pointer", fontWeight:600 };
  const btnSv = { fontSize:14, padding:"2px 10px", borderRadius:4, border:"none", background:"#16a34a", color:"#fff", cursor:"pointer", fontWeight:600, marginRight:4 };
  const btnCx = { fontSize:14, padding:"2px 10px", borderRadius:4, border:"1px solid #d1d5db", background:"#fff", color:"#374151", cursor:"pointer" };
  const secActions = (sec) => editSec[sec]
    ? <><button onClick={() => simpanSec(sec)} disabled={saving} style={btnSv}>{saving ? "..." : "💾 Simpan"}</button><button onClick={() => batalSec(sec)} style={btnCx}>Batal</button></>
    : <button onClick={() => setEditSec(p => ({...p, [sec]: true}))} style={btnEd}>✏️ Edit</button>;
  const gpr = (sec, label, nominalKey, qtyKey, ketKey) =>
    <GajiPotonganRow key={nominalKey} label={label} nominalKey={nominalKey} qtyKey={qtyKey} ketKey={ketKey} form={form} onNum={onNum} onSet={onSet} editMode={editSec[sec]} />;
  const tglInp = (key) => <input type="date" value={form[key]||""} onChange={e=>onSet(key,e.target.value)} style={{border:"1px solid #d1d5db", borderRadius:5, padding:"2px 6px", fontSize:14, width:130}}/>;
  const tglTxt = (key) => <span style={{fontSize:14, color:"#374151"}}>{fmtTgl(form[key])}</span>;

  return (
    <div style={styles.page}>
      <h2 style={{...styles.pageTitle, marginBottom:12}}>💼 Gaji Karyawan</h2>

      {/* Tabs */}
      <div style={{display:"flex", borderBottom:"2px solid #e2e8f0", marginBottom:20, gap:0}}>
        {[["input","📝 Input Gaji"],["summary","📊 Summary"]].map(([tab,label])=>(
          <button key={tab} onClick={()=>setActiveTab(tab)}
            style={{padding:"9px 22px", border:"none", background:"none", cursor:"pointer",
              fontWeight:600, fontSize:14,
              color: activeTab===tab ? "#2563eb" : "#64748b",
              borderBottom: activeTab===tab ? "2px solid #2563eb" : "2px solid transparent",
              marginBottom:-2}}>
            {label}
          </button>
        ))}
      </div>

      {activeTab === "summary" && <GajiSummaryTab />}

      {activeTab === "input" && <>

      {/* Filter */}
      <div style={{...styles.card, display:"flex", gap:16, alignItems:"flex-end", flexWrap:"wrap", marginBottom:16}}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Tahun</label>
          <select style={styles.select} value={tahun} onChange={e=>setTahun(+e.target.value)}>
            {[2024,2025,2026,2027].map(y=><option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Bulan</label>
          <select style={styles.select} value={bulan} onChange={e=>setBulan(+e.target.value)}>
            {Object.entries(BULAN_NAMA).map(([k,v])=><option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Cabang</label>
          <select style={styles.select} value={cabang} onChange={e=>setCabang(e.target.value)}>
            {CABANG_LIST.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Nama Karyawan</label>
          <select style={{...styles.select, minWidth:160}} value={nama} onChange={e=>setNama(e.target.value)}>
            <option value="">— Pilih Nama —</option>
            {namaList.map(n=><option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      {/* Form Gaji */}
      {nama && (
        <div style={styles.card}>
          <div style={{fontWeight:700, fontSize:14, marginBottom:14, color:"#1e293b"}}>
            {nama} — {BULAN_NAMA[bulan]} {tahun} — {cabang}
          </div>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%", borderCollapse:"collapse", minWidth:580}}>
              <colgroup>
                <col style={{width:"22%"}}/>
                <col style={{width:"15%"}}/>
                <col style={{width:"4%"}}/>
                <col style={{width:"8%"}}/>
                <col style={{width:"15%"}}/>
                <col style={{width:"36%"}}/>
              </colgroup>
              <thead>
                <tr>
                  <th style={thS}>Komponen</th>
                  <th style={{...thS, textAlign:"right"}}>Nominal</th>
                  <th style={thS}></th>
                  <th style={{...thS, textAlign:"center"}}>Qty</th>
                  <th style={{...thS, textAlign:"right"}}>Total</th>
                  <th style={thS}>Keterangan</th>
                </tr>
              </thead>
              <tbody>
                {/* ── GAJI TENGAH BULAN ── */}
                <tr>
                  <td colSpan={6} style={secHdr("#dbeafe")}>Gaji Tengah Bulan <span style={{fontWeight:400}}>{secActions("tengah")}</span></td>
                </tr>
                {gpr("tengah","Gaji","tengah_gaji","tengah_gaji_qty","tengah_gaji_ket")}
                {gpr("tengah","Potongan 1","tengah_pot1_nominal","tengah_pot1_qty","tengah_pot1_ket")}
                {gpr("tengah","Potongan 2","tengah_pot2_nominal","tengah_pot2_qty","tengah_pot2_ket")}
                {gpr("tengah","Potongan 3","tengah_pot3_nominal","tengah_pot3_qty","tengah_pot3_ket")}
                <tr style={totalRow}>
                  <td colSpan={3} style={{...gajiTd, textAlign:"right", paddingRight:8, color:"#1e293b"}}>Terima Gaji Tengah</td>
                  <td style={gajiTd}></td>
                  <td style={{...gajiTd, textAlign:"right", color: tengah_terima>=0?"#16a34a":"#dc2626"}}>{formatRp(tengah_terima)}</td>
                  <td style={gajiTd}>{editSec.tengah ? tglInp("tengah_tgl") : tglTxt("tengah_tgl")}</td>
                </tr>

                {/* ── GAJI AKHIR BULAN ── */}
                <tr><td colSpan={6} style={{...secHdr("#dbeafe"), paddingTop:6}}></td></tr>
                <tr>
                  <td colSpan={6} style={secHdr("#bfdbfe")}>Gaji Akhir Bulan <span style={{fontWeight:400}}>{secActions("akhir")}</span></td>
                </tr>
                {gpr("akhir","Gaji","akhir_gaji","akhir_gaji_qty","akhir_gaji_ket")}
                {gpr("akhir","Potongan 1","akhir_pot1_nominal","akhir_pot1_qty","akhir_pot1_ket")}
                {gpr("akhir","Potongan 2","akhir_pot2_nominal","akhir_pot2_qty","akhir_pot2_ket")}
                {gpr("akhir","Potongan 3","akhir_pot3_nominal","akhir_pot3_qty","akhir_pot3_ket")}
                <tr style={totalRow}>
                  <td colSpan={3} style={{...gajiTd, textAlign:"right", paddingRight:8, color:"#1e293b"}}>Terima Gaji Akhir</td>
                  <td style={gajiTd}></td>
                  <td style={{...gajiTd, textAlign:"right", color: akhir_terima>=0?"#16a34a":"#dc2626"}}>{formatRp(akhir_terima)}</td>
                  <td style={gajiTd}>{editSec.akhir ? tglInp("akhir_tgl") : tglTxt("akhir_tgl")}</td>
                </tr>

                {/* ── BONUS DAN LEMBURAN ── */}
                <tr><td colSpan={6} style={{...secHdr("#dbeafe"), paddingTop:6}}></td></tr>
                <tr>
                  <td colSpan={6} style={secHdr("#fef9c3")}>Bonus dan Lemburan <span style={{fontWeight:400}}>{secActions("bonus")}</span></td>
                </tr>
                {gpr("bonus","Bonus","bonus_nominal","bonus_nominal_qty","bonus_nominal_ket")}
                {gpr("bonus","Lembur","lembur_rate","lembur_qty","lembur_ket")}
                {gpr("bonus","Longshift","longshift_rate","longshift_qty","longshift_ket")}
                {gpr("bonus","Bonus Sepatu","bonus_sepatu","bonus_sepatu_qty","bonus_sepatu_ket")}
                {gpr("bonus","Potongan 1","bonus_pot1_nominal","bonus_pot1_qty","bonus_pot1_ket")}
                {gpr("bonus","Potongan 2","bonus_pot2_nominal","bonus_pot2_qty","bonus_pot2_ket")}
                <tr style={totalRow}>
                  <td colSpan={3} style={{...gajiTd, textAlign:"right", paddingRight:8, color:"#1e293b"}}>Terima Bonus</td>
                  <td style={gajiTd}></td>
                  <td style={{...gajiTd, textAlign:"right", color: bonus_terima>=0?"#16a34a":"#dc2626"}}>{formatRp(bonus_terima)}</td>
                  <td style={gajiTd}>{editSec.bonus ? tglInp("bonus_tgl") : tglTxt("bonus_tgl")}</td>
                </tr>

              </tbody>
            </table>
          </div>

          {msg && (
            <div style={{marginTop:10, fontSize:14, color: msg.ok?"#16a34a":"#dc2626", fontWeight:600}}>
              {msg.ok ? "✓ " : "✗ "}{msg.text}
            </div>
          )}
        </div>
      )}

      {!nama && (
        <div style={{...styles.card, textAlign:"center", padding:48, color:"#a0aec0"}}>
          Pilih nama karyawan untuk melihat atau mengisi data gaji
        </div>
      )}

      </>}
    </div>
  );
}

// =====================
// LAPORAN KASIR PAGE
// =====================
function LaporanKasirPage() {
  const CABANG_LIST = ["PO", "PM", "MT", "DM", "TM"];
  const CABANG_NAMA = {PO:"Puri Orchard", PM:"Palm Mansion", MT:"Metro", DM:"Daan Mogot City", TM:"Tomang"};
  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];
  const firstOfMonth = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-01`;
  const [cabang, setCabang] = useState("PO");
  const [tglDari, setTglDari] = useState(firstOfMonth);
  const [tglSampai, setTglSampai] = useState(todayStr);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({total_tunai:0, total_qris:0, total_transfer:0, total:0, jumlah_transaksi:0});
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);

  const isRange = tglDari !== tglSampai;

  async function fetchData(cab, dari, sampai) {
    if (!dari || !sampai) return;
    setLoading(true);
    try {
      const res = await api.get("/kasir/laporan", { params: { cabang: cab, tgl_dari: dari, tgl_sampai: sampai } });
      setData(res.data.data || []);
      setSummary({
        total_tunai: res.data.total_tunai || 0,
        total_qris: res.data.total_qris || 0,
        total_transfer: res.data.total_transfer || 0,
        total: res.data.total || 0,
        jumlah_transaksi: res.data.jumlah_transaksi || 0,
      });
    } catch { setData([]); }
    finally { setLoading(false); }
  }

  async function handleDeleteKasir(id) {
    try {
      await api.delete("/kasir/transaksi/" + id);
      await fetchData(cabang, tglDari, tglSampai);
      setDeleteModal(null);
    } catch(e) {
      alert("Gagal hapus: " + (e.response?.data?.detail || e.message));
      setDeleteModal(null);
    }
  }

  useEffect(() => { fetchData(cabang, tglDari, tglSampai); }, [cabang, tglDari, tglSampai]);

  const th = {padding:"10px 12px", textAlign:"left", fontWeight:600, fontSize:14, whiteSpace:"nowrap"};
  const td = {padding:"8px 12px", borderBottom:"1px solid #e2e8f0", fontSize:14};

  const formatTgl = (d) => {
    if (!d) return "—";
    const dt = new Date(d+"T00:00:00");
    return dt.getDate() + " " + BULAN_NAMA[dt.getMonth()+1].slice(0,3) + " " + dt.getFullYear();
  };

  const colSpanKiri = isRange ? 4 : 3;
  const totalCols = isRange ? 10 : 9;

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20, flexWrap:"wrap", gap:12}}>
        <div>
          <h2 style={{...styles.pageTitle, marginBottom:2}}>Laporan Kasir</h2>
          <span style={{fontSize:14, color:"#6b7280"}}>{cabang} — {CABANG_NAMA[cabang]} · {isRange ? `${tglDari} s/d ${tglSampai}` : tglDari}</span>
        </div>
        <div style={{display:"flex", gap:8, alignItems:"flex-end", flexWrap:"wrap"}}>
          <div>
            <label style={{fontSize:14, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3, textTransform:"uppercase"}}>Cabang</label>
            <select style={{...styles.select, margin:0}} value={cabang} onChange={e=>setCabang(e.target.value)}>
              {CABANG_LIST.map(c=><option key={c} value={c}>{c} - {CABANG_NAMA[c]}</option>)}
            </select>
          </div>
          <div>
            <label style={{fontSize:14, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3, textTransform:"uppercase"}}>Dari</label>
            <input type="date" value={tglDari} onChange={e=>{
              setTglDari(e.target.value);
              if (e.target.value > tglSampai) setTglSampai(e.target.value);
            }} style={{...styles.select, margin:0, width:"auto"}}/>
          </div>
          <div>
            <label style={{fontSize:14, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3, textTransform:"uppercase"}}>Sampai</label>
            <input type="date" value={tglSampai} min={tglDari} onChange={e=>setTglSampai(e.target.value)}
              style={{...styles.select, margin:0, width:"auto"}}/>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{display:"flex", gap:12, marginBottom:16, flexWrap:"wrap"}}>
        {[
          ["Transaksi", summary.jumlah_transaksi, "#3182ce"],
          ["Tunai", formatRp(summary.total_tunai), "#38a169"],
          ["QRIS", formatRp(summary.total_qris), "#805ad5"],
          ["Transfer", formatRp(summary.total_transfer), "#e53e3e"],
          ["Total", formatRp(summary.total), "#c05621"],
        ].map(([lbl,val,clr])=>(
          <div key={lbl} style={{background:"#fff", borderRadius:10, padding:"14px 20px",
            boxShadow:"0 1px 4px rgba(0,0,0,0.08)", minWidth:140, borderTop:"3px solid "+clr}}>
            <div style={{fontSize:14, color:"#718096", marginBottom:4, textTransform:"uppercase", fontWeight:600}}>{lbl}</div>
            <div style={{fontSize:22, fontWeight:700, color:clr}}>{val}</div>
          </div>
        ))}
      </div>

      {/* Tabel */}
      <div style={{overflowX:"auto", borderRadius:10, boxShadow:"0 1px 4px rgba(0,0,0,0.08)"}}>
        <table style={{width:"100%", borderCollapse:"collapse", background:"#fff", fontSize:14}}>
          <thead>
            <tr style={{background:"#f8fafc"}}>
              {[
                {label:"No", w:40},
                {label:"Jam", w:80},
                ...(isRange ? [{label:"Tanggal", w:100}] : []),
                {label:"Nama Customer", w:"auto"},
                {label:"Tunai", w:120},
                {label:"QRIS", w:120},
                {label:"Transfer", w:120},
                {label:"Total", w:120},
                {label:"Input Oleh", w:140},
                {label:"Hapus", w:60},
              ].map(h=>(
                <th key={h.label} style={{...th, width:h.w, textAlign: ["Tunai","QRIS","Transfer","Total"].includes(h.label)?"right":"left"}}>{h.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={totalCols} style={{textAlign:"center",padding:40,color:"#a0aec0"}}>Memuat data...</td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={totalCols} style={{textAlign:"center",padding:40,color:"#a0aec0"}}>
                Tidak ada transaksi kasir untuk {CABANG_NAMA[cabang]}{isRange
                  ? ` periode ${formatTgl(tglDari)} – ${formatTgl(tglSampai)}`
                  : ` tanggal ${formatTgl(tglDari)}`}
              </td></tr>
            ) : data.map((r,i) => (
              <tr key={r.id} style={{background:i%2===0?"#fff":"#f7fafc", borderBottom:"1px solid #e2e8f0"}}>
                <td style={{...td, color:"#a0aec0"}}>{i+1}</td>
                <td style={{...td, whiteSpace:"nowrap", color:"#718096"}}>{r.created_at}</td>
                {isRange && <td style={{...td, whiteSpace:"nowrap", color:"#4a5568"}}>{formatTgl(r.tanggal)}</td>}
                <td style={{...td, fontWeight:600}}>{r.nama_customer}</td>
                <td style={{...td, textAlign:"right", color: r.tunai>0?"#38a169":"#a0aec0"}}>
                  {r.tunai > 0 ? formatRp(r.tunai) : "—"}
                </td>
                <td style={{...td, textAlign:"right", color: r.qris>0?"#805ad5":"#a0aec0"}}>
                  {r.qris > 0 ? formatRp(r.qris) : "—"}
                </td>
                <td style={{...td, textAlign:"right", color: r.transfer>0?"#e53e3e":"#a0aec0"}}>
                  {r.transfer > 0 ? formatRp(r.transfer) : "—"}
                </td>
                <td style={{...td, textAlign:"right", fontWeight:700, color:"#c05621"}}>{formatRp(r.total)}</td>
                <td style={{...td, fontSize:14, color:"#718096"}}>{r.input_by_name}</td>
                <td style={{...td, textAlign:"center"}}>
                  <button onClick={()=>setDeleteModal({id:r.id, label:`Hapus transaksi ${r.nama_customer} - ${formatRp(r.total)}?`})}
                    style={{background:"#fff5f5",color:"#e53e3e",border:"1px solid #fed7d7",padding:"3px 10px",borderRadius:6,fontSize:14,fontWeight:600,cursor:"pointer"}}>
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          {data.length > 0 && (
            <tfoot>
              <tr style={{background:"#ebf8ff"}}>
                <td colSpan={colSpanKiri} style={{...td, fontWeight:700}}>Total</td>
                <td style={{...td, textAlign:"right", fontWeight:700, color:"#38a169"}}>{formatRp(summary.total_tunai)}</td>
                <td style={{...td, textAlign:"right", fontWeight:700, color:"#805ad5"}}>{formatRp(summary.total_qris)}</td>
                <td style={{...td, textAlign:"right", fontWeight:700, color:"#e53e3e"}}>{formatRp(summary.total_transfer)}</td>
                <td style={{...td, textAlign:"right", fontWeight:700, color:"#c05621"}}>{formatRp(summary.total)}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {deleteModal && (
        <DeleteModal
          label={deleteModal.label}
          onConfirm={() => handleDeleteKasir(deleteModal.id)}
          onCancel={() => setDeleteModal(null)}
        />
      )}
    </div>
  );
}

// =====================
// STOK PAGE
// =====================
function StokPage() {
  const CABANG_LIST = ["PO","PM","MT","DM","TM"];
  const now = new Date();
  const [bulan, setBulan] = useState(now.getMonth() + 1);
  const [tahun, setTahun] = useState(now.getFullYear());
  const [barangs, setBarangs] = useState([]);
  const [pemakaian, setPemakaian] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalMasuk, setModalMasuk] = useState(false);
  const [masukStep, setMasukStep] = useState(1); // 1=pilih barang, 2=isi qty
  const [masukBarang, setMasukBarang] = useState(null);
  const [masukTgl, setMasukTgl] = useState(new Date().toISOString().slice(0,10));
  const [masukCatatan, setMasukCatatan] = useState('');
  const [masukItems, setMasukItems] = useState({});
  const [saving, setSaving] = useState(false);
  const [revisiModal, setRevisiModal] = useState(null); // {barang, varian|null}
  const [revisiDelta, setRevisiDelta] = useState('');
  const [revisiAlasan, setRevisiAlasan] = useState('');
  const [savingRevisi, setSavingRevisi] = useState(false);
  const [historyRevisiModal, setHistoryRevisiModal] = useState(false);
  const [historyRevisi, setHistoryRevisi] = useState([]);
  const [loadingRevisi, setLoadingRevisi] = useState(false);
  const [historyKirimModal, setHistoryKirimModal] = useState(false);
  const [historyKirim, setHistoryKirim] = useState([]);
  const [loadingKirim, setLoadingKirim] = useState(false);
  const [editKirim, setEditKirim] = useState(null);
  const [editKirimItems, setEditKirimItems] = useState([]);
  const [editKirimSaving, setEditKirimSaving] = useState(false);
  const [barangMasterStok, setBarangMasterStok] = useState([]);
  const [historyMasukModal, setHistoryMasukModal] = useState(false);
  const [historyMasuk, setHistoryMasuk] = useState([]);
  const [loadingMasuk, setLoadingMasuk] = useState(false);
  const [pemakaianTab, setPemakaianTab] = useState('PO');
  const currentUser = localStorage.getItem('fullname') || '';
  const isRonald = currentUser.toLowerCase().includes('ronald');

  async function load() {
    setLoading(true);
    try {
      const params = { bulan, tahun };
      const [b, p] = await Promise.all([
        api.get('/stok/barang', { params }),
        api.get('/stok/pemakaian', { params }),
      ]);
      setBarangs(b.data || []);
      setPemakaian(p.data || []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [bulan, tahun]);

  function openMasuk() {
    setMasukStep(1); setMasukBarang(null); setMasukItems({});
    setMasukTgl(new Date().toISOString().slice(0,10)); setMasukCatatan('');
    setModalMasuk(true);
  }

  function pilihBarang(b) {
    setMasukBarang(b);
    setMasukItems({});
    setMasukStep(2);
  }

  function setQty(key, val) { setMasukItems(p => ({...p, [key]: val})); }

  async function simpanMasuk() {
    if (!masukBarang) return;
    const items = masukBarang.has_varian
      ? masukBarang.varians
          .filter(v => parseInt(masukItems[v.id] || 0) > 0)
          .map(v => ({ barang_id: masukBarang.id, varian_id: v.id, qty: parseInt(masukItems[v.id]) }))
      : [{ barang_id: masukBarang.id, varian_id: null, qty: parseInt(masukItems['_'] || 0) }];
    if (!items.length || items.every(i => i.qty <= 0)) { alert('Isi qty minimal satu item'); return; }
    setSaving(true);
    try {
      await api.post('/stok/masuk', { tanggal: masukTgl, catatan: masukCatatan, items });
      setModalMasuk(false); load();
    } catch(e) { alert('Gagal simpan: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  }

  async function simpanRevisi() {
    if (!revisiAlasan.trim()) { alert('Alasan wajib diisi'); return; }
    const delta = parseFloat(revisiDelta);
    if (!delta) { alert('Masukkan selisih qty (+ atau -)'); return; }
    setSavingRevisi(true);
    try {
      await api.post('/stok/revisi', {
        barang_id: revisiModal.barang.id,
        varian_id: revisiModal.varian?.id || null,
        qty_delta: delta,
        alasan: revisiAlasan,
      });
      setRevisiModal(null); setRevisiDelta(''); setRevisiAlasan(''); load();
    } catch(e) { alert('Gagal: ' + (e.response?.data?.detail || e.message)); }
    finally { setSavingRevisi(false); }
  }

  async function openHistoryMasuk() {
    setHistoryMasukModal(true);
    setLoadingMasuk(true);
    try {
      const r = await api.get('/stok/masuk/history');
      setHistoryMasuk(r.data || []);
    } catch(e) { setHistoryMasuk([]); }
    finally { setLoadingMasuk(false); }
  }

  async function openHistoryKirim() {
    setHistoryKirimModal(true);
    setLoadingKirim(true);
    try {
      const r = await api.get('/stok/kirim/all', { params: { bulan, tahun } });
      setHistoryKirim(r.data || []);
    } catch(e) { setHistoryKirim([]); }
    finally { setLoadingKirim(false); }
  }

  async function openEditKirim(r) {
    if (!barangMasterStok.length) {
      const res = await api.get('/stok/barang');
      setBarangMasterStok(res.data || []);
    }
    const items = typeof r.items === 'string' ? JSON.parse(r.items) : r.items;
    setEditKirimItems(items.map(it => ({
      barang_id: it.barang_id, varian_id: it.varian_id || null,
      barang_nama: it.barang, varian_nama: it.varian || null,
      satuan: it.satuan, qty: it.qty,
    })));
    setEditKirim(r);
  }

  async function simpanEditKirim() {
    if (!editKirimItems.length) return alert('Minimal 1 barang');
    if (editKirimItems.some(i => !i.qty || i.qty <= 0)) return alert('Qty harus lebih dari 0');
    setEditKirimSaving(true);
    try {
      await api.put(`/stok/kirim/${editKirim.id}`, { items: editKirimItems });
      const r = await api.get('/stok/kirim/all', { params: { bulan, tahun } });
      setHistoryKirim(r.data || []);
      setEditKirim(null);
      load();
    } catch(e) { alert('Gagal menyimpan: ' + (e.response?.data?.detail || e.message)); }
    finally { setEditKirimSaving(false); }
  }

  async function hapusKirim(id) {
    if (!window.confirm('Hapus transaksi ini? Stok akan dikembalikan.')) return;
    try {
      await api.delete(`/stok/kirim/${id}`);
      setHistoryKirim(p => p.filter(r => r.id !== id));
      load();
    } catch(e) { alert('Gagal hapus: ' + (e.response?.data?.detail || e.message)); }
  }

  async function openHistoryRevisi() {
    setHistoryRevisiModal(true);
    setLoadingRevisi(true);
    try {
      const r = await api.get('/stok/revisi/history');
      setHistoryRevisi(r.data || []);
    } catch(e) { setHistoryRevisi([]); }
    finally { setLoadingRevisi(false); }
  }

  const cardStyle = { background:'#fff', borderRadius:12, boxShadow:'0 1px 4px rgba(0,0,0,0.08)', padding:'16px 18px', marginBottom:12 };
  const labelStyle = { fontSize:10, fontWeight:700, color:'#a0aec0', letterSpacing:1, textTransform:'uppercase' };
  const saldoColor = s => s <= 0 ? '#e53e3e' : s < 5 ? '#dd6b20' : '#276749';

  // pemakaian pivot: per cabang, per barang, per hari
  // pemRows[cabang][key] = { satuan, days: {hari: qty}, total }
  const pemRows = {};
  const pemHaris = {}; // pemHaris[cabang] = sorted unique days
  pemakaian.forEach(r => {
    const cab = r.cabang;
    const key = r.varian ? `${r.barang} (${r.varian})` : r.barang;
    const qty = Number(r.qty) || 0;
    if (!pemRows[cab]) pemRows[cab] = {};
    if (!pemRows[cab][key]) pemRows[cab][key] = { satuan: r.satuan, days: {}, total: 0 };
    pemRows[cab][key].days[r.hari] = (pemRows[cab][key].days[r.hari] || 0) + qty;
    pemRows[cab][key].total += qty;
    if (!pemHaris[cab]) pemHaris[cab] = new Set();
    pemHaris[cab].add(r.hari);
  });
  const pemDays = pemHaris[pemakaianTab] ? [...pemHaris[pemakaianTab]].sort((a,b)=>a-b) : [];
  const pemKeys = Object.keys(pemRows[pemakaianTab] || {}).sort();

  return (
    <div style={{padding:'20px 16px', maxWidth:900, margin:'0 auto'}}>
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12}}>
        <div style={{display:'flex', alignItems:'center', gap:10}}>
          <h2 style={{margin:0, fontSize:22, fontWeight:700, color:'#1a202c'}}>📦 Stok Gudang</h2>
          <select value={bulan} onChange={e=>setBulan(Number(e.target.value))}
            style={{padding:'5px 10px',borderRadius:8,border:'1px solid #cbd5e0',fontSize:14,color:'#2d3748'}}>
            {[1,2,3,4,5,6,7,8,9,10,11,12].map(m=>(
              <option key={m} value={m}>{['Jan','Feb','Mar','Apr','Mei','Jun','Jul','Agu','Sep','Okt','Nov','Des'][m-1]}</option>
            ))}
          </select>
          <select value={tahun} onChange={e=>setTahun(Number(e.target.value))}
            style={{padding:'5px 10px',borderRadius:8,border:'1px solid #cbd5e0',fontSize:14,color:'#2d3748'}}>
            {[2024,2025,2026,2027].map(y=><option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          {isRonald && (
            <button onClick={openHistoryMasuk}
              style={{background:'#fff',color:'#4a5568',border:'1px solid #cbd5e0',padding:'8px 14px',borderRadius:8,fontWeight:600,fontSize:14,cursor:'pointer'}}>
              📥 History Masuk
            </button>
          )}
          {isRonald && (
            <button onClick={openHistoryKirim}
              style={{background:'#fff',color:'#4a5568',border:'1px solid #cbd5e0',padding:'8px 14px',borderRadius:8,fontWeight:600,fontSize:14,cursor:'pointer'}}>
              🚚 Transaksi Kirim
            </button>
          )}
          {isRonald && (
            <button onClick={openHistoryRevisi}
              style={{background:'#fff',color:'#4a5568',border:'1px solid #cbd5e0',padding:'8px 14px',borderRadius:8,fontWeight:600,fontSize:14,cursor:'pointer'}}>
              📋 History Revisi
            </button>
          )}
          {isRonald && (
            <button onClick={openMasuk}
              style={{background:'#2b6cb0',color:'#fff',border:'none',padding:'8px 18px',borderRadius:8,fontWeight:600,fontSize:14,cursor:'pointer'}}>
              + Masuk Stok
            </button>
          )}
        </div>
      </div>
      <div style={{display:'flex',alignItems:'center',gap:10,background:'#ebf8ff',border:'1px solid #bee3f8',borderRadius:10,padding:'10px 14px',marginBottom:16}}>
        <span style={{fontSize:13,color:'#2b6cb0',flex:1,wordBreak:'break-all'}}>🔗 https://reportlaundry.mommywash.my.id/gudang/</span>
        <button onClick={()=>{
          navigator.clipboard.writeText('https://reportlaundry.mommywash.my.id/gudang/').catch(()=>{});
          const btn = document.getElementById('btn-copy-gudang-url');
          if(btn){btn.textContent='✅ Tersalin!'; setTimeout(()=>btn.textContent='📋 Copy',2000);}
        }} id="btn-copy-gudang-url"
          style={{flexShrink:0,padding:'6px 13px',background:'#2b6cb0',color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer',whiteSpace:'nowrap'}}>
          📋 Copy
        </button>
      </div>

      {loading ?<div style={{color:'#718096',padding:32,textAlign:'center'}}>Memuat...</div> : (
        <>
          {/* STOK LIST */}
          {barangs.map(b => (
            <div key={b.id} style={cardStyle}>
              <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: b.has_varian ? 10 : 0}}>
                <span style={{fontWeight:700, fontSize:14, color:'#2d3748'}}>{b.nama}</span>
                {!b.has_varian && (
                  <div style={{display:'flex', alignItems:'center', gap:10}}>
                    <span style={{fontSize:22, fontWeight:700, color:saldoColor(b.saldo)}}>{b.saldo % 1 === 0 ? b.saldo : b.saldo.toFixed(2)}</span>
                    <span style={{fontSize:14, color:'#718096'}}>{b.satuan}</span>
                    {isRonald && <button onClick={()=>{setRevisiModal({barang:b,varian:null});setRevisiDelta('');setRevisiAlasan('');}}
                      style={{fontSize:14,padding:'3px 8px',borderRadius:6,border:'1px solid #cbd5e0',background:'#f7fafc',cursor:'pointer',color:'#4a5568'}}>✏️ Revisi</button>}
                  </div>
                )}
              </div>
              {b.has_varian && (
                <table style={{width:'100%', borderCollapse:'collapse'}}>
                  <tbody>
                    {b.varians.map(v => (
                      <tr key={v.id} style={{borderTop:'1px solid #edf2f7'}}>
                        <td style={{padding:'7px 0', fontSize:14, color:'#4a5568', width:'40%'}}>{v.nama}</td>
                        <td style={{textAlign:'right', fontWeight:600, fontSize:14, color:saldoColor(v.saldo)}}>
                          {v.saldo % 1 === 0 ? v.saldo : v.saldo.toFixed(2)}
                        </td>
                        <td style={{paddingLeft:6, fontSize:14, color:'#718096', width:40}}>{b.satuan}</td>
                        <td style={{textAlign:'right', width:70}}>
                          {isRonald && <button onClick={()=>{setRevisiModal({barang:b,varian:v});setRevisiDelta('');setRevisiAlasan('');}}
                            style={{fontSize:14,padding:'3px 8px',borderRadius:6,border:'1px solid #cbd5e0',background:'#f7fafc',cursor:'pointer',color:'#4a5568'}}>✏️ Revisi</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}

          {/* PEMAKAIAN PER CABANG */}
          <div style={{...cardStyle, marginTop:24}}>
            <div style={{...labelStyle, marginBottom:12}}>Pemakaian per Cabang</div>
            <div style={{display:'flex', gap:6, marginBottom:14, flexWrap:'wrap'}}>
              {CABANG_LIST.map(c => (
                <button key={c} onClick={()=>setPemakaianTab(c)}
                  style={{padding:'5px 14px', borderRadius:20, border:'none', cursor:'pointer', fontWeight:600, fontSize:14,
                    background: pemakaianTab===c ? '#2b6cb0' : '#edf2f7', color: pemakaianTab===c ? '#fff' : '#4a5568'}}>
                  {c}
                </button>
              ))}
            </div>
            {pemKeys.length === 0
              ? <div style={{color:'#a0aec0', fontSize:14}}>Belum ada pengiriman ke cabang {pemakaianTab}</div>
              : (
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%', borderCollapse:'collapse', fontSize:14}}>
                    <thead>
                      <tr style={{borderBottom:'2px solid #e2e8f0'}}>
                        <th style={{textAlign:'left', padding:'6px 8px 6px 0', color:'#718096', fontWeight:600, whiteSpace:'nowrap'}}>Barang</th>
                        {pemDays.map(d => (
                          <th key={d} style={{textAlign:'left', padding:'6px 10px', color:'#718096', fontWeight:600, minWidth:36, whiteSpace:'nowrap'}}>{d}</th>
                        ))}
                        <th style={{width:'100%'}}></th>
                        <th style={{textAlign:'right', padding:'6px 0 6px 16px', color:'#718096', fontWeight:600, whiteSpace:'nowrap'}}>Total</th>
                        <th style={{textAlign:'right', padding:'6px 0 6px 8px', color:'#718096', fontWeight:600, whiteSpace:'nowrap'}}>Satuan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pemKeys.map(k => {
                        const row = pemRows[pemakaianTab][k];
                        const fmt = v => v % 1 === 0 ? String(v) : v.toFixed(2).replace(/\.?0+$/,'');
                        return (
                          <tr key={k} style={{borderBottom:'1px solid #edf2f7'}}>
                            <td style={{padding:'7px 8px 7px 0', color:'#2d3748', whiteSpace:'nowrap'}}>{k}</td>
                            {pemDays.map(d => (
                              <td key={d} style={{padding:'7px 10px', color:'#2d3748', textAlign:'left', whiteSpace:'nowrap'}}>
                                {row.days[d] ? fmt(row.days[d]) : ''}
                              </td>
                            ))}
                            <td></td>
                            <td style={{padding:'7px 0 7px 16px', fontWeight:700, color:'#2b6cb0', textAlign:'right', whiteSpace:'nowrap'}}>
                              {fmt(row.total)}
                            </td>
                            <td style={{textAlign:'right', color:'#718096', padding:'7px 8px 7px 8px', whiteSpace:'nowrap'}}>{row.satuan}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            }
          </div>
        </>
      )}

      {/* MODAL MASUK STOK */}
      {modalMasuk && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div style={{background:'#fff',borderRadius:14,padding:24,width:'min(480px,95vw)',maxHeight:'85vh',overflowY:'auto',boxShadow:'0 8px 40px rgba(0,0,0,0.18)'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:18}}>
              <span style={{fontWeight:700,fontSize:16}}>
                {masukStep===1 ? '📦 Pilih Barang' : `📦 ${masukBarang?.nama}`}
              </span>
              <button onClick={()=>setModalMasuk(false)} style={{background:'none',border:'none',fontSize:22,cursor:'pointer',color:'#718096'}}>✕</button>
            </div>

            {masukStep === 1 && (
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {barangs.map(b => (
                  <button key={b.id} onClick={()=>pilihBarang(b)}
                    style={{textAlign:'left',padding:'12px 16px',borderRadius:10,border:'1px solid #e2e8f0',background:'#f7fafc',cursor:'pointer',fontSize:14,fontWeight:500,color:'#2d3748'}}>
                    {b.nama} <span style={{color:'#a0aec0',fontSize:14}}>({b.satuan})</span>
                  </button>
                ))}
              </div>
            )}

            {masukStep === 2 && masukBarang && (
              <>
                <div style={{marginBottom:14}}>
                  <label style={labelStyle}>Tanggal</label>
                  <input type="date" value={masukTgl} onChange={e=>setMasukTgl(e.target.value)}
                    style={{display:'block',width:'100%',padding:'8px 10px',borderRadius:8,border:'1px solid #cbd5e0',fontSize:14,marginTop:4,boxSizing:'border-box'}}/>
                </div>

                <div style={{marginBottom:14}}>
                  <label style={labelStyle}>Qty Masuk</label>
                  {masukBarang.has_varian ? (
                    <table style={{width:'100%',borderCollapse:'collapse',marginTop:6}}>
                      <tbody>
                        {masukBarang.varians.map(v => (
                          <tr key={v.id} style={{borderBottom:'1px solid #edf2f7'}}>
                            <td style={{padding:'8px 0',fontSize:14,color:'#4a5568'}}>{v.nama}</td>
                            <td style={{paddingLeft:8}}>
                              <input type="number" min="0" step="1" value={masukItems[v.id]||''} onChange={e=>setQty(v.id, e.target.value)}
                                placeholder="0"
                                style={{width:90,padding:'6px 8px',borderRadius:6,border:'1px solid #cbd5e0',fontSize:14,textAlign:'right'}}/>
                            </td>
                            <td style={{paddingLeft:6,fontSize:14,color:'#718096'}}>{masukBarang.satuan}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{display:'flex',alignItems:'center',gap:8,marginTop:6}}>
                      <input type="number" min="0" step="1" value={masukItems['_']||''} onChange={e=>setQty('_',e.target.value)}
                        placeholder="0"
                        style={{width:120,padding:'8px 10px',borderRadius:8,border:'1px solid #cbd5e0',fontSize:14}}/>
                      <span style={{color:'#718096'}}>{masukBarang.satuan}</span>
                    </div>
                  )}
                </div>

                <div style={{marginBottom:18}}>
                  <label style={labelStyle}>Catatan (opsional)</label>
                  <input value={masukCatatan} onChange={e=>setMasukCatatan(e.target.value)}
                    placeholder="Misal: dari supplier X"
                    style={{display:'block',width:'100%',padding:'8px 10px',borderRadius:8,border:'1px solid #cbd5e0',fontSize:14,marginTop:4,boxSizing:'border-box'}}/>
                </div>

                <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                  <button onClick={()=>setMasukStep(1)}
                    style={{padding:'8px 16px',borderRadius:8,border:'1px solid #cbd5e0',background:'#f7fafc',cursor:'pointer',fontSize:14}}>← Kembali</button>
                  <button onClick={simpanMasuk} disabled={saving}
                    style={{padding:'8px 18px',borderRadius:8,border:'none',background:'#2b6cb0',color:'#fff',fontWeight:600,fontSize:14,cursor:'pointer'}}>
                    {saving ? 'Menyimpan...' : '💾 Simpan'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* MODAL REVISI */}
      {revisiModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div style={{background:'#fff',borderRadius:14,padding:24,width:'min(400px,95vw)',boxShadow:'0 8px 40px rgba(0,0,0,0.18)'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
              <span style={{fontWeight:700,fontSize:16}}>✏️ Revisi Stok</span>
              <button onClick={()=>setRevisiModal(null)} style={{background:'none',border:'none',fontSize:22,cursor:'pointer',color:'#718096'}}>✕</button>
            </div>
            <div style={{fontSize:14,color:'#4a5568',marginBottom:16}}>
              <b>{revisiModal.barang.nama}</b>{revisiModal.varian ? ` — ${revisiModal.varian.nama}` : ''}
            </div>
            <div style={{marginBottom:12}}>
              <label style={labelStyle}>Selisih Qty (+ tambah / - kurangi)</label>
              <input type="number" step="0.01" value={revisiDelta} onChange={e=>setRevisiDelta(e.target.value)}
                placeholder="Contoh: -5 atau 10"
                style={{display:'block',width:'100%',padding:'8px 10px',borderRadius:8,border:'1px solid #cbd5e0',fontSize:14,marginTop:4,boxSizing:'border-box'}}/>
            </div>
            <div style={{marginBottom:18}}>
              <label style={labelStyle}>Alasan (wajib)</label>
              <textarea value={revisiAlasan} onChange={e=>setRevisiAlasan(e.target.value)}
                placeholder="Contoh: koreksi hitung stok fisik bulan Juli"
                rows={3}
                style={{display:'block',width:'100%',padding:'8px 10px',borderRadius:8,border:'1px solid #cbd5e0',fontSize:14,marginTop:4,boxSizing:'border-box',resize:'vertical'}}/>
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button onClick={()=>setRevisiModal(null)}
                style={{padding:'8px 16px',borderRadius:8,border:'1px solid #cbd5e0',background:'#f7fafc',cursor:'pointer',fontSize:14}}>Batal</button>
              <button onClick={simpanRevisi} disabled={savingRevisi}
                style={{padding:'8px 18px',borderRadius:8,border:'none',background:'#c53030',color:'#fff',fontWeight:600,fontSize:14,cursor:'pointer'}}>
                {savingRevisi ? 'Menyimpan...' : '💾 Simpan Revisi'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL HISTORY MASUK */}
      {historyMasukModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div style={{background:'#fff',borderRadius:14,padding:24,width:'min(580px,95vw)',maxHeight:'88vh',display:'flex',flexDirection:'column',boxShadow:'0 8px 40px rgba(0,0,0,0.18)'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16,flexShrink:0}}>
              <span style={{fontWeight:700,fontSize:16}}>📥 History Masuk Stok</span>
              <button onClick={()=>setHistoryMasukModal(false)} style={{background:'none',border:'none',fontSize:22,cursor:'pointer',color:'#718096'}}>✕</button>
            </div>
            <div style={{overflowY:'auto',flex:1}}>
              {loadingMasuk
                ? <div style={{color:'#718096',textAlign:'center',padding:32}}>Memuat...</div>
                : historyMasuk.length === 0
                  ? <div style={{color:'#a0aec0',textAlign:'center',padding:32}}>Belum ada data masuk stok</div>
                  : (
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:14}}>
                      <thead>
                        <tr style={{borderBottom:'2px solid #e2e8f0',background:'#f7fafc'}}>
                          <th style={{textAlign:'left',padding:'8px 10px',color:'#718096',fontWeight:600}}>Barang</th>
                          <th style={{textAlign:'right',padding:'8px 6px',color:'#718096',fontWeight:600}}>Qty</th>
                          <th style={{textAlign:'left',padding:'8px 6px',color:'#718096',fontWeight:600}}>Catatan</th>
                          <th style={{textAlign:'left',padding:'8px 6px',color:'#718096',fontWeight:600}}>Tgl</th>
                          <th style={{textAlign:'left',padding:'8px 6px',color:'#718096',fontWeight:600}}>Oleh</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyMasuk.map(r => {
                          const nama = r.varian ? `${r.barang} — ${r.varian}` : r.barang;
                          const tgl = new Date(r.tanggal).toLocaleDateString('id-ID',{day:'2-digit',month:'short',year:'numeric'});
                          return (
                            <tr key={r.id} style={{borderBottom:'1px solid #edf2f7'}}>
                              <td style={{padding:'8px 10px',color:'#2d3748'}}>{nama}</td>
                              <td style={{textAlign:'right',padding:'8px 6px',fontWeight:700,color:'#276749'}}>+{r.qty} {r.satuan}</td>
                              <td style={{padding:'8px 6px',color:'#718096',maxWidth:140,wordBreak:'break-word'}}>{r.catatan||'-'}</td>
                              <td style={{padding:'8px 6px',color:'#718096',whiteSpace:'nowrap'}}>{tgl}</td>
                              <td style={{padding:'8px 6px',color:'#718096',whiteSpace:'nowrap'}}>{r.created_by}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )
              }
            </div>
          </div>
        </div>
      )}

      {/* MODAL HISTORY KIRIM */}
      {historyKirimModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div style={{background:'#fff',borderRadius:14,padding:24,width:'min(620px,95vw)',maxHeight:'88vh',display:'flex',flexDirection:'column',boxShadow:'0 8px 40px rgba(0,0,0,0.18)'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16,flexShrink:0}}>
              <span style={{fontWeight:700,fontSize:16}}>🚚 Transaksi Kirim Stok</span>
              <button onClick={()=>setHistoryKirimModal(false)} style={{background:'none',border:'none',fontSize:22,cursor:'pointer',color:'#718096'}}>✕</button>
            </div>
            <div style={{overflowY:'auto',flex:1}}>
              {loadingKirim
                ? <div style={{color:'#718096',textAlign:'center',padding:32}}>Memuat...</div>
                : historyKirim.length === 0
                  ? <div style={{color:'#a0aec0',textAlign:'center',padding:32}}>Belum ada transaksi kirim</div>
                  : historyKirim.map(r => {
                      const tgl = new Date(r.tanggal).toLocaleDateString('id-ID',{day:'2-digit',month:'short',year:'numeric'});
                      const jam = new Date(r.tanggal).toLocaleTimeString('id-ID',{hour:'2-digit',minute:'2-digit'});
                      const items = typeof r.items === 'string' ? JSON.parse(r.items) : r.items;
                      return (
                        <div key={r.id} style={{border:'1px solid #e2e8f0',borderRadius:10,padding:'12px 14px',marginBottom:10}}>
                          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
                            <div>
                              <span style={{fontWeight:700,fontSize:14,color:'#2d3748'}}>Ke Cabang {r.cabang}</span>
                              <span style={{fontSize:14,color:'#a0aec0',marginLeft:8}}>{tgl} {jam}</span>
                              {r.created_by && <span style={{fontSize:14,color:'#718096',marginLeft:8}}>oleh {r.created_by}</span>}
                            </div>
                            {isRonald && (
                              <div style={{display:'flex',gap:6}}>
                                <button onClick={()=>openEditKirim(r)}
                                  style={{background:'none',border:'1px solid #93c5fd',color:'#2563eb',borderRadius:6,padding:'3px 10px',fontSize:14,cursor:'pointer',whiteSpace:'nowrap'}}>
                                  ✏️ Edit
                                </button>
                                <button onClick={()=>hapusKirim(r.id)}
                                  style={{background:'none',border:'1px solid #fc8181',color:'#c53030',borderRadius:6,padding:'3px 10px',fontSize:14,cursor:'pointer',whiteSpace:'nowrap'}}>
                                  🗑️ Hapus
                                </button>
                              </div>
                            )}
                          </div>
                          <div style={{fontSize:14,color:'#4a5568'}}>
                            {items.map((it,i) => (
                              <span key={i} style={{display:'inline-block',background:'#ebf8ff',color:'#2b6cb0',borderRadius:5,padding:'2px 8px',marginRight:4,marginBottom:4,fontSize:14}}>
                                {it.barang}{it.varian ? ` (${it.varian})` : ''}: <b>{it.qty}</b> {it.satuan}
                              </span>
                            ))}
                          </div>
                          {r.catatan && <div style={{fontSize:14,color:'#718096',marginTop:4}}>{r.catatan}</div>}
                          {r.foto_path && (
                            <img src={`/api/stok-foto/${r.foto_path}`} alt="foto"
                              style={{width:'100%',maxHeight:180,objectFit:'cover',borderRadius:8,marginTop:8}}/>
                          )}
                        </div>
                      );
                    })
              }
            </div>
          </div>
        </div>
      )}

      {/* MODAL EDIT KIRIM */}
      {editKirim && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1100}}>
          <div style={{background:'#fff',borderRadius:16,width:'min(540px,95vw)',maxHeight:'90vh',display:'flex',flexDirection:'column',boxShadow:'0 8px 40px rgba(0,0,0,0.22)',overflow:'hidden'}}>
            {/* Header */}
            <div style={{padding:'18px 20px 14px',borderBottom:'1px solid #e2e8f0',flexShrink:0}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <span style={{fontWeight:700,fontSize:16,color:'#1e293b'}}>✏️ Edit Pengiriman</span>
                <button onClick={()=>setEditKirim(null)} style={{background:'#f1f5f9',border:'none',borderRadius:8,width:32,height:32,cursor:'pointer',fontSize:16,color:'#64748b',display:'flex',alignItems:'center',justifyContent:'center'}}>×</button>
              </div>
              <div style={{fontSize:13,color:'#64748b',marginTop:4}}>
                Cabang <b style={{color:'#1e293b'}}>{editKirim.cabang}</b> &nbsp;·&nbsp; {new Date(editKirim.tanggal).toLocaleDateString('id-ID',{day:'2-digit',month:'long',year:'numeric'})}
              </div>
            </div>

            <div style={{overflowY:'auto',flex:1,padding:'16px 20px'}}>
              {/* Daftar item */}
              <div style={{fontSize:11,fontWeight:700,color:'#94a3b8',letterSpacing:1,textTransform:'uppercase',marginBottom:10}}>Barang Dikirim</div>
              {editKirimItems.map((item, idx) => (
                <div key={idx} style={{display:'flex',alignItems:'center',gap:10,background:'#f8fafc',borderRadius:10,padding:'10px 12px',marginBottom:8,border:'1px solid #e2e8f0'}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:600,fontSize:14,color:'#1e293b',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{item.barang_nama}</div>
                    {item.varian_nama && <div style={{fontSize:12,color:'#64748b'}}>{item.varian_nama}</div>}
                  </div>
                  <input type="number" min="0" step="0.01" value={item.qty}
                    onChange={e=>setEditKirimItems(p=>p.map((it,i)=>i===idx?{...it,qty:parseFloat(e.target.value)||0}:it))}
                    style={{width:72,padding:'6px 8px',border:'1px solid #cbd5e0',borderRadius:8,fontSize:14,textAlign:'right',outline:'none'}}/>
                  <span style={{fontSize:13,color:'#64748b',width:30,flexShrink:0}}>{item.satuan}</span>
                  <button onClick={()=>setEditKirimItems(p=>p.filter((_,i)=>i!==idx))}
                    style={{background:'#fee2e2',border:'none',borderRadius:7,width:28,height:28,cursor:'pointer',color:'#dc2626',fontSize:15,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>×</button>
                </div>
              ))}

              {/* Tambah barang */}
              <div style={{fontSize:11,fontWeight:700,color:'#94a3b8',letterSpacing:1,textTransform:'uppercase',margin:'16px 0 10px'}}>Tambah Barang</div>
              <div style={{display:'flex',flexDirection:'column',gap:6}}>
                {barangMasterStok.map(b => {
                  if (b.has_varian) {
                    return b.varians.map(v => {
                      if (editKirimItems.some(i=>i.barang_id===b.id && i.varian_id===v.id)) return null;
                      return (
                        <button key={`${b.id}-${v.id}`}
                          onClick={()=>setEditKirimItems(p=>[...p,{barang_id:b.id,varian_id:v.id,barang_nama:b.nama,varian_nama:v.nama,satuan:b.satuan,qty:0}])}
                          style={{textAlign:'left',padding:'9px 14px',border:'1.5px dashed #cbd5e0',borderRadius:9,background:'#fff',fontSize:13,cursor:'pointer',color:'#2563eb',fontWeight:500}}>
                          + {b.nama} <span style={{color:'#94a3b8'}}>({v.nama})</span>
                        </button>
                      );
                    });
                  } else {
                    if (editKirimItems.some(i=>i.barang_id===b.id && !i.varian_id)) return null;
                    return (
                      <button key={b.id}
                        onClick={()=>setEditKirimItems(p=>[...p,{barang_id:b.id,varian_id:null,barang_nama:b.nama,varian_nama:null,satuan:b.satuan,qty:0}])}
                        style={{textAlign:'left',padding:'9px 14px',border:'1.5px dashed #cbd5e0',borderRadius:9,background:'#fff',fontSize:13,cursor:'pointer',color:'#2563eb',fontWeight:500}}>
                        + {b.nama}
                      </button>
                    );
                  }
                })}
              </div>
            </div>

            {/* Footer */}
            <div style={{padding:'14px 20px',borderTop:'1px solid #e2e8f0',display:'flex',gap:8,flexShrink:0}}>
              <button onClick={simpanEditKirim} disabled={editKirimSaving}
                style={{flex:1,padding:'12px',background:editKirimSaving?'#86efac':'#16a34a',color:'#fff',border:'none',borderRadius:10,fontWeight:700,fontSize:15,cursor:'pointer',transition:'background .2s'}}>
                {editKirimSaving ? 'Menyimpan...' : '💾 Simpan Perubahan'}
              </button>
              <button onClick={()=>setEditKirim(null)}
                style={{padding:'12px 20px',background:'#f1f5f9',color:'#374151',border:'none',borderRadius:10,fontWeight:600,fontSize:15,cursor:'pointer'}}>
                Batal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL HISTORY REVISI */}
      {historyRevisiModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div style={{background:'#fff',borderRadius:14,padding:24,width:'min(560px,95vw)',maxHeight:'85vh',display:'flex',flexDirection:'column',boxShadow:'0 8px 40px rgba(0,0,0,0.18)'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
              <span style={{fontWeight:700,fontSize:16}}>📋 History Revisi Stok</span>
              <button onClick={()=>setHistoryRevisiModal(false)} style={{background:'none',border:'none',fontSize:22,cursor:'pointer',color:'#718096'}}>✕</button>
            </div>
            <div style={{overflowY:'auto',flex:1}}>
              {loadingRevisi
                ? <div style={{color:'#718096',textAlign:'center',padding:32}}>Memuat...</div>
                : historyRevisi.length === 0
                  ? <div style={{color:'#a0aec0',textAlign:'center',padding:32}}>Belum ada revisi stok</div>
                  : (
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:14}}>
                      <thead>
                        <tr style={{borderBottom:'2px solid #e2e8f0',background:'#f7fafc'}}>
                          <th style={{textAlign:'left',padding:'8px 10px',color:'#718096',fontWeight:600}}>Barang</th>
                          <th style={{textAlign:'right',padding:'8px 6px',color:'#718096',fontWeight:600}}>Selisih</th>
                          <th style={{textAlign:'left',padding:'8px 10px',color:'#718096',fontWeight:600}}>Alasan</th>
                          <th style={{textAlign:'left',padding:'8px 6px',color:'#718096',fontWeight:600}}>Oleh</th>
                          <th style={{textAlign:'left',padding:'8px 6px',color:'#718096',fontWeight:600}}>Waktu</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyRevisi.map(r => {
                          const tgl = new Date(r.created_at).toLocaleDateString('id-ID',{day:'2-digit',month:'short',year:'numeric'});
                          const jam = new Date(r.created_at).toLocaleTimeString('id-ID',{hour:'2-digit',minute:'2-digit'});
                          const nama = r.varian ? `${r.barang} — ${r.varian}` : r.barang;
                          const plus = r.qty_delta > 0;
                          return (
                            <tr key={r.id} style={{borderBottom:'1px solid #edf2f7'}}>
                              <td style={{padding:'9px 10px',color:'#2d3748'}}>{nama}</td>
                              <td style={{textAlign:'right',padding:'9px 6px',fontWeight:700,color:plus?'#276749':'#c53030'}}>
                                {plus?'+':''}{r.qty_delta} {r.satuan}
                              </td>
                              <td style={{padding:'9px 10px',color:'#4a5568',maxWidth:160,wordBreak:'break-word'}}>{r.alasan}</td>
                              <td style={{padding:'9px 6px',color:'#718096',whiteSpace:'nowrap'}}>{r.created_by}</td>
                              <td style={{padding:'9px 6px',color:'#718096',whiteSpace:'nowrap',fontSize:14}}>{tgl}<br/>{jam}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =====================
// BOT PAGE
// =====================
const CABANG_OPTIONS = ['PO', 'DM', 'PM', 'TM', 'MT'];

function CctvVideoModal({ camera, onClose }) {
  const videoRef = React.useRef(null);
  const hlsRef = React.useRef(null);
  const [status, setStatus] = React.useState('Memuat stream...');
  const token = localStorage.getItem('token');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/cctv/${camera.device_id}/stream`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (cancelled) return;
        if (!data.hls) { setStatus('URL stream tidak tersedia'); return; }
        setStatus('Menghubungkan...');
        const video = videoRef.current;
        if (!video) return;
        if (window.Hls && window.Hls.isSupported()) {
          const hls = new window.Hls({ enableWorker: false });
          hlsRef.current = hls;
          hls.loadSource(data.hls);
          hls.attachMedia(video);
          hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
            setStatus('');
            video.play().catch(() => {});
          });
          hls.on(window.Hls.Events.ERROR, (_, d) => {
            if (d.fatal) setStatus('Gagal memuat stream: ' + d.type);
          });
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
          video.src = data.hls;
          setStatus('');
          video.play().catch(() => {});
        } else {
          setStatus('Browser tidak support HLS');
        }
      } catch (e) {
        if (!cancelled) setStatus('Error: ' + e.message);
      }
    })();
    return () => {
      cancelled = true;
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    };
  }, [camera.device_id]);

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'#111', borderRadius:12, padding:16, width:'min(90vw,800px)', position:'relative' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
          <div style={{ color:'#fff', fontWeight:700, fontSize:15 }}>
            📷 {camera.nama_kamera || camera.device_id}
            {camera.cabang && <span style={{ marginLeft:8, background:'#2563eb', borderRadius:4, padding:'2px 8px', fontSize:12 }}>{camera.cabang}</span>}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#fff', fontSize:22, cursor:'pointer', lineHeight:1 }}>✕</button>
        </div>
        {status && (
          <div style={{ color:'#9ca3af', textAlign:'center', padding:'40px 0', fontSize:14 }}>{status}</div>
        )}
        <video
          ref={videoRef}
          controls
          muted
          playsInline
          style={{ width:'100%', borderRadius:8, display: status ? 'none' : 'block', background:'#000', maxHeight:'70vh' }}
        />
        <div style={{ marginTop:8, color:'#6b7280', fontSize:11 }}>
          Device ID: {camera.device_id}
        </div>
      </div>
    </div>
  );
}

function CctvPage() {
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterCabang, setFilterCabang] = useState('');
  const [editMap, setEditMap] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [streamCamera, setStreamCamera] = useState(null);

  const token = localStorage.getItem('token');

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/cctv', { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (Array.isArray(data)) {
        setCameras(data);
        setLastRefresh(new Date());
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { load(); }, []);

  const handleEdit = (deviceId, field, value) => {
    setEditMap(prev => ({
      ...prev,
      [deviceId]: { ...(prev[deviceId] || {}), [field]: value },
    }));
  };

  const handleSave = async (cam) => {
    const changes = editMap[cam.device_id] || {};
    const payload = {
      cabang:      changes.cabang      !== undefined ? changes.cabang      : cam.cabang,
      nama_kamera: changes.nama_kamera !== undefined ? changes.nama_kamera : cam.nama_kamera,
    };
    setSavingId(cam.device_id);
    try {
      await fetch(`/api/cctv/${cam.device_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      setCameras(prev => prev.map(c =>
        c.device_id === cam.device_id ? { ...c, ...payload } : c
      ));
      setEditMap(prev => { const n = {...prev}; delete n[cam.device_id]; return n; });
    } catch (e) {
      alert('Gagal menyimpan');
    } finally {
      setSavingId(null);
    }
  };

  const filtered = filterCabang
    ? cameras.filter(c => c.cabang === filterCabang)
    : cameras;

  const onlineCount  = cameras.filter(c => c.online === true).length;
  const offlineCount = cameras.filter(c => c.online === false).length;

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>📷 CCTV Monitor</h2>
        <span style={{ background: '#dcfce7', color: '#166534', borderRadius: 20, padding: '3px 12px', fontSize: 13, fontWeight: 600 }}>🟢 {onlineCount} Online</span>
        <span style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 20, padding: '3px 12px', fontSize: 13, fontWeight: 600 }}>🔴 {offlineCount} Offline</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            value={filterCabang}
            onChange={e => setFilterCabang(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}
          >
            <option value="">Semua Cabang</option>
            {CABANG_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button
            onClick={load}
            disabled={loading}
            style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}
          >
            {loading ? '⟳ Loading...' : '🔄 Refresh'}
          </button>
        </div>
      </div>
      {lastRefresh && (
        <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 12 }}>
          Terakhir diperbarui: {lastRefresh.toLocaleTimeString('id-ID')}
        </div>
      )}
      {streamCamera && <CctvVideoModal camera={streamCamera} onClose={() => setStreamCamera(null)} />}
      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 6px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', width: 70 }}>Status</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Device ID</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', width: 100 }}>Cabang</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Nama Kamera</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', width: 120 }}>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>
                {loading ? 'Memuat data kamera...' : 'Tidak ada kamera ditemukan'}
              </td></tr>
            )}
            {filtered.map((cam, idx) => {
              const edit = editMap[cam.device_id] || {};
              const cabangVal      = edit.cabang      !== undefined ? edit.cabang      : (cam.cabang || '');
              const namaVal        = edit.nama_kamera !== undefined ? edit.nama_kamera : (cam.nama_kamera || '');
              const isDirty = editMap[cam.device_id] !== undefined;
              return (
                <tr key={cam.device_id} style={{ borderBottom: '1px solid #f3f4f6', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={{ padding: '10px 14px' }}>
                    {cam.online === true  && <span style={{ color: '#16a34a', fontWeight: 700, fontSize: 14 }}>🟢</span>}
                    {cam.online === false && <span style={{ color: '#dc2626', fontWeight: 700, fontSize: 14 }}>🔴</span>}
                    {cam.online === null  && <span style={{ color: '#9ca3af' }}>❓</span>}
                  </td>
                  <td style={{ padding: '10px 14px', fontFamily: 'monospace', color: '#6b7280', fontSize: 12 }}>{cam.device_id}</td>
                  <td style={{ padding: '8px 14px' }}>
                    <select
                      value={cabangVal}
                      onChange={e => handleEdit(cam.device_id, 'cabang', e.target.value)}
                      style={{ width: '100%', padding: '4px 6px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: 13 }}
                    >
                      <option value="">—</option>
                      {CABANG_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: '8px 14px' }}>
                    <input
                      type="text"
                      value={namaVal}
                      placeholder="Isi nama kamera..."
                      onChange={e => handleEdit(cam.device_id, 'nama_kamera', e.target.value)}
                      style={{ width: '100%', padding: '4px 8px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: 13, boxSizing: 'border-box' }}
                    />
                  </td>
                  <td style={{ padding: '8px 14px' }}>
                    <div style={{ display:'flex', gap:6 }}>
                      {cam.online === true && (
                        <button
                          onClick={() => setStreamCamera(cam)}
                          style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 5, padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}
                        >
                          ▶ Live
                        </button>
                      )}
                      {isDirty && (
                        <button
                          onClick={() => handleSave(cam)}
                          disabled={savingId === cam.device_id}
                          style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 5, padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}
                        >
                          {savingId === cam.device_id ? '...' : 'Simpan'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BotPage() {
  const [botInfo, setBotInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [msg, setMsg] = useState('');

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await api.get('/bot/status');
      setBotInfo(res.data);
    } catch (e) {
      setBotInfo({ status: 'unreachable', qrImage: null });
    } finally {
      setLoading(false);
    }
  };

  const handleReconnect = async () => {
    setReconnecting(true);
    setMsg('');
    try {
      await api.post('/bot/reconnect');
      setMsg('✅ Reconnect dimulai, tunggu beberapa detik...');
      setTimeout(fetchStatus, 5000);
    } catch (e) {
      setMsg('❌ Gagal reconnect: ' + (e.response?.data?.detail || e.message));
    } finally {
      setReconnecting(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 15000);
    return () => clearInterval(interval);
  }, []);

  const statusColor = {
    connected: '#38a169',
    disconnected: '#e53e3e',
    qr_pending: '#d69e2e',
    connecting: '#3182ce',
    unreachable: '#718096',
  };

  const statusLabel = {
    connected: '✅ Terhubung',
    disconnected: '❌ Terputus',
    qr_pending: '📱 Menunggu Scan QR',
    connecting: '⏳ Menghubungkan...',
    unreachable: '⚠️ Tidak Dapat Dijangkau',
  };

  const st = botInfo?.status || 'connecting';

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: 24 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 24 }}>🤖 Status WhatsApp Bot</h2>

      {/* Status Card */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 24, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{
            width: 14, height: 14, borderRadius: '50%',
            background: statusColor[st] || '#718096',
            boxShadow: `0 0 8px ${statusColor[st] || '#718096'}`
          }} />
          <span style={{ fontSize: 18, fontWeight: 700, color: statusColor[st] || '#718096' }}>
            {statusLabel[st] || st}
          </span>
        </div>
        {botInfo?.lastUpdate && (
          <div style={{ fontSize: 12, color: '#718096' }}>
            Update terakhir: {new Date(botInfo.lastUpdate).toLocaleString('id-ID')}
          </div>
        )}
      </div>

      {/* QR Code */}
      {st === 'qr_pending' && botInfo?.qrImage && (
        <div style={{ background: '#fff', border: '2px solid #d69e2e', borderRadius: 12, padding: 24, marginBottom: 20, textAlign: 'center' }}>
          <p style={{ fontWeight: 600, marginBottom: 12, color: '#744210' }}>
            📱 Scan QR ini dengan WhatsApp
          </p>
          <img src={botInfo.qrImage} alt="QR Code" style={{ width: 240, height: 240, borderRadius: 8 }} />
          <p style={{ fontSize: 12, color: '#718096', marginTop: 12 }}>
            Buka WhatsApp → Perangkat Tertaut → Tautkan Perangkat
          </p>
        </div>
      )}

      {/* Tombol */}
      <div style={{ display: 'flex', gap: 10 }}>
        <button
          onClick={fetchStatus}
          disabled={loading}
          style={{ padding: '10px 20px', background: '#2b6cb0', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
          {loading ? '⏳ Mengecek...' : '🔄 Refresh Status'}
        </button>
        <button
          onClick={handleReconnect}
          disabled={reconnecting}
          style={{ padding: '10px 20px', background: '#e53e3e', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
          {reconnecting ? '⏳ Proses...' : '🔌 Reconnect'}
        </button>
      </div>

      {msg && (
        <div style={{ marginTop: 12, padding: 12, background: '#ebf8ff', border: '1px solid #bee3f8', borderRadius: 8, fontSize: 14 }}>
          {msg}
        </div>
      )}

      {/* Info */}
      <div style={{ marginTop: 24, padding: 16, background: '#f7fafc', borderRadius: 8, fontSize: 13, color: '#4a5568' }}>
        <strong>Cara pakai:</strong>
        <ul style={{ marginTop: 8, paddingLeft: 20, lineHeight: 1.8 }}>
          <li>Status di-refresh otomatis setiap 15 detik</li>
          <li>Jika status <strong>Menunggu Scan QR</strong>, scan QR di atas dengan WhatsApp</li>
          <li>Tombol <strong>Reconnect</strong> untuk paksa bot terhubung ulang</li>
          <li>Jika status <strong>Tidak Dapat Dijangkau</strong>, cek apakah container bot sedang jalan</li>
        </ul>
      </div>
    </div>
  );
}

// =====================
// PENGATURAN AKUN PAGE
// =====================
function PengaturanAkunPage() {
  const currentUser = localStorage.getItem('fullname') || '';
  const currentUsername = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return '';
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub || '';
    } catch { return ''; }
  })();
  const canManageUsers = ['Ronald','ronald'].some(r => currentUsername.toLowerCase().includes(r.toLowerCase()));

  // Change password state
  const [oldPass, setOldPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [passMsg, setPassMsg] = useState(null);
  const [passLoading, setPassLoading] = useState(false);

  // User management state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [addMode, setAddMode] = useState(false);
  const [newGrup, setNewGrup] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [newFullName, setNewFullName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [addMsg, setAddMsg] = useState(null);
  const [addLoading, setAddLoading] = useState(false);

  // Edit user state
  const [editUserId, setEditUserId] = useState(null);
  const [editUsername, setEditUsername] = useState('');
  const [editFullName, setEditFullName] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editGrup, setEditGrup] = useState('');
  const [editMsg, setEditMsg] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  // Inline delete confirmation state
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    if (canManageUsers) loadUsers();
  }, []);

  async function loadUsers() {
    setUsersLoading(true);
    try {
      const res = await api.get('/auth/users');
      setUsers(res.data);
    } catch { setUsers([]); }
    finally { setUsersLoading(false); }
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    if (newPass !== confirmPass) { setPassMsg({ type: 'error', text: 'Password baru tidak cocok' }); return; }
    if (newPass.length < 6) { setPassMsg({ type: 'error', text: 'Password minimal 6 karakter' }); return; }
    setPassLoading(true);
    setPassMsg(null);
    try {
      const form = new FormData();
      form.append('old_password', oldPass);
      form.append('new_password', newPass);
      await api.post('/auth/change-password', form);
      setPassMsg({ type: 'success', text: 'Password berhasil diubah' });
      setOldPass(''); setNewPass(''); setConfirmPass('');
    } catch (err) {
      setPassMsg({ type: 'error', text: err.response?.data?.detail || 'Gagal mengubah password' });
    } finally { setPassLoading(false); }
  }

  async function handleAddUser(e) {
    e.preventDefault();
    if (!newUsername.trim() || !newFullName.trim() || !newPassword.trim()) return;
    setAddLoading(true); setAddMsg(null);
    try {
      const form = new FormData();
      form.append('username', newUsername.trim());
      form.append('full_name', newFullName.trim());
      form.append('password', newPassword);
      await api.post('/auth/users', form);
      setAddMsg({ type: 'success', text: 'User berhasil ditambahkan' });
      setNewGrup(''); setNewUsername(''); setNewFullName(''); setNewPassword('');
      setAddMode(false);
      loadUsers();
    } catch (err) {
      setAddMsg({ type: 'error', text: err.response?.data?.detail || 'Gagal menambah user' });
    } finally { setAddLoading(false); }
  }

  async function handleDeleteUser(u) {
    try {
      await api.delete(`/auth/users/${u.id}`);
      setConfirmDeleteId(null);
      loadUsers();
    } catch (err) {
      setConfirmDeleteId(null);
      alert(err.response?.data?.detail || 'Gagal menghapus user');
    }
  }

  async function handleEditUser(e) {
    e.preventDefault();
    setEditLoading(true); setEditMsg(null);
    try {
      const form = new FormData();
      form.append('full_name', editFullName);
      form.append('username', editUsername);
      if (editPassword) form.append('new_password', editPassword);
      await api.put(`/auth/users/${editUserId}`, form);
      setEditMsg({ type: 'success', text: 'User berhasil diupdate' });
      setEditUserId(null); setEditUsername(''); setEditFullName(''); setEditPassword(''); setEditGrup('');
      loadUsers();
    } catch (err) {
      setEditMsg({ type: 'error', text: err.response?.data?.detail || 'Gagal update user' });
    } finally { setEditLoading(false); }
  }

  const cardStyle = { background: '#fff', borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '1px solid #e2e8f0', marginBottom: 24 };
  const cardHeaderStyle = { padding: '18px 24px 0', borderBottom: '1px solid #f1f5f9', paddingBottom: 14, marginBottom: 20 };
  const cardBodyStyle = { padding: '0 24px 24px' };
  const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.4 };

  return (
    <div>
      {/* Page Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '20px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#1e293b' }}>Pengaturan Akun</div>
          <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>Kelola profil dan keamanan akun</div>
        </div>
      </div>

      <div style={{ padding: 28, maxWidth: 720 }}>

        {/* Profile Info Card */}
        <div style={cardStyle}>
          <div style={cardHeaderStyle}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>Informasi Akun</div>
          </div>
          <div style={cardBodyStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                {currentUser.trim().split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: 17, fontWeight: 700, color: '#1e293b' }}>{currentUser}</div>
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>@{currentUsername}</div>
                <div style={{ fontSize: 11, color: '#22c55e', fontWeight: 600, marginTop: 4, background: '#f0fdf4', display: 'inline-block', padding: '2px 8px', borderRadius: 20, border: '1px solid #bbf7d0' }}>
                  {canManageUsers ? 'Administrator' : 'Staff'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Change Password Card */}
        <div style={cardStyle}>
          <div style={cardHeaderStyle}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>Ubah Password</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Gunakan password yang kuat minimal 6 karakter</div>
          </div>
          <div style={cardBodyStyle}>
            <form onSubmit={handleChangePassword}>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Password Lama</label>
                <input type="password" style={inputStyle} placeholder="Masukkan password lama" value={oldPass} onChange={e => setOldPass(e.target.value)} required />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Password Baru</label>
                <input type="password" style={inputStyle} placeholder="Minimal 6 karakter" value={newPass} onChange={e => setNewPass(e.target.value)} required />
              </div>
              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Konfirmasi Password Baru</label>
                <input type="password" style={inputStyle} placeholder="Ulangi password baru" value={confirmPass} onChange={e => setConfirmPass(e.target.value)} required />
              </div>
              {passMsg && (
                <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
                  background: passMsg.type === 'success' ? '#f0fdf4' : '#fef2f2',
                  color: passMsg.type === 'success' ? '#16a34a' : '#dc2626',
                  border: `1px solid ${passMsg.type === 'success' ? '#bbf7d0' : '#fecaca'}` }}>
                  {passMsg.text}
                </div>
              )}
              <button type="submit" disabled={passLoading}
                style={{ background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {passLoading ? 'Menyimpan...' : 'Simpan Password'}
              </button>
            </form>
          </div>
        </div>

        {/* User Management Card (Ronald only) */}
        {canManageUsers && (
          <div style={cardStyle}>
            <div style={{ ...cardHeaderStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>Manajemen User</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Kelola akun login pengguna aplikasi</div>
              </div>
              <button onClick={() => { setAddMode(m => !m); setAddMsg(null); setNewGrup(''); setNewUsername(''); setNewFullName(''); setNewPassword(''); }}
                style={{ background: addMode ? '#f1f5f9' : 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: addMode ? '#475569' : '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {addMode ? 'Batal' : '+ Tambah User'}
              </button>
            </div>
            <div style={cardBodyStyle}>

              {/* Add User Form */}
              {addMode && (() => {
                const KASIR_CABANG = [
                  { value: 'kasirpo', label: 'Puri Orchard (PO)' },
                  { value: 'kasirdm', label: 'Daan Mogot (DM)' },
                  { value: 'kasirpm', label: 'Palm Mansion (PM)' },
                  { value: 'kasirtm', label: 'Tomang (TM)' },
                  { value: 'kasirmt', label: 'Metro (MT)' },
                ];
                const handleNewGrupChange = (val) => {
                  setNewGrup(val);
                  if (val === 'gudang') setNewUsername('gudang');
                  else if (val !== 'kasir') setNewUsername('');
                  else setNewUsername('');
                };
                return (
                  <form onSubmit={handleAddUser} style={{ background: '#f8fafc', borderRadius: 10, padding: 16, marginBottom: 20, border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 12 }}>Tambah User Baru</div>
                    <div style={{ display: 'grid', gridTemplateColumns: newGrup === 'karyawan' ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 12 }}>
                      <div>
                        <label style={labelStyle}>Grup Akun</label>
                        <select style={inputStyle} value={newGrup} onChange={e => handleNewGrupChange(e.target.value)} required>
                          <option value="">-- Pilih grup --</option>
                          <option value="administrator">👑 Administrator</option>
                          <option value="report">📊 Akun Report</option>
                          <option value="kasir">🧾 Akun Kasir</option>
                          <option value="gudang">📦 Akun Gudang</option>
                          <option value="karyawan">👔 Akun Karyawan</option>
                        </select>
                      </div>
                      {newGrup !== 'karyawan' && (
                        <div>
                          {newGrup === 'kasir' ? (
                            <>
                              <label style={labelStyle}>Cabang Kasir</label>
                              <select style={inputStyle} value={newUsername} onChange={e => setNewUsername(e.target.value)} required>
                                <option value="">-- Pilih cabang --</option>
                                {KASIR_CABANG.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                              </select>
                            </>
                          ) : (
                            <>
                              <label style={labelStyle}>Username</label>
                              <input style={inputStyle} placeholder="Username untuk login" value={newUsername}
                                onChange={e => setNewUsername(e.target.value)}
                                readOnly={newGrup === 'gudang'}
                                required />
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    {newGrup === 'karyawan' && (
                      <div style={{ padding: '10px 12px', background: '#f0fdf4', border: '1px solid #a7f3d0', borderRadius: 8, fontSize: 12, color: '#065f46', marginBottom: 12 }}>
                        👔 Akun karyawan dikelola melalui PIN di halaman <strong>SDM → Data Karyawan</strong>. Tidak perlu membuat akun di sini.
                      </div>
                    )}
                    {newGrup !== 'karyawan' && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                        <div>
                          <label style={labelStyle}>Nama Lengkap</label>
                          <input style={inputStyle} placeholder="Nama tampilan" value={newFullName} onChange={e => setNewFullName(e.target.value)} required />
                        </div>
                        <div>
                          <label style={labelStyle}>Password Awal</label>
                          <input type="password" style={inputStyle} placeholder="Password untuk login pertama" value={newPassword} onChange={e => setNewPassword(e.target.value)} required />
                        </div>
                      </div>
                    )}
                    {addMsg && (
                      <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 7, fontSize: 12, fontWeight: 500,
                        background: addMsg.type === 'success' ? '#f0fdf4' : '#fef2f2',
                        color: addMsg.type === 'success' ? '#16a34a' : '#dc2626' }}>
                        {addMsg.text}
                      </div>
                    )}
                    {newGrup !== 'karyawan' && (
                      <button type="submit" disabled={addLoading}
                        style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        {addLoading ? 'Menyimpan...' : 'Tambahkan'}
                      </button>
                    )}
                  </form>
                );
              })()}

              {/* Edit User Form */}
              {editUserId && (() => {
                const KASIR_CABANG = [
                  { value: 'kasirpo', label: 'Puri Orchard (PO)' },
                  { value: 'kasirdm', label: 'Daan Mogot (DM)' },
                  { value: 'kasirpm', label: 'Palm Mansion (PM)' },
                  { value: 'kasirtm', label: 'Tomang (TM)' },
                  { value: 'kasirmt', label: 'Metro (MT)' },
                ];
                const handleGrupChange = (val) => {
                  setEditGrup(val);
                  if (val === 'gudang') setEditUsername('gudang');
                  else if (val !== 'kasir') setEditUsername(editUsername);
                };
                return (
                  <form onSubmit={handleEditUser} style={{ background: '#fffbeb', borderRadius: 10, padding: 16, marginBottom: 20, border: '1px solid #fde68a' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 14 }}>✏️ Edit User</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                      <div>
                        <label style={labelStyle}>Grup Akun</label>
                        <select style={inputStyle} value={editGrup} onChange={e => handleGrupChange(e.target.value)}>
                          <option value="">-- Pilih grup --</option>
                          <option value="administrator">👑 Administrator</option>
                          <option value="report">📊 Akun Report</option>
                          <option value="kasir">🧾 Akun Kasir</option>
                          <option value="gudang">📦 Akun Gudang</option>
                          <option value="karyawan">👔 Akun Karyawan</option>
                        </select>
                      </div>
                      <div>
                        {editGrup === 'kasir' ? (
                          <>
                            <label style={labelStyle}>Cabang Kasir</label>
                            <select style={inputStyle} value={editUsername} onChange={e => setEditUsername(e.target.value)}>
                              <option value="">-- Pilih cabang --</option>
                              {KASIR_CABANG.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                          </>
                        ) : editGrup === 'karyawan' ? (
                          <div style={{ padding: '10px 12px', background: '#f0fdf4', border: '1px solid #a7f3d0', borderRadius: 8, fontSize: 12, color: '#065f46', marginTop: 18 }}>
                            👔 Akun karyawan dikelola melalui PIN di halaman <strong>SDM → Data Karyawan</strong>. Default PIN: <span style={{ fontFamily: 'monospace' }}>123456</span>
                          </div>
                        ) : (
                          <>
                            <label style={labelStyle}>Username</label>
                            <input style={inputStyle} value={editUsername} onChange={e => setEditUsername(e.target.value)} required placeholder="Username login"
                              readOnly={editGrup === 'gudang'} />
                          </>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                      <div>
                        <label style={labelStyle}>Nama Lengkap</label>
                        <input style={inputStyle} value={editFullName} onChange={e => setEditFullName(e.target.value)} required />
                      </div>
                      <div>
                        <label style={labelStyle}>Reset Password (opsional)</label>
                        <input type="password" style={inputStyle} placeholder="Kosongkan jika tidak diubah" value={editPassword} onChange={e => setEditPassword(e.target.value)} />
                      </div>
                    </div>
                    {editMsg && (
                      <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 7, fontSize: 12, fontWeight: 500,
                        background: editMsg.type === 'success' ? '#f0fdf4' : '#fef2f2',
                        color: editMsg.type === 'success' ? '#16a34a' : '#dc2626' }}>
                        {editMsg.text}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="submit" disabled={editLoading}
                        style={{ background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        {editLoading ? 'Menyimpan...' : 'Simpan Perubahan'}
                      </button>
                      <button type="button" onClick={() => { setEditUserId(null); setEditMsg(null); setEditGrup(''); }}
                        style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 7, padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                        Batal
                      </button>
                    </div>
                  </form>
                );
              })()}

              {/* Users grouped by role */}
              {(() => {
                const KASIR_USERNAMES = ['kasirpo', 'kasirpm', 'kasirtm', 'kasirdm', 'kasirmt'];
                function getUserRole(username) {
                  if (username === 'ronald') return 'Administrator';
                  if (KASIR_USERNAMES.includes(username)) return 'Kasir';
                  if (username === 'gudang') return 'Gudang';
                  return 'Report';
                }
                const GROUPS = [
                  { key: 'Administrator', label: '👑 Administrator', desc: 'Akses penuh ke semua fitur', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
                  { key: 'Report',        label: '📊 Akun Report',   desc: 'Login ke web laporan',       color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
                  { key: 'Kasir',         label: '🧾 Akun Kasir',    desc: 'Login ke web kasir (/kasir/)', color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc' },
                  { key: 'Gudang',        label: '📦 Akun Gudang',   desc: 'Login ke web gudang (/gudang/)', color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' },
                ];
                if (usersLoading) return <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>Memuat data user...</div>;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {GROUPS.map(group => {
                      const groupUsers = users.filter(u => getUserRole(u.username) === group.key);
                      if (!groupUsers.length) return null;
                      return (
                        <div key={group.key} style={{ border: `1px solid ${group.border}`, borderRadius: 10, overflow: 'hidden' }}>
                          <div style={{ background: group.bg, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: `1px solid ${group.border}` }}>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: group.color }}>{group.label}</div>
                              <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>{group.desc}</div>
                            </div>
                            <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: group.color, background: '#fff', padding: '2px 10px', borderRadius: 20, border: `1px solid ${group.border}` }}>
                              {groupUsers.length} akun
                            </span>
                          </div>
                          {groupUsers.map((u, i) => (
                            <div key={u.id} style={{ padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: i < groupUsers.length - 1 ? '1px solid #f1f5f9' : 'none', background: '#fff' }}>
                              <div style={{ width: 34, height: 34, borderRadius: '50%', background: group.bg, border: `1.5px solid ${group.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: group.color, flexShrink: 0 }}>
                                {u.full_name.trim().split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase()}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{u.full_name}</div>
                                <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>@{u.username}</div>
                              </div>
                              <span style={{ fontSize: 11, fontWeight: 600, color: u.is_active ? '#16a34a' : '#dc2626', background: u.is_active ? '#f0fdf4' : '#fef2f2', padding: '2px 8px', borderRadius: 20, border: `1px solid ${u.is_active ? '#bbf7d0' : '#fecaca'}`, flexShrink: 0 }}>
                                {u.is_active ? 'Aktif' : 'Nonaktif'}
                              </span>
                              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                <button onClick={() => {
                                  const KASIR_USERNAMES = ['kasirpo','kasirpm','kasirtm','kasirdm','kasirmt'];
                                  const grup = u.username === 'ronald' ? 'administrator' : KASIR_USERNAMES.includes(u.username) ? 'kasir' : u.username === 'gudang' ? 'gudang' : 'report';
                                  setEditUserId(u.id); setEditUsername(u.username); setEditFullName(u.full_name); setEditPassword(''); setEditGrup(grup); setEditMsg(null); setAddMode(false);
                                }}
                                  style={{ background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                  Edit
                                </button>
                                {u.username !== currentUsername && (
                                  confirmDeleteId === u.id ? (
                                    <>
                                      <button onClick={() => handleDeleteUser(u)} style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Yakin?</button>
                                      <button onClick={() => setConfirmDeleteId(null)} style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}>Batal</button>
                                    </>
                                  ) : (
                                    <button onClick={() => { setConfirmDeleteId(u.id); setEditUserId(null); }} style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Hapus</button>
                                  )
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })}

                    {/* Info card: Akun Karyawan */}
                    <div style={{ border: '1px solid #a7f3d0', borderRadius: 10, overflow: 'hidden' }}>
                      <div style={{ background: '#ecfdf5', padding: '10px 14px', borderBottom: '1px solid #a7f3d0', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>👔 Akun Karyawan</div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>Login ke web kerja harian (/kerja/) menggunakan nama + PIN 6 angka</div>
                        </div>
                      </div>
                      <div style={{ padding: '12px 14px', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                        <div style={{ fontSize: 12, color: '#64748b' }}>
                          PIN karyawan dikelola di halaman <strong>SDM → Data Karyawan</strong>. Default PIN: <span style={{ fontFamily: 'monospace', background: '#f1f5f9', padding: '1px 6px', borderRadius: 4 }}>123456</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// =====================
// HUTANG KARYAWAN PAGE
// =====================
function HutangPage() {
  const CABANG_LIST = ['PO','DM','PM','TM','MT'];
  const CABANG_NAMA = {PO:'Puri Orchard',DM:'Daan Mogot',PM:'Palm Mansion',TM:'Tomang',MT:'Metro'};
  const today = new Date().toISOString().slice(0,10);

  const [cabang, setCabang] = useState('PO');
  const [namaList, setNamaList] = useState([]);
  const [nama, setNama] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [fTgl, setFTgl] = useState(today);
  const [fPinjaman, setFPinjaman] = useState('');
  const [fPelunasan, setFPelunasan] = useState('');
  const [fKeterangan, setFKeterangan] = useState('');
  const [editId, setEditId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    api.get('/karyawan/list', { params: { cabang, status: 'aktif' } })
      .then(r => {
        const list = (r.data.data || []).map(k => k.nama);
        setNamaList(list);
        setNama(list[0] || '');
      })
      .catch(() => { setNamaList([]); setNama(''); });
  }, [cabang]);

  useEffect(() => {
    if (!nama) { setRows([]); return; }
    setLoading(true);
    api.get('/hutang', { params: { cabang, nama_karyawan: nama } })
      .then(r => setRows(r.data.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [cabang, nama]);

  const totalPinjaman = rows.reduce((s,r) => s + r.pinjaman, 0);
  const totalPelunasan = rows.reduce((s,r) => s + r.pelunasan, 0);
  const sisa = totalPinjaman - totalPelunasan;

  function startEdit(r) {
    setEditId(r.id);
    setFTgl(r.tanggal);
    setFPinjaman(r.pinjaman > 0 ? String(r.pinjaman) : '');
    setFPelunasan(r.pelunasan > 0 ? String(r.pelunasan) : '');
    setFKeterangan(r.keterangan || '');
    setMsg(null);
  }

  function cancelEdit() {
    setEditId(null);
    setFTgl(today);
    setFPinjaman(''); setFPelunasan(''); setFKeterangan('');
    setMsg(null);
  }

  async function handleSubmit() {
    if (!nama) return;
    if (!fPinjaman && !fPelunasan) { setMsg({text:'Isi pinjaman atau pelunasan', err:true}); return; }
    if (fPinjaman && fPelunasan) { setMsg({text:'Isi salah satu saja: pinjaman atau pelunasan', err:true}); return; }
    setSubmitting(true); setMsg(null);
    try {
      const body = {
        cabang, nama_karyawan: nama, tanggal: fTgl,
        pinjaman: fPinjaman ? parseFloat(fPinjaman) : 0,
        pelunasan: fPelunasan ? parseFloat(fPelunasan) : 0,
        keterangan: fKeterangan || '',
      };
      if (editId) {
        await api.put(`/hutang/${editId}`, body);
      } else {
        await api.post('/hutang', body);
      }
      setEditId(null);
      setFPinjaman(''); setFPelunasan(''); setFKeterangan('');
      setFTgl(today);
      const r = await api.get('/hutang', { params: { cabang, nama_karyawan: nama } });
      setRows(r.data.data || []);
      setMsg({text: editId ? '✅ Data diperbarui' : '✅ Tersimpan', err:false});
      setTimeout(() => setMsg(null), 2000);
    } catch(e) {
      setMsg({text:'Gagal menyimpan', err:true});
    } finally { setSubmitting(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Hapus entri ini?')) return;
    try {
      await api.delete(`/hutang/${id}`);
      setRows(p => p.filter(r => r.id !== id));
    } catch(e) { alert('Gagal menghapus'); }
  }

  const fmtRp = v => v === 0 ? '—' : 'Rp ' + Math.round(v).toLocaleString('id-ID');
  const fmtTgl = s => { const [y,m,d] = s.split('-'); return `${d}/${m}/${y.slice(2)}`; };


  const th = { padding:'10px 14px', fontSize:12, fontWeight:700, color:'#6b7280',
    textTransform:'uppercase', letterSpacing:0.5, background:'#f8fafc',
    borderBottom:'2px solid #e5e7eb', textAlign:'left', whiteSpace:'nowrap' };
  const td = { padding:'9px 14px', fontSize:13, color:'#374151', borderBottom:'1px solid #f1f5f9' };
  const inp = { width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8,
    fontSize:14, color:'#374151', outline:'none', boxSizing:'border-box' };
  const lbl = { fontSize:11, fontWeight:700, color:'#6b7280', textTransform:'uppercase',
    letterSpacing:0.5, display:'block', marginBottom:5 };

  return (
    <div style={{ padding:'24px 28px', maxWidth:1100 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <h2 style={{ fontSize:20, fontWeight:700, color:'#1e293b', margin:0 }}>💳 Hutang Karyawan</h2>
        {rows.length > 0 && (
          <button onClick={async () => {
            const token = localStorage.getItem('token');
            const res = await fetch(`/api/hutang/export-excel?cabang=${cabang}&nama_karyawan=${encodeURIComponent(nama)}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `hutang_${nama}_${cabang}.xlsx`;
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 5000);
          }}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px',
              background:'#16a34a', color:'#fff', border:'none', borderRadius:8,
              fontSize:13, fontWeight:600, cursor:'pointer' }}>
            📊 Export Excel
          </button>
        )}
      </div>

      {/* Filter */}
      <div style={{ display:'flex', gap:12, marginBottom:20, alignItems:'flex-end' }}>
        <div>
          <label style={lbl}>Cabang</label>
          <select value={cabang} onChange={e=>setCabang(e.target.value)}
            style={{ ...inp, width:200 }}>
            {CABANG_LIST.map(c => <option key={c} value={c}>{CABANG_NAMA[c]} ({c})</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Nama Karyawan</label>
          <select value={nama} onChange={e=>setNama(e.target.value)}
            style={{ ...inp, width:220 }}>
            {namaList.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        {nama && (
          <div style={{ display:'flex', gap:10 }}>
            {[['Total Pinjaman', totalPinjaman, '#dc2626'], ['Total Pelunasan', totalPelunasan, '#16a34a'], ['Sisa Hutang', sisa, sisa>0?'#b45309':'#16a34a']].map(([l,v,c]) => (
              <div key={l} style={{ background:'#fff', border:`1.5px solid ${c}22`, borderRadius:10,
                padding:'8px 16px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
                <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:0.5 }}>{l}</div>
                <div style={{ fontSize:15, fontWeight:800, color:c, marginTop:2 }}>{fmtRp(v)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 2 card layout */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:16, alignItems:'start' }}>

        {/* LEFT — Tabel */}
        <div style={{ background:'#fff', borderRadius:14, boxShadow:'0 1px 4px rgba(0,0,0,0.06)', overflow:'hidden' }}>
          {loading
            ? <div style={{ padding:40, textAlign:'center', color:'#94a3b8' }}>Memuat...</div>
            : !nama
            ? <div style={{ padding:40, textAlign:'center', color:'#94a3b8' }}>Pilih karyawan dulu</div>
            : rows.length === 0
            ? <div style={{ padding:40, textAlign:'center', color:'#94a3b8' }}>Belum ada data</div>
            : <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>Tanggal</th>
                    <th style={{...th, textAlign:'right'}}>Pinjaman</th>
                    <th style={{...th, textAlign:'right'}}>Pelunasan</th>
                    <th style={th}>Keterangan</th>
                    <th style={{...th, width:72}}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r,i) => (
                    <tr key={r.id} style={{ background: editId===r.id?'#eff6ff': i%2===0?'#fff':'#f8fafc' }}>
                      <td style={td}>{fmtTgl(r.tanggal)}</td>
                      <td style={{...td, textAlign:'right', color: r.pinjaman>0?'#dc2626':'#d1d5db', fontWeight: r.pinjaman>0?700:400}}>
                        {fmtRp(r.pinjaman)}
                      </td>
                      <td style={{...td, textAlign:'right', color: r.pelunasan>0?'#16a34a':'#d1d5db', fontWeight: r.pelunasan>0?700:400}}>
                        {fmtRp(r.pelunasan)}
                      </td>
                      <td style={{...td, color:'#64748b', fontSize:12}}>{r.keterangan || '—'}</td>
                      <td style={{...td, textAlign:'center', whiteSpace:'nowrap'}}>
                        <button onClick={()=>startEdit(r)}
                          title="Edit" style={{ background:'none', border:'none', cursor:'pointer', color:'#60a5fa', fontSize:15, padding:'2px 4px' }}>✏️</button>
                        <button onClick={()=>handleDelete(r.id)}
                          title="Hapus" style={{ background:'none', border:'none', cursor:'pointer', color:'#fca5a5', fontSize:15, padding:'2px 4px' }}>🗑</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
          }
        </div>

        {/* RIGHT — Form Input */}
        <div style={{ background:'#fff', borderRadius:14, boxShadow:'0 1px 4px rgba(0,0,0,0.06)', padding:20 }}>
          <div style={{ fontSize:14, fontWeight:700, color:'#1e293b', marginBottom:16 }}>
            {editId ? '✏️ Edit Data' : '➕ Input Data'}
          </div>

          <div style={{ marginBottom:14 }}>
            <label style={lbl}>Tanggal</label>
            <input type="date" value={fTgl} onChange={e=>setFTgl(e.target.value)} style={inp}/>
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={lbl}>Pinjaman (Rp)</label>
            <input type="number" value={fPinjaman} onChange={e=>{setFPinjaman(e.target.value); setFPelunasan('');}}
              placeholder="0" style={inp}/>
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={lbl}>Pelunasan (Rp)</label>
            <input type="number" value={fPelunasan} onChange={e=>{setFPelunasan(e.target.value); setFPinjaman('');}}
              placeholder="0" style={inp}/>
          </div>
          <div style={{ marginBottom:18 }}>
            <label style={lbl}>Keterangan</label>
            <input type="text" value={fKeterangan} onChange={e=>setFKeterangan(e.target.value)}
              placeholder="Opsional" style={inp}/>
          </div>

          {msg && (
            <div style={{ marginBottom:12, padding:'8px 12px', borderRadius:8, fontSize:13, fontWeight:600,
              background: msg.err?'#fef2f2':'#f0fdf4', color: msg.err?'#dc2626':'#16a34a',
              border: `1px solid ${msg.err?'#fecaca':'#bbf7d0'}` }}>
              {msg.text}
            </div>
          )}

          <button onClick={handleSubmit} disabled={submitting || !nama}
            style={{ width:'100%', padding:'11px', background: submitting||!nama?'#94a3b8': editId?'#0369a1':'#1e40af',
              color:'#fff', border:'none', borderRadius:9, fontSize:14, fontWeight:700,
              cursor: submitting||!nama?'not-allowed':'pointer' }}>
            {submitting ? 'Menyimpan...' : editId ? '💾 Update' : '💾 Simpan'}
          </button>

          {editId && (
            <button onClick={cancelEdit}
              style={{ width:'100%', marginTop:8, padding:'10px', background:'none',
                color:'#64748b', border:'1.5px solid #e2e8f0', borderRadius:9, fontSize:14,
                fontWeight:600, cursor:'pointer' }}>
              Batal Edit
            </button>
          )}

          {!editId && (
            <div style={{ marginTop:12, padding:'10px 12px', background:'#f8fafc', borderRadius:8, fontSize:12, color:'#94a3b8' }}>
              💡 Isi salah satu saja — pinjaman <strong>atau</strong> pelunasan
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =====================
// KERJA HARIAN PAGE
// =====================
const PEKERJAAN_KEYS = ['Cuci','Kering','Lipat','Setrika','Packing'];
const PEKERJAAN_ICON2 = {Cuci:'🫧',Kering:'💨',Lipat:'👕',Setrika:'🔥',Packing:'📦'};

function KerjaHarianPage() {
  const now = new Date();
  const [cabang, setCabang] = useState('PO');
  const [bulan, setBulan] = useState(now.getMonth() + 1);
  const [tahun, setTahun] = useState(now.getFullYear());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('rekap');
  const [filterKaryawan, setFilterKaryawan] = useState('');

  const CABANG_LIST = ['PO','DM','PM','TM','MT'];
  const CABANG_NAMA = {PO:'Puri Orchard',DM:'Daan Mogot',PM:'Palm Mansion',TM:'Tomang',MT:'Metro'};
  const BULAN_NAMA2 = ['','Jan','Feb','Mar','Apr','Mei','Jun','Jul','Agu','Sep','Okt','Nov','Des'];

  async function load() {
    setLoading(true);
    try {
      const res = await api.get('/kerja/laporan', { params: { cabang, bulan, tahun } });
      setData(res.data.data || []);
    } catch(e) {
      setData([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [cabang, bulan, tahun]);

  // Group by karyawan for rekap
  const rekap = (() => {
    const map = {};
    data.forEach(r => {
      if (!map[r.nama_karyawan]) {
        map[r.nama_karyawan] = { nama: r.nama_karyawan, total_nota: 0, total_kg: 0 };
        PEKERJAAN_KEYS.forEach(k => { map[r.nama_karyawan][k] = { nota: 0, kg: 0 }; });
      }
      const m = map[r.nama_karyawan];
      m.total_nota++;
      m.total_kg += r.berat_kg || 0;
      if (m[r.pekerjaan]) { m[r.pekerjaan].nota++; m[r.pekerjaan].kg += r.berat_kg || 0; }
    });
    return Object.values(map).sort((a,b) => b.total_nota - a.total_nota);
  })();

  const fmtKg2 = v => v === 0 ? '-' : (v % 1 === 0 ? v : v.toFixed(1)) + ' kg';
  const fmtTgl = s => { const [y,m,d] = s.split('-'); return `${d}/${m}`; };

  const filteredData = filterKaryawan
    ? data.filter(r => r.nama_karyawan === filterKaryawan)
    : data;

  const thStyle = { padding:'10px 12px', fontSize:12, fontWeight:700, color:'#6b7280', textTransform:'uppercase', letterSpacing:0.5,
    background:'#f8fafc', borderBottom:'2px solid #e5e7eb', whiteSpace:'nowrap', textAlign:'left' };
  const tdStyle = { padding:'9px 12px', fontSize:13, color:'#374151', borderBottom:'1px solid #f1f5f9', verticalAlign:'top' };

  const totalNota = rekap.reduce((a,r) => a + r.total_nota, 0);
  const totalKg = rekap.reduce((a,r) => a + r.total_kg, 0);

  return (
    <div style={{ padding:'24px 28px', maxWidth:1200 }}>
      <h2 style={{ fontSize:20, fontWeight:700, color:'#1e293b', marginBottom:20 }}>👔 Laporan Kerja Harian</h2>

      {/* Filter */}
      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap', alignItems:'flex-end' }}>
        {[['Cabang', <select value={cabang} onChange={e=>setCabang(e.target.value)} style={styles.filterSelect}>
          {CABANG_LIST.map(c => <option key={c} value={c}>{CABANG_NAMA[c]} ({c})</option>)}
        </select>],
        ['Bulan', <select value={bulan} onChange={e=>setBulan(+e.target.value)} style={styles.filterSelect}>
          {BULAN_NAMA2.slice(1).map((n,i) => <option key={i+1} value={i+1}>{n}</option>)}
        </select>],
        ['Tahun', <select value={tahun} onChange={e=>setTahun(+e.target.value)} style={styles.filterSelect}>
          {[2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
        </select>]].map(([lbl, el]) => (
          <div key={lbl}>
            <div style={{ fontSize:11, fontWeight:600, color:'#94a3b8', textTransform:'uppercase', letterSpacing:0.5, marginBottom:4 }}>{lbl}</div>
            {el}
          </div>
        ))}
      </div>

      {/* Summary chips */}
      <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap' }}>
        {[['Total Nota',totalNota,'#3b82f6'],['Total KG',fmtKg2(totalKg),'#059669'],['Karyawan',rekap.length,'#7c3aed']].map(([l,v,c]) => (
          <div key={l} style={{ background:'#fff', border:`1.5px solid ${c}22`, borderRadius:12, padding:'10px 18px', display:'flex', alignItems:'center', gap:10, boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize:20, fontWeight:800, color:c }}>{v}</div>
            <div style={{ fontSize:11, fontWeight:600, color:'#94a3b8', textTransform:'uppercase', letterSpacing:0.5 }}>{l}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:16 }}>
        {[['rekap','📊 Rekap per Karyawan'],['detail','📋 Detail Data']].map(([id,lbl]) => (
          <button key={id} onClick={() => setTab(id)} style={{
            padding:'8px 16px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight:600,
            background: tab===id ? '#276749' : '#f1f5f9',
            color: tab===id ? '#fff' : '#64748b',
          }}>{lbl}</button>
        ))}
      </div>

      {loading && <div style={{ textAlign:'center', padding:40, color:'#94a3b8' }}>Memuat...</div>}

      {!loading && tab === 'rekap' && (
        <div style={{ background:'#fff', borderRadius:14, boxShadow:'0 1px 4px rgba(0,0,0,0.06)', overflow:'hidden' }}>
          {rekap.length === 0
            ? <div style={{ textAlign:'center', padding:40, color:'#94a3b8' }}>Tidak ada data</div>
            : <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Karyawan</th>
                      {PEKERJAAN_KEYS.map(k => (
                        <th key={k} style={{...thStyle, textAlign:'center'}}>
                          {PEKERJAAN_ICON2[k]} {k}
                        </th>
                      ))}
                      <th style={{...thStyle, textAlign:'center'}}>Total Nota</th>
                      <th style={{...thStyle, textAlign:'center'}}>Total KG</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rekap.map((r,i) => (
                      <tr key={r.nama} style={{ background: i%2===0 ? '#fff' : '#f8fafc' }}>
                        <td style={{ ...tdStyle, fontWeight:600, color:'#1e293b' }}>{r.nama}</td>
                        {PEKERJAAN_KEYS.map(k => (
                          <td key={k} style={{ ...tdStyle, textAlign:'center' }}>
                            {r[k].nota > 0 ? (
                              <>
                                <div style={{ fontWeight:700, color:'#276749' }}>{r[k].nota}</div>
                                <div style={{ fontSize:11, color:'#94a3b8' }}>{fmtKg2(r[k].kg)}</div>
                              </>
                            ) : <span style={{ color:'#e2e8f0' }}>—</span>}
                          </td>
                        ))}
                        <td style={{ ...tdStyle, textAlign:'center', fontWeight:800, color:'#3b82f6', fontSize:15 }}>{r.total_nota}</td>
                        <td style={{ ...tdStyle, textAlign:'center', fontWeight:700, color:'#059669' }}>{fmtKg2(r.total_kg)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
          }
        </div>
      )}

      {!loading && tab === 'detail' && (
        <div style={{ background:'#fff', borderRadius:14, boxShadow:'0 1px 4px rgba(0,0,0,0.06)', overflow:'hidden' }}>
          {/* Karyawan filter */}
          <div style={{ padding:'12px 16px', borderBottom:'1px solid #f1f5f9', display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ fontSize:12, fontWeight:600, color:'#94a3b8' }}>Filter karyawan:</span>
            <select value={filterKaryawan} onChange={e=>setFilterKaryawan(e.target.value)} style={{ ...styles.filterSelect, marginBottom:0 }}>
              <option value="">Semua karyawan</option>
              {rekap.map(r => <option key={r.nama} value={r.nama}>{r.nama}</option>)}
            </select>
            <span style={{ fontSize:12, color:'#94a3b8' }}>{filteredData.length} entri</span>
          </div>
          {filteredData.length === 0
            ? <div style={{ textAlign:'center', padding:40, color:'#94a3b8' }}>Tidak ada data</div>
            : <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr>
                      {['Tgl','Karyawan','Customer','Pekerjaan','PCS','KG'].map(h => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((r,i) => (
                      <tr key={r.id} style={{ background: i%2===0 ? '#fff' : '#f8fafc' }}>
                        <td style={{ ...tdStyle, whiteSpace:'nowrap', color:'#64748b', fontSize:12 }}>{fmtTgl(r.tanggal)}</td>
                        <td style={{ ...tdStyle, fontWeight:600 }}>{r.nama_karyawan}</td>
                        <td style={{ ...tdStyle }}>
                          <div style={{ fontSize:15, fontWeight:700, color:'#111827' }}>{r.nama_customer}</div>
                          {r.no_nota && <div style={{ fontSize:13, fontWeight:700, color:'#111827', fontFamily:'monospace' }}>#{r.no_nota.slice(-5)}</div>}
                        </td>
                        <td style={{ ...tdStyle }}>
                          <span style={{ background:'#f0fdf4', color:'#276749', padding:'3px 8px', borderRadius:6, fontSize:12, fontWeight:700 }}>
                            {PEKERJAAN_ICON2[r.pekerjaan]} {r.pekerjaan}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, textAlign:'center', fontWeight:700 }}>{r.jumlah_pcs}</td>
                        <td style={{ ...tdStyle, textAlign:'center', color:'#059669', fontWeight:600 }}>{fmtKg2(r.berat_kg)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
          }
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(localStorage.getItem('fullname'));
  const [page, setPage] = useState('dashboard');
  const [pageKey, setPageKey] = useState(0);

  const handleLogin = (fullname) => setUser(fullname);
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('fullname');
    setUser(null);
  };

  if (!user) return <LoginPage onLogin={handleLogin} />;

  return (
    <div style={styles.appContainer}>
      {/* Sidebar */}
      <div style={styles.sidebar}>

        {/* ── Logo ── */}
        <div style={{ padding: '12px 16px 8px', display: 'flex', alignItems: 'center', gap: 9 }}>
          <div style={{
            width: 36, height: 36, flexShrink: 0,
            background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
            borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 3px 10px rgba(59,130,246,0.4)',
          }}>
            <svg width="20" height="20" viewBox="0 0 28 28" fill="none">
              <circle cx="14" cy="14" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" fill="rgba(255,255,255,0.08)"/>
              <path d="M6 14 Q8.5 11.5 11 14 Q13.5 16.5 16 14 Q18.5 11.5 22 14" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round"/>
              <path d="M9 10 Q10.5 8.5 12 10 Q13.5 11.5 15 10 Q16.5 8.5 18 10" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
            </svg>
          </div>
          <div>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: 12, letterSpacing: -0.2, lineHeight: 1.2 }}>Mommy Wash</div>
            <div style={{ color: 'rgba(255,255,255,0.38)', fontSize: 9, fontWeight: 500, letterSpacing: 1.2, textTransform: 'uppercase' }}>Laundry</div>
          </div>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', margin: '6px 14px 4px' }} />

        {/* ── Nav Groups ── */}
        <nav style={{ flex: 1, overflowY: 'auto', padding: '0 8px' }}>
          {[
            { group: 'OVERVIEW', items: [
              { id: 'dashboard', emoji: '📊', label: 'Dashboard' },
            ]},
            { group: 'DATA', items: [
              { id: 'upload',  emoji: '📤', label: 'Upload Data' },
              { id: 'detail',  emoji: '📋', label: 'Detail Transaksi' },
              { id: 'status',  emoji: '🔍', label: 'Status Upload' },
            ]},
            { group: 'KEUANGAN', items: [
              { id: 'qris',             emoji: '💳', label: 'Laporan QRIS' },
              { id: 'settlement-qris',  emoji: '↳',  label: 'Settlement QRIS', sub: true },
              { id: 'transfer',         emoji: '🔄', label: 'Transfer Masuk' },
              { id: 'pelunasan',    emoji: '💵', label: 'Data Pelunasan' },
              { id: 'laporankasir', emoji: '🧾', label: 'Laporan Kasir' },
            ]},
            { group: 'SDM', items: [
              { id: 'karyawan',   emoji: '👥', label: 'Data Karyawan' },
              { id: 'kerja',      emoji: '👔', label: 'Kerja Harian' },
              { id: 'absensi',    emoji: '📋', label: 'Laporan Absensi' },
              { id: 'gaji',       emoji: '💼', label: 'Gaji Karyawan' },
              { id: 'hutang',     emoji: '💳', label: 'Hutang Karyawan' },
              { id: 'cash',       emoji: '💵', label: 'Cash Harian' },
              { id: 'pengeluaran',emoji: '💰', label: 'Pengeluaran' },
              { id: 'kasbon',     emoji: '🤝', label: 'Kasbon' },
            ]},
            { group: 'GUDANG', items: [
              { id: 'stok', emoji: '📦', label: 'Stok Gudang' },
            ]},
            { group: 'SISTEM', items: [
              { id: 'cctv',       emoji: '📷', label: 'CCTV Monitor' },
              { id: 'bot', emoji: '🤖', label: 'Status Bot' },
              { id: 'pengaturan', emoji: '⚙️', label: 'Pengaturan Akun' },
            ]},
          ].map(({ group, items }) => (
            <div key={group} style={{ marginBottom: 2 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.28)', letterSpacing: 1.3, textTransform: 'uppercase', padding: '10px 10px 4px' }}>
                {group}
              </div>
              {items.map(item => {
                const isActive = page === item.id;
                return (
                  <div key={item.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 9,
                      padding: item.sub ? '5px 10px 5px 22px' : '8px 10px',
                      borderRadius: 9, cursor: 'pointer', marginBottom: 1,
                      transition: 'background 0.15s, color 0.15s',
                      background: isActive ? 'rgba(255,255,255,0.12)' : 'transparent',
                      color: isActive ? '#fff' : item.sub ? 'rgba(255,255,255,0.42)' : 'rgba(255,255,255,0.58)',
                      fontWeight: isActive ? 600 : 400,
                      boxShadow: isActive ? 'inset 3px 0 0 #60a5fa' : 'none',
                    }}
                    onClick={() => { setPage(item.id); setPageKey(k => k+1); }}
                    onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; }}}
                    onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = item.sub ? 'rgba(255,255,255,0.42)' : 'rgba(255,255,255,0.58)'; }}}
                  >
                    <span style={{ fontSize: item.sub ? 11 : 14 }}>{item.emoji}</span>
                    <span style={{ fontSize: item.sub ? 12 : 13 }}>{item.label}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </nav>

        {/* ── Footer ── */}
        <div style={{ padding: '10px 10px 16px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '6px 6px 10px' }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg, #60a5fa, #2563eb)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 700, color: '#fff', letterSpacing: -0.5,
            }}>
              {user ? user.trim().split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase() : '?'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: '#fff', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user}</div>
              <div style={{ color: 'rgba(255,255,255,0.38)', fontSize: 10 }}>Admin</div>
            </div>
          </div>
          <button style={styles.btnLogout} onClick={handleLogout}>Keluar</button>
        </div>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {page === 'dashboard' && <DashboardPage />}
        {page === 'upload' && <UploadPage />}
        {page === 'detail' && <DetailPage />}
        {page === 'status' && <StatusPage />}
        {page === 'qris' && <QrisPage />}
        {page === 'settlement-qris' && <SettlementQrisPage />}
        {page === 'transfer' && <TransferPage />}
        {page === 'pelunasan' && <PelunasanPage />}
        {page === 'absensi' && <AbsensiPage />}
        {page === 'karyawan' && <KaryawanPage key={pageKey} />}
        {page === 'kerja' && <KerjaHarianPage key={pageKey} />}
        {page === 'hutang' && <HutangPage key={pageKey} />}
        {page === 'cash' && <CashHarianPage key={pageKey} />}
        {page === 'gaji' && <GajiPage key={pageKey} />}
        {page === 'pengeluaran' && <PengeluaranPage key={pageKey} />}
        {page === 'kasbon' && <KasbonPage key={pageKey} />}
        {page === 'laporankasir' && <LaporanKasirPage />}
        {page === 'stok' && <StokPage key={pageKey} />}
        {page === 'cctv' && <CctvPage key={pageKey} />}
        {page === 'bot' && <BotPage />}
        {page === 'pengaturan' && <PengaturanAkunPage />}
      </div>
    </div>
  );
}

// =====================
// CASH HARIAN PAGE
// =====================
const PECAHAN = [100000, 50000, 20000, 10000, 5000, 2000, 1000];
const fmtPecahan = v => v >= 1000 ? (v/1000) + 'rb' : v;

function PeriodeCard({ title, color, periode, cabang, bulan, tahun, totalLaporan, totalFisikHarian = 0, totalPengeluaran = 0, dailyRows = [] }) {
  const initJml = () => Object.fromEntries(PECAHAN.map(p => [`jml_${p}`, '']));
  const [jml, setJml] = useState(initJml());
  const [totalFisik, setTotalFisik] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fmtRp = v => 'Rp ' + Math.round(v).toLocaleString('id-ID');

  useEffect(() => {
    api.get('/cash/periode', { params: { cabang, bulan, tahun } })
      .then(r => {
        const d = r.data[String(periode)];
        if (d) {
          setJml(Object.fromEntries(PECAHAN.map(p => [`jml_${p}`, d[`jml_${p}`] || ''])));
          setTotalFisik(d.total || 0);
        } else { setJml(initJml()); setTotalFisik(0); }
      }).catch(() => {});
  }, [cabang, bulan, tahun, periode]);

  useEffect(() => {
    setTotalFisik(PECAHAN.reduce((s, p) => s + (parseInt(jml[`jml_${p}`]) || 0) * p, 0));
  }, [jml]);

  async function handleSave() {
    setSaving(true);
    try {
      const body = { cabang, bulan, tahun, periode };
      PECAHAN.forEach(p => { body[`jml_${p}`] = parseInt(jml[`jml_${p}`]) || 0; });
      await api.post('/cash/periode', body);
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch(e) {} finally { setSaving(false); }
  }

  const selisih = totalFisikHarian - totalLaporan;
  const selisihColor = selisih === 0 ? '#16a34a' : selisih > 0 ? '#1e40af' : '#dc2626';
  const inp = { width:'100%', padding:'5px 8px', border:'1.5px solid #e2e8f0', borderRadius:6,
    fontSize:13, color:'#374151', outline:'none', boxSizing:'border-box', textAlign:'center' };

  return (
    <div style={{ background:'#fff', borderRadius:12, boxShadow:'0 1px 4px rgba(0,0,0,0.06)', padding:16, flex:1 }}>
      <div style={{ fontSize:13, fontWeight:700, color, marginBottom:10 }}>{title}</div>

      {/* Summary chips */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:6, marginBottom:12 }}>
        {[['Cash Laporan', totalLaporan, '#64748b'], ['Cash Fisik', totalFisikHarian, '#1e40af'], ['Selisih', selisih, selisihColor], ['Pengeluaran', totalPengeluaran, '#b45309']].map(([l,v,c]) => (
          <div key={l} style={{ background:'#f8fafc', borderRadius:7, padding:'7px 8px' }}>
            <div style={{ fontSize:10, color:'#94a3b8', textTransform:'uppercase', fontWeight:700, marginBottom:2 }}>{l}</div>
            <div style={{ fontSize:12, fontWeight:800, color:c }}>{fmtRp(v)}</div>
          </div>
        ))}
      </div>

      {/* Tabel detail harian */}
      {(() => {
        const lastDay = new Date(tahun, bulan, 0).getDate();
        const startDay = periode === 1 ? 1 : 16;
        const endDay   = periode === 1 ? 15 : lastDay;
        const dataByDay = Object.fromEntries(dailyRows.map(r => [parseInt(r.tanggal.split('-')[2]), r]));
        const days = Array.from({length: endDay - startDay + 1}, (_, i) => startDay + i);
        return (
          <div style={{ borderTop:'1px solid #f1f5f9', paddingTop:10, marginBottom:12 }}>
            <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:0.5, marginBottom:6 }}>Detail Harian</div>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr>
                  {['Tgl','Laporan','Fisik','Selisih'].map(h => (
                    <th key={h} style={{ fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase',
                      padding:'3px 4px', borderBottom:'1px solid #e5e7eb', textAlign: h==='Tgl'?'left':'right' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {days.map(d => {
                  const r = dataByDay[d];
                  const sel = r ? r.cash_fisik - r.cash_laporan : null;
                  return (
                    <tr key={d} style={{ background: d%2===0?'#f8fafc':'#fff' }}>
                      <td style={{ padding:'3px 4px', fontSize:11, color:'#374151', fontWeight:600 }}>{String(d).padStart(2,'0')}</td>
                      <td style={{ padding:'3px 4px', fontSize:11, color:'#64748b', textAlign:'right' }}>{r ? fmtRp(r.cash_laporan) : '—'}</td>
                      <td style={{ padding:'3px 4px', fontSize:11, color:'#374151', textAlign:'right' }}>{r ? fmtRp(r.cash_fisik) : '—'}</td>
                      <td style={{ padding:'3px 4px', fontSize:11, fontWeight:700, textAlign:'right',
                        color: sel===null?'#d1d5db': sel===0?'#16a34a': sel>0?'#1e40af':'#dc2626' }}>
                        {sel===null ? '—' : (sel>=0?'+':'')+fmtRp(sel)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* Pecahan selalu tampil */}
      <div style={{ borderTop:'1px solid #f1f5f9', paddingTop:10 }}>
        <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:0.5, marginBottom:8 }}>Pecahan Fisik</div>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <tbody>
            {PECAHAN.map(p => (
              <tr key={p}>
                <td style={{ padding:'3px 0', fontSize:12, color:'#64748b', width:45 }}>{fmtPecahan(p)}</td>
                <td style={{ padding:'3px 4px' }}>
                  <input type="number" min="0" value={jml[`jml_${p}`]}
                    onChange={e => setJml(prev => ({...prev, [`jml_${p}`]: e.target.value}))}
                    placeholder="0" style={inp}/>
                </td>
                <td style={{ padding:'3px 0', fontSize:12, textAlign:'right', color:'#374151', width:90, fontWeight: (parseInt(jml[`jml_${p}`])||0)>0?600:400 }}>
                  {(parseInt(jml[`jml_${p}`])||0) > 0 ? fmtRp((parseInt(jml[`jml_${p}`])||0)*p) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
          borderTop:'1.5px solid #e5e7eb', marginTop:6, paddingTop:6 }}>
          <span style={{ fontSize:12, fontWeight:700, color:'#374151' }}>Total</span>
          <span style={{ fontSize:13, fontWeight:800, color: color }}>{fmtRp(totalFisik)}</span>
        </div>
        <button onClick={handleSave} disabled={saving}
          style={{ width:'100%', marginTop:8, padding:'8px', background: saving?'#94a3b8':color,
            color:'#fff', border:'none', borderRadius:7, fontSize:13, fontWeight:700, cursor:'pointer' }}>
          {saving ? 'Menyimpan...' : saved ? '✅ Tersimpan' : '💾 Simpan Pecahan'}
        </button>
      </div>
    </div>
  );
}

function CashHarianPage() {
  const CABANG_LIST = ['PO','DM','PM','TM','MT'];
  const CABANG_NAMA = {PO:'Puri Orchard',DM:'Daan Mogot',PM:'Palm Mansion',TM:'Tomang',MT:'Metro'};
  const BULAN_NAMA = ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];
  const now = new Date();
  const [cabang, setCabang] = useState('PO');
  const [bulan, setBulan] = useState(now.getMonth() + 1);
  const [tahun, setTahun] = useState(now.getFullYear());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const today = new Date().toISOString().slice(0,10);
  const [fTgl, setFTgl] = useState(today);
  const [fLaporan, setFLaporan] = useState('');
  const [fFisik, setFFisik] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);
  const [editId, setEditId] = useState(null);

  // Pengeluaran state
  const [pRows, setPRows] = useState([]);
  const [pTgl, setPTgl] = useState(today);
  const [pKet, setPKet] = useState('');
  const [pNom, setPNom] = useState('');
  const [pSubmitting, setPSubmitting] = useState(false);
  const [pMsg, setPMsg] = useState(null);

  // Total pecahan bulanan
  const [periodeData, setPeriodeData] = useState({});

  function load() {
    setLoading(true);
    api.get('/cash', { params: { cabang, bulan, tahun } })
      .then(r => setRows(r.data.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }

  function loadPengeluaran() {
    api.get('/cash/pengeluaran', { params: { cabang, bulan, tahun } })
      .then(r => setPRows(r.data.data || []))
      .catch(() => setPRows([]));
  }

  function loadPeriode() {
    api.get('/cash/periode', { params: { cabang, bulan, tahun } })
      .then(r => setPeriodeData(r.data || {}))
      .catch(() => setPeriodeData({}));
  }

  useEffect(() => { load(); loadPengeluaran(); loadPeriode(); }, [cabang, bulan, tahun]);

  const rows1_15   = rows.filter(r => parseInt(r.tanggal.split('-')[2]) <= 15);
  const rows16_end = rows.filter(r => parseInt(r.tanggal.split('-')[2]) > 15);
  const sumLaporan1 = rows1_15.reduce((s,r) => s + r.cash_laporan, 0);
  const sumLaporan2 = rows16_end.reduce((s,r) => s + r.cash_laporan, 0);
  const sumFisik1   = rows1_15.reduce((s,r) => s + r.cash_fisik, 0);
  const sumFisik2   = rows16_end.reduce((s,r) => s + r.cash_fisik, 0);
  const sumPengeluaran1 = pRows.filter(r => parseInt(r.tanggal.split('-')[2]) <= 15).reduce((s,r) => s + r.nominal, 0);
  const sumPengeluaran2 = pRows.filter(r => parseInt(r.tanggal.split('-')[2]) > 15).reduce((s,r) => s + r.nominal, 0);
  const lastDay = new Date(tahun, bulan, 0).getDate();
  const tahunList = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];

  function startEdit(r) {
    setEditId(r.id); setFTgl(r.tanggal);
    setFLaporan(r.cash_laporan > 0 ? String(r.cash_laporan) : '');
    setFFisik(r.cash_fisik > 0 ? String(r.cash_fisik) : '');
    setMsg(null);
  }

  function cancelEdit() {
    setEditId(null); setFTgl(today); setFLaporan(''); setFFisik(''); setMsg(null);
  }

  async function handleSubmit() {
    if (!fLaporan && !fFisik) { setMsg({text:'Isi cash laporan atau cash fisik', err:true}); return; }
    setSubmitting(true); setMsg(null);
    try {
      await api.post('/cash', {
        cabang, tanggal: fTgl,
        cash_laporan: parseFloat(fLaporan) || 0,
        cash_fisik: parseFloat(fFisik) || 0,
      });
      setEditId(null); setFLaporan(''); setFFisik(''); setFTgl(today);
      load();
      setMsg({text: editId ? '✅ Diperbarui' : '✅ Tersimpan', err:false});
      setTimeout(() => setMsg(null), 2000);
    } catch(e) {
      setMsg({text:'Gagal menyimpan', err:true});
    } finally { setSubmitting(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Hapus entri ini?')) return;
    try {
      await api.delete(`/cash/${id}`);
      setRows(p => p.filter(r => r.id !== id));
    } catch(e) { alert('Gagal menghapus'); }
  }

  async function handleAddPengeluaran() {
    if (!pNom || !pKet) { setPMsg({text:'Isi keterangan dan nominal', err:true}); return; }
    setPSubmitting(true); setPMsg(null);
    try {
      await api.post('/cash/pengeluaran', {
        cabang, tanggal: pTgl,
        nominal: parseFloat(pNom) || 0,
        keterangan: pKet,
      });
      setPKet(''); setPNom(''); setPTgl(today);
      loadPengeluaran();
      setPMsg({text:'✅ Tersimpan', err:false});
      setTimeout(() => setPMsg(null), 2000);
    } catch(e) { setPMsg({text:'Gagal menyimpan', err:true}); }
    finally { setPSubmitting(false); }
  }

  async function handleDeletePengeluaran(id) {
    if (!window.confirm('Hapus pengeluaran ini?')) return;
    try {
      await api.delete(`/cash/pengeluaran/${id}`);
      setPRows(p => p.filter(r => r.id !== id));
    } catch(e) { alert('Gagal menghapus'); }
  }

  const fmtRp = v => 'Rp ' + Math.round(v).toLocaleString('id-ID');
  const fmtTgl = s => { const [y,m,d] = s.split('-'); return `${d}/${m}/${y.slice(2)}`; };

  const th = { padding:'9px 12px', fontSize:11, fontWeight:700, color:'#6b7280',
    textTransform:'uppercase', letterSpacing:0.4, background:'#f8fafc',
    borderBottom:'2px solid #e5e7eb', whiteSpace:'nowrap' };
  const td = { padding:'8px 12px', fontSize:13, color:'#374151', borderBottom:'1px solid #f1f5f9' };
  const inp = { width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8,
    fontSize:14, color:'#374151', outline:'none', boxSizing:'border-box' };
  const lbl = { fontSize:11, fontWeight:700, color:'#6b7280', textTransform:'uppercase',
    letterSpacing:0.5, display:'block', marginBottom:5 };

  return (
    <div style={{ padding:'24px 28px', maxWidth:1100 }}>
      <h2 style={{ fontSize:20, fontWeight:700, color:'#1e293b', marginBottom:20 }}>💵 Cash Harian</h2>

      {/* Filter */}
      <div style={{ display:'flex', gap:12, marginBottom:20, alignItems:'flex-end', flexWrap:'wrap' }}>
        <div>
          <label style={lbl}>Cabang</label>
          <select value={cabang} onChange={e=>setCabang(e.target.value)} style={{ ...inp, width:200 }}>
            {CABANG_LIST.map(c => <option key={c} value={c}>{CABANG_NAMA[c]} ({c})</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Bulan</label>
          <select value={bulan} onChange={e=>setBulan(Number(e.target.value))} style={{ ...inp, width:140 }}>
            {BULAN_NAMA.map((n,i) => <option key={i+1} value={i+1}>{n}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Tahun</label>
          <select value={tahun} onChange={e=>setTahun(Number(e.target.value))} style={{ ...inp, width:100 }}>
            {tahunList.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* Main layout: 2 kolom */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 340px', gap:16, alignItems:'start' }}>

        {/* Kolom kiri: Card Periode 1-15, lalu 16-akhir */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <PeriodeCard
            title={`Periode 1–15 ${BULAN_NAMA[bulan-1]} ${tahun}`} color="#1e40af"
            periode={1} cabang={cabang} bulan={bulan} tahun={tahun}
            totalLaporan={sumLaporan1} totalFisikHarian={sumFisik1} totalPengeluaran={sumPengeluaran1} dailyRows={rows1_15}
          />
          <PeriodeCard
            title={`Periode 16–${lastDay} ${BULAN_NAMA[bulan-1]} ${tahun}`} color="#7c3aed"
            periode={2} cabang={cabang} bulan={bulan} tahun={tahun}
            totalLaporan={sumLaporan2} totalFisikHarian={sumFisik2} totalPengeluaran={sumPengeluaran2} dailyRows={rows16_end}
          />
        </div>

        {/* Kolom kanan: Form input harian, lalu form pengeluaran */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

          {/* Form input harian */}
          <div style={{ background:'#fff', borderRadius:14, boxShadow:'0 1px 4px rgba(0,0,0,0.06)', padding:20 }}>
            <div style={{ fontSize:14, fontWeight:700, color:'#1e293b', marginBottom:16 }}>
              {editId ? '✏️ Edit Data Harian' : '➕ Input Cash Harian'}
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={lbl}>Tanggal</label>
              <input type="date" value={fTgl} onChange={e=>setFTgl(e.target.value)} style={inp}/>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={lbl}>Cash Laporan (Rp)</label>
              <input type="number" value={fLaporan} onChange={e=>setFLaporan(e.target.value)}
                placeholder="0" style={inp}/>
            </div>
            <div style={{ marginBottom:18 }}>
              <label style={lbl}>Cash Fisik (Rp)</label>
              <input type="number" value={fFisik} onChange={e=>setFFisik(e.target.value)}
                placeholder="0" style={inp}/>
            </div>
            {msg && (
              <div style={{ marginBottom:12, padding:'8px 12px', borderRadius:8, fontSize:13, fontWeight:600,
                background: msg.err?'#fef2f2':'#f0fdf4', color: msg.err?'#dc2626':'#16a34a',
                border:`1px solid ${msg.err?'#fecaca':'#bbf7d0'}` }}>
                {msg.text}
              </div>
            )}
            <button onClick={handleSubmit} disabled={submitting}
              style={{ width:'100%', padding:'11px', background: submitting?'#94a3b8': editId?'#0369a1':'#1e40af',
                color:'#fff', border:'none', borderRadius:9, fontSize:14, fontWeight:700, cursor: submitting?'not-allowed':'pointer' }}>
              {submitting ? 'Menyimpan...' : editId ? '💾 Update' : '💾 Simpan'}
            </button>
            {editId && (
              <button onClick={cancelEdit}
                style={{ width:'100%', marginTop:8, padding:'10px', background:'none',
                  color:'#64748b', border:'1.5px solid #e2e8f0', borderRadius:9,
                  fontSize:13, fontWeight:600, cursor:'pointer' }}>
                Batal Edit
              </button>
            )}
          </div>

          {/* Form pengeluaran */}
          <div style={{ background:'#fff', borderRadius:14, boxShadow:'0 1px 4px rgba(0,0,0,0.06)', padding:20 }}>
            <div style={{ fontSize:14, fontWeight:700, color:'#1e293b', marginBottom:16 }}>💸 Input Pengeluaran</div>
            <div style={{ marginBottom:12 }}>
              <label style={lbl}>Tanggal</label>
              <input type="date" value={pTgl} onChange={e=>setPTgl(e.target.value)} style={inp}/>
            </div>
            <div style={{ marginBottom:12 }}>
              <label style={lbl}>Keterangan</label>
              <input type="text" value={pKet} onChange={e=>setPKet(e.target.value)}
                placeholder="Keterangan pengeluaran..." style={inp}/>
            </div>
            <div style={{ marginBottom:16 }}>
              <label style={lbl}>Nominal (Rp)</label>
              <input type="number" value={pNom} onChange={e=>setPNom(e.target.value)}
                placeholder="0" style={inp}/>
            </div>
            {pMsg && (
              <div style={{ marginBottom:10, padding:'7px 12px', borderRadius:8, fontSize:13, fontWeight:600,
                background: pMsg.err?'#fef2f2':'#f0fdf4', color: pMsg.err?'#dc2626':'#16a34a',
                border:`1px solid ${pMsg.err?'#fecaca':'#bbf7d0'}` }}>
                {pMsg.text}
              </div>
            )}
            <button onClick={handleAddPengeluaran} disabled={pSubmitting}
              style={{ width:'100%', padding:'11px', background: pSubmitting?'#94a3b8':'#0f766e',
                color:'#fff', border:'none', borderRadius:9, fontSize:14, fontWeight:700,
                cursor: pSubmitting?'not-allowed':'pointer' }}>
              {pSubmitting ? 'Menyimpan...' : '+ Tambah'}
            </button>

            {/* Tabel pengeluaran */}
            {pRows.length > 0 && (
              <div style={{ marginTop:16, borderTop:'1px solid #f1f5f9', paddingTop:14 }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr>
                      <th style={th}>Tgl</th>
                      <th style={th}>Keterangan</th>
                      <th style={{...th, textAlign:'right'}}>Nominal</th>
                      <th style={{...th, width:30}}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pRows.map((r,i) => (
                      <tr key={r.id} style={{ background: i%2===0?'#fff':'#f8fafc' }}>
                        <td style={{...td, whiteSpace:'nowrap', fontSize:12}}>{fmtTgl(r.tanggal)}</td>
                        <td style={{...td, fontSize:12}}>{r.keterangan}</td>
                        <td style={{...td, textAlign:'right', fontWeight:600, fontSize:12}}>{fmtRp(r.nominal)}</td>
                        <td style={td}>
                          <button onClick={()=>handleDeletePengeluaran(r.id)}
                            style={{ background:'none', border:'none', cursor:'pointer', color:'#fca5a5', fontSize:13 }}>🗑</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background:'#f1f5f9' }}>
                      <td colSpan={2} style={{...td, fontWeight:700, fontSize:12}}>Total</td>
                      <td style={{...td, textAlign:'right', fontWeight:800, color:'#dc2626', fontSize:12}}>{fmtRp(pRows.reduce((s,r)=>s+r.nominal,0))}</td>
                      <td style={td}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
            {pRows.length === 0 && (
              <div style={{ marginTop:14, paddingTop:14, borderTop:'1px solid #f1f5f9',
                textAlign:'center', color:'#94a3b8', fontSize:12 }}>Belum ada pengeluaran</div>
            )}
          </div>

          {/* Card total pecahan bulanan */}
          {(() => {
            const p1 = periodeData['1'] || {};
            const p2 = periodeData['2'] || {};
            const totalPecahan = PECAHAN.reduce((s,p) => s + ((p1[`jml_${p}`]||0) + (p2[`jml_${p}`]||0)) * p, 0);
            return (
              <div style={{ background:'#fff', borderRadius:14, boxShadow:'0 1px 4px rgba(0,0,0,0.06)', padding:20 }}>
                <div style={{ fontSize:14, fontWeight:700, color:'#1e293b', marginBottom:14 }}>🪙 Total Pecahan Bulan Ini</div>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', padding:'3px 4px', borderBottom:'1px solid #e5e7eb', textAlign:'left' }}>Pecahan</th>
                      <th style={{ fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', padding:'3px 4px', borderBottom:'1px solid #e5e7eb', textAlign:'center' }}>Lembar</th>
                      <th style={{ fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', padding:'3px 4px', borderBottom:'1px solid #e5e7eb', textAlign:'right' }}>Jumlah</th>
                    </tr>
                  </thead>
                  <tbody>
                    {PECAHAN.map(p => {
                      const lembar = (p1[`jml_${p}`]||0) + (p2[`jml_${p}`]||0);
                      return (
                        <tr key={p} style={{ opacity: lembar===0?0.35:1 }}>
                          <td style={{ padding:'4px 4px', fontSize:12, color:'#64748b' }}>{fmtPecahan(p)}</td>
                          <td style={{ padding:'4px 4px', fontSize:12, textAlign:'center', color:'#374151', fontWeight:600 }}>{lembar}</td>
                          <td style={{ padding:'4px 4px', fontSize:12, textAlign:'right', color:'#374151' }}>{lembar > 0 ? fmtRp(lembar * p) : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop:'1.5px solid #e5e7eb' }}>
                      <td colSpan={2} style={{ padding:'6px 4px', fontSize:12, fontWeight:700, color:'#374151' }}>Total</td>
                      <td style={{ padding:'6px 4px', fontSize:13, fontWeight:800, color:'#1e40af', textAlign:'right' }}>{fmtRp(totalPecahan)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            );
          })()}

        </div>
      </div>
    </div>
  );
}

// =====================
// STYLES
// =====================
const styles = {
  loginContainer: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)' },
  loginBox: { background: '#fff', borderRadius: 16, padding: 40, width: 360, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  loginLogo: { fontSize: 48, textAlign: 'center', marginBottom: 8 },
  loginTitle: { textAlign: 'center', fontSize: 24, fontWeight: 700, color: '#1e40af', marginBottom: 4 },
  loginSubtitle: { textAlign: 'center', color: '#64748b', marginBottom: 24, fontSize: 14 },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, marginBottom: 12, outline: 'none' },
  error: { color: '#ef4444', fontSize: 13, marginBottom: 12, textAlign: 'center' },
  appContainer: { display: 'flex', minHeight: '100vh' },
  sidebar: { width: 220, background: 'linear-gradient(180deg, #0f2044 0%, #152d5a 100%)', display: 'flex', flexDirection: 'column', padding: 0, position: 'fixed', top: 0, bottom: 0, left: 0, boxShadow: '4px 0 20px rgba(0,0,0,0.25)' },
  sidebarLogo: { fontSize: 36, textAlign: 'center', marginBottom: 4 },
  sidebarTitle: { color: '#fff', textAlign: 'center', fontWeight: 700, fontSize: 14, marginBottom: 32, padding: '0 16px' },
  nav: { flex: 1 },
  navItem: { padding: '5px 20px', color: '#93c5fd', cursor: 'pointer', fontSize: 14, transition: 'all 0.2s', borderLeft: '3px solid transparent' },
  navItemActive: { background: 'rgba(255,255,255,0.1)', color: '#fff', borderLeft: '3px solid #60a5fa' },
  sidebarFooter: { padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.1)' },
  userName: { color: '#93c5fd', fontSize: 13, marginBottom: 8 },
  btnLogout: { background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)', padding: '7px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, width: '100%', fontWeight: 500, transition: 'background 0.15s' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '6px 8px', background: '#f8fafc', borderBottom: '2px solid #e2e8f0', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#374151' },
  td: { padding: '5px 8px', borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#374151' },
  trEven: { background: '#fafafa' },
  content: { marginLeft: 220, flex: 1, padding: 24, minHeight: '100vh' },
  page: { maxWidth: 1280, margin: '0 auto' },
  pageTitle: { fontSize: 24, fontWeight: 700, color: '#1e3a5f', marginBottom: 20 },
  card: { background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  cardTitle: { fontSize: 16, fontWeight: 600, color: '#374151', marginBottom: 12 },
  hint: { fontSize: 13, color: '#6b7280', marginBottom: 10 },
  row: { display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: 13, color: '#6b7280', fontWeight: 500 },
  select: { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 15, outline: 'none' },
  inputSmall: { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 15, outline: 'none', width: 100 },
  fileInput: { padding: '6px 0', fontSize: 14 },
  fileList: { marginTop: 8 },
  fileItem: { fontSize: 14, color: '#374151', padding: '4px 0' },
  mutasiRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' },
  cabangLabel: { fontSize: 14, fontWeight: 600, color: '#374151', minWidth: 160 },
  fileCheck: { fontSize: 13, color: '#10b981' },
  statusBox: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 16, marginBottom: 16, fontSize: 14, color: '#166534' },
  btnPrimary: { background: '#2563eb', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 15, fontWeight: 600 },
  btnSecondary: { background: '#fff', color: '#2563eb', border: '1px solid #2563eb', padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 15, fontWeight: 600 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16, marginBottom: 16 },
  summaryCard: { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  summaryHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 },
  cabangBadge: { background: '#2563eb', color: '#fff', padding: '4px 10px', borderRadius: 20, fontSize: 15, fontWeight: 700 },
  cabangNama: { fontSize: 16, color: '#374151', fontWeight: 600 },
  summaryRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid #f1f5f9' },
  summaryLabel: { fontSize: 15, color: '#6b7280' },
  summaryValue: { fontSize: 16, fontWeight: 600, color: '#374151' },
};
